/* tslint:disable */
/* eslint-disable */
/**
 * Scrivio Admin API
 * Internal Admin Portal API for Scrivio platform management.  ## Authentication All endpoints require Firebase Authentication JWT token with admin permissions.  ## Authorization - Platform admins: see all organizations - Support agents: see only organizations assigned to their support partner  ## Error Responses All error responses follow RFC 7807 Problem Details format. 
 *
 * The version of the OpenAPI document: 1.0.0
 * Contact: support@scrivio.nl
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import type { Configuration } from './configuration';
import type { AxiosPromise, AxiosInstance, RawAxiosRequestConfig } from 'axios';
import globalAxios from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import { DUMMY_BASE_URL, assertParamExists, setApiKeyToObject, setBasicAuthToObject, setBearerAuthToObject, setOAuthToObject, setSearchParams, serializeDataIfNeeded, toPathString, createRequestFunction } from './common';
import type { RequestArgs } from './base';
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, BaseAPI, RequiredError, operationServerMap } from './base';

/**
 * Body for accepting an admin invitation. Carries the password the invitee chooses for their admin account; the backend sets it on the (new or pre-existing) Firebase account. The invitation token in the path is the capability proving inbox ownership. 
 */
export interface AcceptAdminInvitationRequest {
    /**
     * The password to set on the invitee\'s Firebase account. Minimum 6 characters (Firebase requirement).
     */
    'password': string;
}
/**
 * Result of successfully accepting an admin invitation.
 */
export interface AcceptAdminInvitationResponse {
    /**
     * Email address of the newly provisioned admin user
     */
    'email': string;
    /**
     * Support partner the admin user is scoped to. Null for platform admins.
     */
    'supportPartnerId'?: string | null;
}
/**
 * The published edition one acceptance agreed to, addressed and named (#4571). Derived from the **accepted** version rather than the current one, so an acceptance the current version has overtaken still points at the text that was agreed.  `null` exactly when an acceptance\'s `version` is `null`: a contract signed outside Scrivio was individually negotiated and has no published edition, so there is none to offer — and offering the generic edition instead would name a text that practice never agreed to. 
 */
export interface AcceptedAgreementDocument {
    /**
     * The committed PDF rendition of exactly that edition, on the public docs site
     */
    'pdfUrl': string;
    /**
     * What to call it on disk. It carries the day the practice accepted, so a file saved out of the admin portal still says when it was agreed, and two practices\' copies of one edition do not overwrite each other. 
     */
    'fileName': string;
}
/**
 * One organisation accepting one version of one agreement, as it was recorded
 */
export interface AdminAgreementAcceptanceDto {
    'id': string;
    'document': AgreementDocument;
    /**
     * The published version accepted. `null` only for `PRE_EXISTING_CONTRACT`: a contract signed outside Scrivio was individually negotiated and has no published version to name. 
     */
    'version'?: string | null;
    /**
     * When the acceptance took effect, in milliseconds. For a pre-existing contract this is the start of the signing date in Amsterdam, not the moment it was recorded. 
     */
    'acceptedAt': number;
    'method': AgreementAcceptanceMethod;
    /**
     * The practice\'s own user who accepted in the product; null for any other method
     */
    'acceptedByName'?: string | null;
    /**
     * The Scrivio admin who recorded an acceptance made elsewhere; null otherwise
     */
    'recordedByAdminEmail'?: string | null;
    /**
     * Where the signed document is, for an acceptance made outside Scrivio
     */
    'reference'?: string | null;
    /**
     * What to call the signed contract on disk, when Scrivio holds one for this acceptance — download it from `getAgreementContractFile`. `null` when no file was uploaded, which is every acceptance other than a pre-existing contract, and a pre-existing contract recorded by `reference` alone (#4837). 
     */
    'contractFileName'?: string | null;
    /**
     * The counterparty\'s name as it was when it accepted
     */
    'partyName': string;
    /**
     * The counterparty\'s KvK-nummer as it was when it accepted
     */
    'partyKvkNumber'?: string | null;
    /**
     * The counterparty\'s address as it was when it accepted, on one line
     */
    'partyAddress'?: string | null;
    'acceptedDocument'?: AcceptedAgreementDocument | null;
}


/**
 * Verb describing an admin audit action. Combined with `AdminAuditTargetType` to compose the full semantic action (e.g. `SUSPENDED` + `ORGANIZATION` = \"Organization suspended\"). 
 */

export const AdminAuditAction = {
    Created: 'CREATED',
    Updated: 'UPDATED',
    Deleted: 'DELETED',
    Suspended: 'SUSPENDED',
    Reactivated: 'REACTIVATED',
    LicenseChanged: 'LICENSE_CHANGED',
    Cancelled: 'CANCELLED',
    ImpersonationStarted: 'IMPERSONATION_STARTED',
    ImpersonationEnded: 'IMPERSONATION_ENDED',
    MfaReset: 'MFA_RESET',
    InviteResent: 'INVITE_RESENT',
    PasswordReset: 'PASSWORD_RESET',
    Revoked: 'REVOKED',
    Deactivated: 'DEACTIVATED',
    OrgsUpdated: 'ORGS_UPDATED',
    Purged: 'PURGED',
    Activated: 'ACTIVATED',
    Requested: 'REQUESTED',
    Downloaded: 'DOWNLOADED'
} as const;

export type AdminAuditAction = typeof AdminAuditAction[keyof typeof AdminAuditAction];


/**
 * Classifies who (or what) performed an audit-logged action. SYSTEM-driven writes (e.g. domain-event listeners) may have null `actorUserId` and `actorEmail`. 
 */

export const AdminAuditActorType = {
    HumanAdmin: 'HUMAN_ADMIN',
    HumanUser: 'HUMAN_USER',
    System: 'SYSTEM'
} as const;

export type AdminAuditActorType = typeof AdminAuditActorType[keyof typeof AdminAuditActorType];


/**
 * Audit log entry for dashboard display. Sourced from `admin_audit_log` and rendered by reusing the i18n labels of `/admin/audit-log` (see GitLab #2441). 
 */
export interface AdminAuditEntryDto {
    /**
     * Audit entry ID
     */
    'id': string;
    /**
     * When the action occurred (ISO 8601)
     */
    'timestamp': string;
    'actorType': AdminAuditActorType;
    /**
     * Email of the actor who performed the action (null for SYSTEM)
     */
    'actorEmail'?: string | null;
    'action': AdminAuditAction;
    'targetType': AdminAuditTargetType;
    /**
     * ID of the affected resource (null for some action types)
     */
    'targetId'?: string | null;
    /**
     * Name or identifier of the affected resource
     */
    'targetName'?: string | null;
}


/**
 * Admin audit log entry
 */
export interface AdminAuditLogDto {
    /**
     * Unique audit log entry identifier
     */
    'id': string;
    /**
     * When the action occurred (ISO 8601)
     */
    'timestamp': string;
    'actorType': AdminAuditActorType;
    /**
     * ID of the user who performed the action (null for SYSTEM actor)
     */
    'actorUserId'?: string | null;
    /**
     * Email of the actor who performed the action (null for SYSTEM actor)
     */
    'actorEmail'?: string | null;
    'action': AdminAuditAction;
    'targetType': AdminAuditTargetType;
    /**
     * ID of the target entity (null for some action types)
     */
    'targetId'?: string | null;
    /**
     * Organization ID of the target, for scoping queries by organization
     */
    'targetOrganizationId'?: string | null;
    /**
     * JSON representation of the previous value (for updates)
     */
    'previousValue'?: { [key: string]: any; } | null;
    /**
     * JSON representation of the new value (for creates/updates)
     */
    'newValue'?: { [key: string]: any; } | null;
    /**
     * Free-form justification text supplied by the actor, surfaced in the UI for actions like organization suspension or license changes. 
     */
    'reason'?: string | null;
    /**
     * Client IP address (IPv4 or IPv6)
     */
    'ipAddress'?: string | null;
    /**
     * Client User-Agent string
     */
    'userAgent'?: string | null;
}


/**
 * Paginated list of admin audit log entries
 */
export interface AdminAuditLogListResponse {
    /**
     * List of audit log entries
     */
    'content': Array<AdminAuditLogDto>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Page size
     */
    'size': number;
    /**
     * Total number of audit log entries matching the filters
     */
    'totalElements': number;
    /**
     * Total number of pages
     */
    'totalPages': number;
}
/**
 * Types of entities that can be targeted by admin audit actions
 */

export const AdminAuditTargetType = {
    Organization: 'ORGANIZATION',
    User: 'USER',
    AdminUser: 'ADMIN_USER',
    AdminInvitation: 'ADMIN_INVITATION',
    LicenseChange: 'LICENSE_CHANGE',
    SubscriptionDiscount: 'SUBSCRIPTION_DISCOUNT',
    SupportPartner: 'SUPPORT_PARTNER',
    Role: 'ROLE',
    Location: 'LOCATION',
    Subscription: 'SUBSCRIPTION',
    SubscriptionInvoice: 'SUBSCRIPTION_INVOICE',
    AgreementAcceptance: 'AGREEMENT_ACCEPTANCE',
    SubscriptionMandate: 'SUBSCRIPTION_MANDATE',
    SnelstartSubscriptionKey: 'SNELSTART_SUBSCRIPTION_KEY',
    HubspotCompanyLink: 'HUBSPOT_COMPANY_LINK',
    SubscriptionBoekhoudingMail: 'SUBSCRIPTION_BOEKHOUDING_MAIL'
} as const;

export type AdminAuditTargetType = typeof AdminAuditTargetType[keyof typeof AdminAuditTargetType];


/**
 * Request to close an organization (#4905)
 */
export interface AdminCloseOrganizationRequest {
    /**
     * Why the organization is closed (logged to the admin audit log)
     */
    'reason': string;
}
/**
 * Request to create a new organization via admin portal.  A practice created here goes through the same two things a self-signup does: somebody is there to accept the agreements, and the subscription is on a plan from the moment it exists. Both are therefore required rather than filled in afterwards (#4551). 
 */
export interface AdminCreateOrganizationRequest {
    /**
     * Organization display name. Any character except a control character: the name is interpolated into the subject line of the invitation e-mail, so a CR/LF in it would terminate the header line and turn the rest into headers of the caller\'s choosing (#3457). 
     */
    'name': string;
    /**
     * Organization domain (must be unique)
     */
    'domain': string;
    'licenseType': LicenseType;
    /**
     * Email address for the initial beheerder, who is invited into the administrative role. Required: an organization with no member is one nobody can enter and for which the agreements can never be accepted (#4551). 
     */
    'initialAdminEmail': string;
    'subscription': AdminNewOrganizationSubscriptionRequest;
    'numbering'?: AdminOrganizationNumberingRequest;
    /**
     * Tenaamstelling: the legal entity a Scrivio invoice to this practice is made out to, when that is not `name`. Optional here — an organisation created by an admin is normally filled in afterwards. 
     */
    'statutoryName'?: string | null;
    /**
     * The T.a.v. line on a Scrivio invoice to this practice
     */
    'billingContactName'?: string | null;
    /**
     * Where Scrivio sends this practice its own invoices
     */
    'billingEmail'?: string | null;
    /**
     * BTW-identificatienummer: `NL`, 9 digits, `B` and 2 digits. Spacing and case are normalised away before it is stored; anything else is a 400. No `pattern:` here for that reason — one would refuse the spacing this normalises. 
     */
    'btwNumber'?: string | null;
}


/**
 * Current admin user information
 */
export interface AdminCurrentUserDto {
    /**
     * Admin user ID
     */
    'id': string;
    /**
     * Admin user email
     */
    'email': string;
    'role': AdminRoleDto;
    /**
     * List of permission strings decoded from bitmask
     */
    'permissions': Array<string>;
    /**
     * Support partner ID if this admin is a support agent
     */
    'supportPartnerId'?: string | null;
    /**
     * List of organization IDs this admin has access to. Null means access to all organizations (platform admin).
     */
    'scopedOrganizationIds'?: Array<string> | null;
    /**
     * Indicates that Firebase custom claims were updated during this request. When true, the frontend should call `auth.currentUser.getIdToken(true)` to force a token refresh and get the new claims. This typically happens during initial bootstrap when a new admin user is created. 
     */
    'claimsUpdated': boolean;
    'status': AdminUserStatus;
}


/**
 * Aggregated dashboard data for admin portal
 */
export interface AdminDashboardDto {
    'organizationStats': AdminOrganizationStatsDto;
    'userStats': AdminUserStatsDto;
    /**
     * Number of scheduled license changes pending
     */
    'pendingLicenseChanges': number;
    /**
     * Last 10 audit log entries
     */
    'recentAuditEntries': Array<AdminAuditEntryDto>;
    /**
     * Whether data is scoped to support agent\'s organizations
     */
    'isScoped': boolean;
}
/**
 * Request to disable a user
 */
export interface AdminDisableUserRequest {
    /**
     * Reason for disabling the user (logged to admin audit)
     */
    'reason': string;
}
export interface AdminHubSpotLinkRequest {
    /**
     * The HubSpot company\'s address as HubSpot shows it, or its id.
     */
    'company': string;
}
/**
 * An organization\'s link to a company in Scrivio\'s HubSpot (#4836).
 */
export interface AdminHubSpotLinkResponse {
    /**
     * Whether the HubSpot sync is switched on. While it is off nothing can be linked or unlinked.
     */
    'enabled': boolean;
    /**
     * Whether the organization is linked to a HubSpot company.
     */
    'linked': boolean;
    /**
     * The linked company\'s HubSpot id.
     */
    'companyId'?: string | null;
    /**
     * The linked company\'s record in HubSpot, where HubSpot could say which portal it is in.
     */
    'companyUrl'?: string | null;
    /**
     * Why the organization is deliberately left unlinked, if it is.
     */
    'unlinkedReason'?: HubSpotLinkReason | null;
    /**
     * When the last successful sync was.
     */
    'lastSyncedAt'?: string | null;
    /**
     * The last failed attempt\'s error type and HTTP status — never a payload. Null after a success.
     */
    'lastError'?: string | null;
    'lastErrorAt'?: string | null;
}


export interface AdminHubSpotLinkRunEntry {
    'organizationId': string;
    'organizationName': string;
    'outcome': HubSpotLinkOutcome;
    'reason': HubSpotLinkReason;
    /**
     * The company it is, or would be, linked to; null for a company still to be created.
     */
    'companyId'?: string | null;
}


export interface AdminHubSpotLinkRunRequest {
    /**
     * Report what the run would do and write nothing.
     */
    'dryRun': boolean;
}
export interface AdminHubSpotLinkRunResponse {
    'dryRun': boolean;
    /**
     * False when HubSpot\'s rate limit ended the run; the rest are `NOT_REACHED`.
     */
    'complete': boolean;
    /**
     * Every customer organization, by name.
     */
    'entries': Array<AdminHubSpotLinkRunEntry>;
}
export interface AdminHubSpotUnlinkedOrganization {
    'organizationId': string;
    'organizationName': string;
    'primaryContactEmail'?: string | null;
    /**
     * Why it is left unlinked, where the link run or an admin left it so.
     */
    'unlinkedReason'?: HubSpotLinkReason | null;
    /**
     * The last failed sync attempt\'s error type and HTTP status.
     */
    'lastError'?: string | null;
}


/**
 * A pending admin-portal invitation. Returned when an admin is invited or when their invitation is resent. The invitee becomes an admin user only once they accept this invitation. 
 */
export interface AdminInvitationResponse {
    /**
     * Invitation ID
     */
    'id': string;
    /**
     * Email address the invitation was sent to
     */
    'email': string;
    /**
     * Invitation lifecycle status
     */
    'status': AdminInvitationResponseStatusEnum;
    /**
     * When the invitation expires (ISO 8601)
     */
    'expiresAt': string;
    /**
     * Support partner the invitee is scoped to. Null for platform admins.
     */
    'supportPartnerId'?: string | null;
    /**
     * Admin role the invitee receives on acceptance
     */
    'adminRoleId': string;
}

export const AdminInvitationResponseStatusEnum = {
    Pending: 'PENDING',
    Accepted: 'ACCEPTED',
    Expired: 'EXPIRED',
    Revoked: 'REVOKED'
} as const;

export type AdminInvitationResponseStatusEnum = typeof AdminInvitationResponseStatusEnum[keyof typeof AdminInvitationResponseStatusEnum];

/**
 * What the practice being created pays Scrivio (#4551). The same terms `SubscriptionActivationRequest` states, minus the activation date: a subscription created here starts today, because the organization it belongs to does.  The result is `ACTIVE`. `UNMANAGED` is only ever what an organization predating this context was backfilled to (#4463); no path produces a new one. 
 */
export interface AdminNewOrganizationSubscriptionRequest {
    /**
     * An offered plan that sells the licence the organization is created with.
     */
    'planId': string;
    'billingMode': SubscriptionBillingMode;
    /**
     * The day the practice signed its contract, where there is one on paper.
     */
    'contractSignedOn'?: string;
    /**
     * The agreed price per seat type, at most one entry each. A seat type the plan prices and this list leaves out is charged the plan\'s price today.  At least one, unlike `SubscriptionActivationRequest`: creating a practice is the moment its price is agreed, so the snapshot stored against it is a figure somebody named rather than whatever the price book happened to say that day. 
     */
    'seatPrices': Array<SubscriptionSeatPriceRequest>;
    /**
     * Whether this practice also gets the 14 free days a self-signup gets, as a 100% discount from today. Absent is no discount — a practice Scrivio onboards by hand has usually already agreed its terms, so a trial here is a decision per practice rather than an automatic grant. 
     */
    'grantTrialDiscount'?: boolean;
}


/**
 * What an organisation has agreed to with Scrivio (#4492): per document whether the currently published version is still outstanding, and every acceptance ever recorded. 
 */
export interface AdminOrganizationAgreementsResponse {
    /**
     * One entry per `AgreementDocument`, whatever the organisation has recorded
     */
    'agreements': Array<AgreementStatus>;
    /**
     * Every recorded acceptance, newest first. The record is append-only: a newer acceptance is a new entry, and the one it superseded stays listed. 
     */
    'acceptances': Array<AdminAgreementAcceptanceDto>;
}
/**
 * Detailed organization information for admin portal
 */
export interface AdminOrganizationDetailDto {
    /**
     * Unique organization identifier
     */
    'id': string;
    /**
     * Organization name
     */
    'name': string;
    /**
     * Organization domain (slug)
     */
    'domain'?: string | null;
    'licenseType': LicenseType;
    'status': OrganizationStatus;
    'kind': OrganizationKind;
    'authProvider': AuthProvider;
    /**
     * Number of active users in the organization
     */
    'userCount': number;
    /**
     * Number of clients in the organization
     */
    'clientCount': number;
    /**
     * Organization creation timestamp (ISO 8601)
     */
    'createdAt': string;
    /**
     * Last activity timestamp in the organization (ISO 8601)
     */
    'lastActivityAt'?: string | null;
    'supportPartner'?: SupportPartnerDto | null;
    /**
     * List of scheduled license type changes
     */
    'scheduledLicenseChanges'?: Array<ScheduledLicenseChangeDto>;
    /**
     * Date when the current license became effective
     */
    'currentLicenseEffectiveDate'?: string | null;
    /**
     * Tenaamstelling — the legal entity a Scrivio invoice to this practice is made out to; null when it has given none
     */
    'statutoryName'?: string | null;
    /**
     * The T.a.v. line on a Scrivio invoice to this practice; null when it has given none
     */
    'billingContactName'?: string | null;
    /**
     * Where Scrivio sends this practice its own invoices; null when it has given none
     */
    'billingEmail'?: string | null;
    /**
     * BTW-identificatienummer in its canonical NL<9 digits>B<2 digits> form; null when the practice has given none
     */
    'btwNumber'?: string | null;
    'primaryContact'?: AdminPrimaryContactDto | null;
    'vecozoKanalen': VecozoKanalen;
    /**
     * Whether this deployment has a VECOZO berichtenservice connection, so that a stream can be moved onto `BERICHTENSERVICE` at all. A property of the deployment, not of the organization; false in production until its VECOZO system certificate exists. 
     */
    'berichtenserviceConfigured': boolean;
}


/**
 * Organization details for admin portal
 */
export interface AdminOrganizationDto {
    /**
     * Unique organization identifier
     */
    'id': string;
    /**
     * Organization name
     */
    'name': string;
    /**
     * Organization domain (slug)
     */
    'domain'?: string | null;
    'licenseType': LicenseType;
    'status': OrganizationStatus;
    'kind': OrganizationKind;
    'authProvider': AuthProvider;
    /**
     * Number of active users in the organization
     */
    'userCount': number;
    /**
     * Organization creation timestamp (ISO 8601)
     */
    'createdAt': string;
    /**
     * ID of the assigned support partner, if any
     */
    'supportPartnerId'?: string | null;
    'primaryContact'?: AdminPrimaryContactDto | null;
}


/**
 * A pending invitation to join an organization, as the admin portal shows it
 */
export interface AdminOrganizationInvitationDto {
    'id': string;
    /**
     * Address the invitation was sent to
     */
    'email': string;
    /**
     * Role the invitee receives on accepting
     */
    'roleName': string;
    /**
     * When the invitation was first issued, in milliseconds
     */
    'createdAt': number;
    /**
     * When the current link expires, in milliseconds
     */
    'expiresAt': number;
    /**
     * Whether the current link has expired; resending issues a new one
     */
    'expired': boolean;
}
export interface AdminOrganizationInvitationListResponse {
    'items': Array<AdminOrganizationInvitationDto>;
}
/**
 * Paginated list of organizations
 */
export interface AdminOrganizationListResponse {
    /**
     * List of organizations
     */
    'content': Array<AdminOrganizationDto>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Page size
     */
    'size': number;
    /**
     * Total number of organizations matching the filters
     */
    'totalElements': number;
    /**
     * Total number of pages
     */
    'totalPages': number;
}
/**
 * Where this practice\'s two number series begin (#4565).  A practice migrating from another EPD keeps its old numbers in its boekhouding, and Scrivio otherwise starts both of its own series at 1 — which at FIER produced `2026-00728` from the old EPD beside `2026-0417` from Scrivio, distinguishable only by the hyphen. Asking at creation is the only moment the question can be answered: once a number has gone to a cliënt or an insurer it can never be moved.  The value is the **sequence part** of the first number the practice should be given, not the whole number: the year prefix is the calendar\'s. `firstInvoiceNumber: 729` in 2026 issues `2026-0729`, `firstBatchNumber: 729` issues `2026-00729`.  Both properties are optional and an omitted one starts that series at 1, exactly as before. The year they apply to is the one the organization is created in — every series restarts at 1 in January, which is what the old EPD did too. 
 */
export interface AdminOrganizationNumberingRequest {
    /**
     * The first factuurnummer this practice should issue to a cliënt — the number the practice thinks of as \"het factuurnummer\". 
     */
    'firstInvoiceNumber'?: number;
    /**
     * The first declaratiebatch number — a separate counter, carried as the GDS801 `DeclaratieContext/Factuurnummer` and quoted back by an insurer. Bounded at seven digits because `FactuurnummerType` is `maxLength: 12` and `yyyy-` takes five of them. 
     */
    'firstBatchNumber'?: number;
}
/**
 * Organization statistics for dashboard
 */
export interface AdminOrganizationStatsDto {
    /**
     * Number of customer organizations (#4900): kind `CUSTOMER`, not suspended, and in their trial, active, or with notice given. A test organization, and one that has ended or closed, is not counted. 
     */
    'active': number;
    /**
     * How many of `active` have given notice. They are still billed until the end date, so they are part of `active` as well. 
     */
    'ending': number;
    /**
     * Number of customer organizations on an administrative hold (\"gepauzeerd\"). They are not part of `active`, and their members are not part of the user count. 
     */
    'suspended': number;
}
/**
 * User details for admin portal organization user list
 */
export interface AdminOrganizationUserDto {
    /**
     * Unique user identifier
     */
    'id': string;
    /**
     * User email address
     */
    'email': string;
    /**
     * User first name
     */
    'firstName': string;
    /**
     * User last name
     */
    'lastName': string;
    'status': UserStatus;
    /**
     * List of role names assigned to the user in this organization
     */
    'roles': Array<string>;
    /**
     * Timestamp of the user\'s last login (ISO 8601)
     */
    'lastLoginAt'?: string | null;
    /**
     * Whether the user has MFA enabled
     */
    'mfaEnabled': boolean;
    /**
     * Count of other organizations this user belongs to (privacy-preserving)
     */
    'otherOrganizationCount': number;
    /**
     * Whether this membership carries the behandelaar designation in this organization. A designation, not a role: a role named \"Behandelaar\" designates nothing. 
     */
    'isBehandelaar': boolean;
    /**
     * Whether this membership carries the regiebehandelaar designation in this organization
     */
    'isRegiebehandelaar': boolean;
    /**
     * Whether this membership counts as a seat in Scrivio\'s subscription billing today: an enabled membership carrying the behandelaar designation (ADR-042). Derived by the backend; a client must not recompute it from `status` and `isBehandelaar`. 
     */
    'countsAsSeat': boolean;
}


/**
 * Paginated list of users in an organization
 */
export interface AdminOrganizationUserListResponse {
    /**
     * List of users
     */
    'content': Array<AdminOrganizationUserDto>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Page size
     */
    'size': number;
    /**
     * Total number of users matching the filters
     */
    'totalElements': number;
    /**
     * Total number of pages
     */
    'totalPages': number;
    /**
     * How many members of the organization count as a seat today (see `AdminOrganizationUserDto.countsAsSeat`). Organization-wide: unaffected by `search`, `status` and paging, and equal to the subscription\'s `seatsToday`. 
     */
    'seatCount': number;
}
/**
 * Response for password reset operation
 */
export interface AdminPasswordResetResponse {
    /**
     * Whether the password reset email was sent successfully
     */
    'success': boolean;
    /**
     * Human-readable message about the operation
     */
    'message': string;
}
/**
 * The organization\'s primary contact person; absent when it has none (#4834)
 */
export interface AdminPrimaryContactDto {
    'state': AdminPrimaryContactState;
    /**
     * The contact\'s user ID; null when `state` is `INVITED`
     */
    'userId'?: string | null;
    /**
     * The contact\'s current email address, or the invited address
     */
    'email': string;
    /**
     * The contact\'s full name; null when `state` is `INVITED`
     */
    'name'?: string | null;
}


/**
 * What the primary contact shown for an organization is (#4834): - `ACTIVE` — a member whose membership is enabled - `INACTIVE` — the recorded contact, whose membership has been disabled or removed;   shown so an admin picks someone else - `INVITED` — nobody is recorded yet; the address of the organization\'s oldest pending   invitation, shown and never stored 
 */

export const AdminPrimaryContactState = {
    Active: 'ACTIVE',
    Inactive: 'INACTIVE',
    Invited: 'INVITED'
} as const;

export type AdminPrimaryContactState = typeof AdminPrimaryContactState[keyof typeof AdminPrimaryContactState];


/**
 * Request to purge an organization\'s dossier data
 */
export interface AdminPurgeDossierRequest {
    /**
     * When true, returns a preview of the per-table counts that would be deleted without mutating anything. When false, performs the irreversible deletion. 
     */
    'dryRun': boolean;
    /**
     * Must exactly match the organization\'s name. A safety check to prevent purging the wrong organization. 
     */
    'confirmationName': string;
    /**
     * Reason for the purge (logged to admin audit)
     */
    'reason': string;
    /**
     * Who at the practice asked for the purge, by name or role (logged to the admin audit log). Required on a real run; ignored on a dry run. 
     */
    'requestedBy'?: string;
    /**
     * The day the practice asked for the purge, not in the future (logged to the admin audit log). Required on a real run; ignored on a dry run. 
     */
    'requestedOn'?: string;
}
/**
 * Admin role information
 */
export interface AdminRoleDto {
    /**
     * Role ID
     */
    'id': string;
    /**
     * Role name
     */
    'name': string;
}
/**
 * Request to schedule a license type change
 */
export interface AdminScheduleLicenseChangeRequest {
    'licenseType': LicenseType;
    /**
     * Date when the license change takes effect (must be in the future)
     */
    'effectiveDate': string;
}


/**
 * Response from starting or refreshing an admin session
 */
export interface AdminSessionResponse {
    /**
     * Session expiration timestamp (Unix epoch seconds)
     */
    'expiresAt': number;
    /**
     * Success message
     */
    'message': string;
}
/**
 * Where an organization\'s behandelaars, or one admin user, sign in from now on
 */
export interface AdminSetAuthProviderRequest {
    'authProvider': AuthProvider;
}


/**
 * The channel a VECOZO stream family should travel on
 */
export interface AdminSetVecozoKanaalRequest {
    'kanaal': VecozoKanaal;
}


/**
 * A practice\'s SnelStart connection as an admin sees it. Never carries a key.
 */
export interface AdminSnelStartResponse {
    /**
     * The route the practice is connected by, or null while it is not connected. - MAATWERK: a pasted maatwerksleutel; sends the key stored here - PRODUCTIE: the productiekoppeling; sends Scrivio\'s platform key 
     */
    'route'?: AdminSnelStartResponseRouteEnum | null;
    /**
     * Whether the maatwerk subscription key is stored for this organization.
     */
    'maatwerkSubscriptionKeyConfigured': boolean;
}

export const AdminSnelStartResponseRouteEnum = {
    Maatwerk: 'MAATWERK',
    Productie: 'PRODUCTIE'
} as const;

export type AdminSnelStartResponseRouteEnum = typeof AdminSnelStartResponseRouteEnum[keyof typeof AdminSnelStartResponseRouteEnum];

export interface AdminSnelStartSubscriptionKeyRequest {
    /**
     * The subscription key SnelStart issued for this practice. Never returned.
     */
    'subscriptionKey': string;
}
/**
 * How an admin ends a practice\'s subscription (#4902).
 */
export interface AdminSubscriptionCancellationRequest {
    /**
     * `true` ends the subscription today, whatever its lifecycle. `false` ends it on the terms the practice itself gets: today during the free trial, otherwise with notice to the end of the next calendar month. 
     */
    'immediate': boolean;
}
/**
 * Request to suspend an organization
 */
export interface AdminSuspendOrganizationRequest {
    /**
     * Reason for suspension (logged to admin audit)
     */
    'reason': string;
}
/**
 * Who a Scrivio invoice to this practice is addressed to (#4448). Every property is optional with the ordinary three states: omit it to leave the stored value alone, send null to clear it, send a value to set it. 
 */
export interface AdminUpdateOrganizationInvoiceDetailsRequest {
    /**
     * Tenaamstelling — the legal entity a Scrivio invoice is made out to; clearing it falls the invoice back to the organisation\'s name
     */
    'statutoryName'?: string | null;
    /**
     * The T.a.v. line on a Scrivio invoice to this practice
     */
    'billingContactName'?: string | null;
    /**
     * Where Scrivio sends this practice its own invoices
     */
    'billingEmail'?: string | null;
    /**
     * BTW-identificatienummer: `NL`, 9 digits, `B` and 2 digits. Spacing and case are normalised away before it is stored; anything else is a 400. 
     */
    'btwNumber'?: string | null;
}
/**
 * Whether this organization is a customer or a test organization (#4773)
 */
export interface AdminUpdateOrganizationKindRequest {
    'kind': OrganizationKind;
}


/**
 * Who the organization\'s primary contact is (#4834)
 */
export interface AdminUpdateOrganizationPrimaryContactRequest {
    /**
     * A member of the organization; null or omitted clears the primary contact
     */
    'userId'?: string | null;
}
/**
 * Detailed user information for admin portal
 */
export interface AdminUserDetailDto {
    /**
     * Unique user identifier
     */
    'id': string;
    /**
     * Firebase Authentication user ID
     */
    'firebaseUid'?: string | null;
    /**
     * User email address
     */
    'email': string;
    /**
     * User first name
     */
    'firstName': string;
    /**
     * User last name
     */
    'lastName': string;
    'status': UserStatus;
    /**
     * Organizations the admin has access to view
     */
    'organizations': Array<AdminUserOrganizationDto>;
    /**
     * Count of organizations the admin does not have access to view
     */
    'otherOrganizationCount': number;
    /**
     * Whether user has MFA enabled
     */
    'mfaEnabled': boolean;
    /**
     * Timestamp of last login (ISO 8601)
     */
    'lastLoginAt'?: string | null;
    /**
     * Account creation timestamp (ISO 8601)
     */
    'createdAt': string;
    /**
     * Aggregated permissions from all visible organizations
     */
    'computedPermissions': Array<string>;
}


/**
 * Full admin user details
 */
export interface AdminUserDto {
    /**
     * Admin user ID
     */
    'id': string;
    /**
     * Admin user email
     */
    'email': string;
    'role': AdminRoleDto;
    'authProvider': AuthProvider;
    'supportPartner'?: SupportPartnerDto | null;
    /**
     * Whether MFA is enrolled for this admin user
     */
    'mfaEnrolled': boolean;
    /**
     * Account creation timestamp (ISO 8601)
     */
    'createdAt': string;
    /**
     * Soft delete timestamp (ISO 8601). Null if not deleted.
     */
    'deletedAt'?: string | null;
}


/**
 * Admin user summary for list view
 */
export interface AdminUserListItemDto {
    /**
     * Admin user ID
     */
    'id': string;
    /**
     * Admin user email
     */
    'email': string;
    'role': AdminRoleDto;
    'authProvider': AuthProvider;
    'supportPartner'?: SupportPartnerDto | null;
    /**
     * Account creation timestamp (ISO 8601)
     */
    'createdAt': string;
    /**
     * Soft delete timestamp (ISO 8601). Null if not deleted.
     */
    'deletedAt'?: string | null;
}


/**
 * Paginated list of admin users
 */
export interface AdminUserListResponse {
    /**
     * List of admin users
     */
    'content': Array<AdminUserListItemDto>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Page size
     */
    'size': number;
    /**
     * Total number of admin users matching the filters
     */
    'totalElements': number;
    /**
     * Total number of pages
     */
    'totalPages': number;
}
/**
 * Organization membership for admin user detail
 */
export interface AdminUserOrganizationDto {
    /**
     * Organization identifier
     */
    'id': string;
    /**
     * Organization name
     */
    'name': string;
    /**
     * List of role names the user has in this organization
     */
    'roles': Array<string>;
}
/**
 * User statistics for dashboard
 */
export interface AdminUserStatsDto {
    /**
     * Number of distinct users who are members of an organization counted in `organizationStats.active` (#4900)
     */
    'total': number;
}
/**
 * Admin user status
 */

export const AdminUserStatus = {
    Active: 'ACTIVE',
    Disabled: 'DISABLED'
} as const;

export type AdminUserStatus = typeof AdminUserStatus[keyof typeof AdminUserStatus];


/**
 * How an organisation came to be bound by a version of an agreement: a click by a named user (`SELF_SIGNUP`, `INVITATION`, `REACCEPTANCE`), or a contract signed outside Scrivio that an admin recorded afterwards (`PRE_EXISTING_CONTRACT`). 
 */

export const AgreementAcceptanceMethod = {
    SelfSignup: 'SELF_SIGNUP',
    Invitation: 'INVITATION',
    PreExistingContract: 'PRE_EXISTING_CONTRACT',
    Reacceptance: 'REACCEPTANCE'
} as const;

export type AgreementAcceptanceMethod = typeof AgreementAcceptanceMethod[keyof typeof AgreementAcceptanceMethod];


/**
 * A Scrivio agreement a practice enters into. The two are versioned and accepted separately: a change to what Scrivio charges is a new Overeenkomst and leaves the Verwerkersovereenkomst untouched. 
 */

export const AgreementDocument = {
    Overeenkomst: 'OVEREENKOMST',
    Verwerkersovereenkomst: 'VERWERKERSOVEREENKOMST'
} as const;

export type AgreementDocument = typeof AgreementDocument[keyof typeof AgreementDocument];


/**
 * What an organisation is bound by for one agreement, and whether the version Scrivio currently publishes is still outstanding (#4489). 
 */
export interface AgreementStatus {
    'document': AgreementDocument;
    /**
     * The version in force. `null` when the organisation has accepted nothing, and also when what it is bound by is a contract signed outside Scrivio — that has no published version to name, which is why `currentVersionOutstanding` is the property to read rather than comparing this to `currentVersion`. 
     */
    'acceptedVersion'?: string | null;
    /**
     * When the acceptance in force was recorded, in milliseconds; null when nothing has been accepted
     */
    'acceptedAt'?: number | null;
    /**
     * The version Scrivio publishes today
     */
    'currentVersion': string;
    /**
     * True when this organisation has not accepted what Scrivio currently publishes and should be asked to (#4494). A contract signed outside Scrivio is **not** outstanding: nothing can compare a signed PDF to a published version. 
     */
    'currentVersionOutstanding': boolean;
}


/**
 * Where a person signs in (ADR-045 §7): Firebase Authentication or Scrivio\'s own Keycloak. `FIREBASE` for everyone until Scrivio staff switch an organization or an admin user. 
 */

export const AuthProvider = {
    Firebase: 'FIREBASE',
    Keycloak: 'KEYCLOAK'
} as const;

export type AuthProvider = typeof AuthProvider[keyof typeof AuthProvider];


/**
 * Billing batch lifecycle status: - DRAFT: Initial state, batch can be edited - READY: All lines validated, ready for export - EXPORTED: Export file generated and downloaded - SUBMITTED: Submitted to insurer via VECOZO - APPROVED: All lines approved by insurer - REJECTED: Batch rejected by insurer - PARTIALLY_APPROVED: Some lines approved, some rejected 
 */

export const BillingBatchStatus = {
    Draft: 'DRAFT',
    Ready: 'READY',
    Exported: 'EXPORTED',
    Submitted: 'SUBMITTED',
    Approved: 'APPROVED',
    Rejected: 'REJECTED',
    PartiallyApproved: 'PARTIALLY_APPROVED'
} as const;

export type BillingBatchStatus = typeof BillingBatchStatus[keyof typeof BillingBatchStatus];


/**
 * Request to create a new admin user
 */
export interface CreateAdminUserRequest {
    /**
     * Admin user email address
     */
    'email': string;
    /**
     * Role ID to assign to the admin user
     */
    'roleId': string;
    /**
     * Support partner ID. Null for platform admins.
     */
    'supportPartnerId'?: string | null;
}
/**
 * Request to create a new support partner
 */
export interface CreateSupportPartnerRequest {
    /**
     * Support partner name
     */
    'name': string;
    /**
     * Contact email address
     */
    'email'?: string;
    /**
     * Contact phone number
     */
    'phone'?: string;
    /**
     * Business address
     */
    'address'?: string;
}
/**
 * One batch whose declaration history was recovered, is incomplete, or could not be read.
 */
export interface DeclarationHistoryBatchOutcome {
    'batchId': string;
    'batchNumber': string;
    /**
     * How many regels the batch declared
     */
    'totalLines': number;
    /**
     * How many of those have a declaration on record
     */
    'recordedLines': number;
    /**
     * How many of those this run wrote from the batch\'s sent GDS801 message
     */
    'recoveredLines': number;
    /**
     * Declared regels still with no record
     */
    'missingLines': number;
    /**
     * Referentienummers of the sent message\'s prestaties nothing was written for — no regel matched, several did, or the prestatie is a credit 
     */
    'unmatchedPrestaties': Array<string>;
    /**
     * Referentienummers the batch\'s retour names that no recorded declaration carries
     */
    'unmatchedReferences': Array<string>;
    /**
     * Recovered declarations whose regel no longer carries the sent prestatiecode — a redeclared regel matched on cliënt, date, uitvoerder and begintijd alone 
     */
    'looseMatches': Array<LooseDeclarationMatch>;
    /**
     * Every declared regel has a record and nothing was left unmatched
     */
    'complete': boolean;
    /**
     * The batch\'s stored message or retour could not be read. The cause is in the log.
     */
    'failed': boolean;
}
/**
 * What one declaration-history backfill found (#4624). Only the batches it recovered declarations for from their sent GDS801 message, or whose history could not be completed, are listed; every other declared batch was already complete. 
 */
export interface DeclarationHistoryReport {
    'mode': Gds802ReapplyMode;
    /**
     * Every declared batch of the organization
     */
    'batchesConsidered': number;
    /**
     * The batches recovered or not completed, by batch number
     */
    'batches': Array<DeclarationHistoryBatchOutcome>;
}


/**
 * What one batch\'s stored retouren came to, and where that leaves it.
 */
export interface Gds802ReapplyBatchOutcome {
    'batchId': string;
    /**
     * The batch number, which is also the `Factuurnummer` its retouren echo.
     */
    'batchNumber': string;
    /**
     * How many stored retouren this batch\'s answer was derived from.
     */
    'retourCount': number;
    /**
     * The status the batch carries today, or absent when it could not be read.
     */
    'statusBefore'?: BillingBatchStatus;
    /**
     * What it says after re-applying — under `DRY_RUN` what it *would* say, worked out by the same code and then rolled back. Absent when nothing was re-applied at all. 
     */
    'statusAfter'?: BillingBatchStatus;
    'result': Gds802ReapplyResult;
    /**
     * Why a `SKIPPED` batch could not take its answer.
     */
    'skipReason'?: Gds802ReturnFileOutcome;
}


/**
 * - `DRY_RUN`: work out what would change with the same code the write uses, then roll it   back. Nothing is written. - `APPLY`: write it.  Required rather than defaulted: this moves billing statuses on data a practice has been looking at, so the caller says which run it wants. 
 */

export const Gds802ReapplyMode = {
    DryRun: 'DRY_RUN',
    Apply: 'APPLY'
} as const;

export type Gds802ReapplyMode = typeof Gds802ReapplyMode[keyof typeof Gds802ReapplyMode];


/**
 * What one re-apply run found and did (#4511). The counts are derived from `batches` rather than tallied beside it, so a summary cannot disagree with the rows it summarises. 
 */
export interface Gds802ReapplyReport {
    'mode': Gds802ReapplyMode;
    /**
     * Every retour the organization has stored, including those in `batches` and the `retourenWithoutBatch` that name none. 
     */
    'retourenConsidered': number;
    /**
     * Stored retouren naming no batch, so there is nothing to re-apply them against.
     */
    'retourenWithoutBatch': number;
    /**
     * How many batches each status change accounts for, busiest first.
     */
    'transitions': Array<Gds802ReapplyTransition>;
    /**
     * One entry per batch that has a stored retour older than the fix, by batch number.
     */
    'batches': Array<Gds802ReapplyBatchOutcome>;
    'movedCount': number;
    'unchangedCount': number;
    'skippedCount': number;
    'failedCount': number;
}


/**
 * Whether the run writes what it works out, or only reports it.
 */
export interface Gds802ReapplyRequest {
    'mode': Gds802ReapplyMode;
}


/**
 * What re-applying one batch\'s stored retouren came to.  - `MOVED`: the batch ends in a different status than it started in. - `UNCHANGED`: the retouren were re-applied and say what the batch already said. - `SKIPPED`: the batch cannot take its answer — see `skipReason`. A batch that was   redeclared holds no declaratieregels (#4427), which is that ticket\'s problem and not   this run\'s. - `FAILED`: the stored retour could not be re-applied. Nothing was written for this batch. 
 */

export const Gds802ReapplyResult = {
    Moved: 'MOVED',
    Unchanged: 'UNCHANGED',
    Skipped: 'SKIPPED',
    Failed: 'FAILED'
} as const;

export type Gds802ReapplyResult = typeof Gds802ReapplyResult[keyof typeof Gds802ReapplyResult];


/**
 * How many batches move from one status to another.
 */
export interface Gds802ReapplyTransition {
    'from': BillingBatchStatus;
    'to': BillingBatchStatus;
    'count': number;
}


/**
 * What became of one retourbericht. `BATCH_NOT_DECLARED` and `NO_BILLING_LINES` are the two a re-apply run can report as a skip reason; the rest belong to the bulk upload path on the app portal. 
 */

export const Gds802ReturnFileOutcome = {
    Applied: 'APPLIED',
    Superseded: 'SUPERSEDED',
    UnknownBatch: 'UNKNOWN_BATCH',
    BatchNotDeclared: 'BATCH_NOT_DECLARED',
    NoBillingLines: 'NO_BILLING_LINES',
    NotARetour: 'NOT_A_RETOUR',
    NoFactuurnummer: 'NO_FACTUURNUMMER',
    Failed: 'FAILED'
} as const;

export type Gds802ReturnFileOutcome = typeof Gds802ReturnFileOutcome[keyof typeof Gds802ReturnFileOutcome];


/**
 * What linking an organization to HubSpot comes to (#4836). - LINKED: linked already, or a company already carries the organization id - LINK_TO_EXISTING: the primary contact\'s one company is linked - CREATE: the primary contact has no company, so one is created - NOT_LINKED: left for an admin to link by hand - FAILED: HubSpot refused or could not be reached - NOT_REACHED: the rate limit ended the run first 
 */

export const HubSpotLinkOutcome = {
    Linked: 'LINKED',
    LinkToExisting: 'LINK_TO_EXISTING',
    Create: 'CREATE',
    NotLinked: 'NOT_LINKED',
    Failed: 'FAILED',
    NotReached: 'NOT_REACHED'
} as const;

export type HubSpotLinkOutcome = typeof HubSpotLinkOutcome[keyof typeof HubSpotLinkOutcome];


/**
 * Why an organization came to its HubSpotLinkOutcome (#4836).
 */

export const HubSpotLinkReason = {
    AlreadyLinked: 'ALREADY_LINKED',
    CompanyCarriesOrganizationId: 'COMPANY_CARRIES_ORGANIZATION_ID',
    ContactHasOneCompany: 'CONTACT_HAS_ONE_COMPANY',
    ContactHasNoCompany: 'CONTACT_HAS_NO_COMPANY',
    NoPrimaryContact: 'NO_PRIMARY_CONTACT',
    NoContactInHubspot: 'NO_CONTACT_IN_HUBSPOT',
    ContactHasSeveralCompanies: 'CONTACT_HAS_SEVERAL_COMPANIES',
    CompanyLinkedToOtherOrganization: 'COMPANY_LINKED_TO_OTHER_ORGANIZATION',
    UnlinkedByHand: 'UNLINKED_BY_HAND',
    HubspotError: 'HUBSPOT_ERROR',
    RateLimited: 'RATE_LIMITED'
} as const;

export type HubSpotLinkReason = typeof HubSpotLinkReason[keyof typeof HubSpotLinkReason];


/**
 * Request to start impersonating a user
 */
export interface ImpersonateUserRequest {
    /**
     * Why the admin is looking at this user\'s data (recorded on the `IMPERSONATION_STARTED` admin audit entry, trimmed). At least 10 characters once surrounding whitespace is trimmed, which is what the pattern states: a non-blank first and last character with at least 8 characters between them. 
     */
    'reason': string;
}
/**
 * Response containing impersonation session details
 */
export interface ImpersonationTokenResponse {
    /**
     * JWT token for the impersonation session
     */
    'token': string;
    /**
     * URL to app.scrivio.nl with impersonation token
     */
    'redirectUrl': string;
    /**
     * Expiration time of the impersonation session (30 minutes from creation)
     */
    'expiresAt': string;
    /**
     * ID of the user being impersonated
     */
    'targetUserId': string;
    /**
     * Email of the user being impersonated
     */
    'targetUserEmail': string;
}
/**
 * Machine-readable finding code raised by the ZPM GGZ rules. Grouped by rule family: structural (source shape), cell (per-value), reference (reference data) and cross-sheet (referential integrity and ZPM invariants). 
 */

export const ImportFindingCode = {
    SheetMissing: 'SHEET_MISSING',
    ColumnMissing: 'COLUMN_MISSING',
    ColumnOrderChanged: 'COLUMN_ORDER_CHANGED',
    RequiredFieldMissing: 'REQUIRED_FIELD_MISSING',
    InvalidDate: 'INVALID_DATE',
    InvalidBsn: 'INVALID_BSN',
    BsnUnknown: 'BSN_UNKNOWN',
    InvalidEnumValue: 'INVALID_ENUM_VALUE',
    InvalidUuid: 'INVALID_UUID',
    UnknownUzovi: 'UNKNOWN_UZOVI',
    UnknownDiagnoseCode: 'UNKNOWN_DIAGNOSE_CODE',
    UnknownSettingCode: 'UNKNOWN_SETTING_CODE',
    UnknownZorgvraagtypeCode: 'UNKNOWN_ZORGVRAAGTYPE_CODE',
    ClientNotFound: 'CLIENT_NOT_FOUND',
    ZorgtrajectNotFound: 'ZORGTRAJECT_NOT_FOUND',
    TrajectStartBeforeZpm: 'TRAJECT_START_BEFORE_ZPM',
    MultiplePrimaryDiagnoses: 'MULTIPLE_PRIMARY_DIAGNOSES',
    NoPrimaryDiagnosis: 'NO_PRIMARY_DIAGNOSIS',
    OverlappingRegiebehandelaar: 'OVERLAPPING_REGIEBEHANDELAAR',
    OverlappingSetting: 'OVERLAPPING_SETTING',
    OverlappingZorgvraagtypering: 'OVERLAPPING_ZORGVRAAGTYPERING',
    TrajectZonderRegiebehandelaar: 'TRAJECT_ZONDER_REGIEBEHANDELAAR',
    MedewerkerNietGevonden: 'MEDEWERKER_NIET_GEVONDEN',
    MedewerkerAmbigu: 'MEDEWERKER_AMBIGU',
    ClientBestaatAlMetActiefZorgtraject: 'CLIENT_BESTAAT_AL_MET_ACTIEF_ZORGTRAJECT',
    ClientBestaatAl: 'CLIENT_BESTAAT_AL'
} as const;

export type ImportFindingCode = typeof ImportFindingCode[keyof typeof ImportFindingCode];


/**
 * Severity of a finding, shared by every import. `ERROR` blocks import; `WARNING` is informational and does not block. 
 */

export const ImportFindingSeverity = {
    Error: 'ERROR',
    Warning: 'WARNING'
} as const;

export type ImportFindingSeverity = typeof ImportFindingSeverity[keyof typeof ImportFindingSeverity];


/**
 * Organization license type
 */

export const LicenseType = {
    FormsOnly: 'FORMS_ONLY',
    Full: 'FULL'
} as const;

export type LicenseType = typeof LicenseType[keyof typeof LicenseType];


/**
 * A sent prestatie written to its redeclared regel although the regel\'s prestatiecode has changed since. The declaration states what was sent, under the code that was sent. 
 */
export interface LooseDeclarationMatch {
    'referentienummer': string;
    /**
     * The prestatiecode the batch\'s GDS801 message declared
     */
    'sentPrestatiecode': string;
    /**
     * The regel\'s prestatiecode now
     */
    'currentPrestatiecode'?: string | null;
}
/**
 * A customer, or an organization Scrivio made for testing or demos (#4773). A `TEST` organization is left out of admin counts, revenue and billing. 
 */

export const OrganizationKind = {
    Customer: 'CUSTOMER',
    Test: 'TEST'
} as const;

export type OrganizationKind = typeof OrganizationKind[keyof typeof OrganizationKind];


/**
 * Where an organization is in its life as a customer: - TRIAL: the free period a self-signup starts with - ACTIVE: a paying customer - ENDING: notice given, still billed until the end date - ENDED: has left; read-only and not billed - CLOSED: gone from every list, all data kept 
 */

export const OrganizationLifecycle = {
    Trial: 'TRIAL',
    Active: 'ACTIVE',
    Ending: 'ENDING',
    Ended: 'ENDED',
    Closed: 'CLOSED'
} as const;

export type OrganizationLifecycle = typeof OrganizationLifecycle[keyof typeof OrganizationLifecycle];


/**
 * Organization status
 */

export const OrganizationStatus = {
    Active: 'ACTIVE',
    Suspended: 'SUSPENDED'
} as const;

export type OrganizationStatus = typeof OrganizationStatus[keyof typeof OrganizationStatus];


/**
 * Mollie\'s test or live mode — the mode of the API key a customer or mandate was created with.
 */

export const PaymentProviderMode = {
    Test: 'TEST',
    Live: 'LIVE'
} as const;

export type PaymentProviderMode = typeof PaymentProviderMode[keyof typeof PaymentProviderMode];


export interface ProblemDetail {
    /**
     * A URI reference that identifies the problem type
     */
    'type': string;
    /**
     * A short, human-readable summary of the problem
     */
    'title': string;
    /**
     * The HTTP status code
     */
    'status': number;
    /**
     * A human-readable explanation specific to this occurrence
     */
    'detail'?: string;
    /**
     * A URI reference that identifies the specific occurrence
     */
    'instance'?: string;
    /**
     * List of validation errors
     */
    'errors'?: Array<ProblemDetailErrorsInner>;
}
export interface ProblemDetailErrorsInner {
    /**
     * Field name that failed validation
     */
    'field'?: string;
    /**
     * Error message
     */
    'message'?: string;
}
/**
 * Public, non-sensitive details of a pending admin invitation, shown on the accept page before the invitee sets a password. 
 */
export interface PublicAdminInvitationResponse {
    /**
     * Email address the invitation was sent to
     */
    'email': string;
    /**
     * Name of the admin role the invitee will receive
     */
    'roleName': string;
    /**
     * Name of the support partner the invitee is scoped to. Null for platform admins.
     */
    'supportPartnerName'?: string | null;
    /**
     * When the invitation expires (ISO 8601)
     */
    'expiresAt': string;
}
/**
 * Result of a dossier purge run (per-table deleted counts and totals)
 */
export interface PurgeReportDto {
    'lifecycle': OrganizationLifecycle;
    /**
     * Whether a real run may go ahead: only for an organization that has left (`ENDED` or `CLOSED`). A dry run answers either way, so the preview can say why not. 
     */
    'purgeAllowed': boolean;
    /**
     * Human-readable description of the purge scope (no PII)
     */
    'scope': string;
    /**
     * Whether this was a dry run (true) or a committed deletion (false)
     */
    'dryRun': boolean;
    /**
     * Per-table number of rows deleted, in plan order (leaves to roots)
     */
    'deletedCountsByTable': { [key: string]: number; };
    /**
     * Total number of rows deleted across all tables
     */
    'totalDeleted': number;
}


/**
 * Scheduled license type change
 */
export interface ScheduledLicenseChangeDto {
    /**
     * Scheduled change ID
     */
    'id': string;
    'licenseType': LicenseType;
    /**
     * Date when the license change takes effect
     */
    'effectiveDate': string;
    /**
     * When the change was scheduled (ISO 8601)
     */
    'scheduledAt': string;
    /**
     * Email of the admin who scheduled the change
     */
    'scheduledBy': string;
}


/**
 * Which plan an UNMANAGED organization goes on, at what agreed price, from when.
 */
export interface SubscriptionActivationRequest {
    /**
     * An offered plan that sells the licence the organization holds.
     */
    'planId': string;
    'billingMode': SubscriptionBillingMode;
    /**
     * First day the subscription is billed. Today (Amsterdam) or later — never backdated. A `CANCELED` subscription is billed from `firstUnbilledDay` instead where that is later. 
     */
    'activationDate': string;
    /**
     * The day the practice signed its contract, where there is one on paper.
     */
    'contractSignedOn'?: string;
    /**
     * The agreed price per seat type, at most one entry each. A seat type the plan prices and this list leaves out is charged the plan\'s price today; an empty list takes the plan\'s prices as they are. 
     */
    'seatPrices': Array<SubscriptionSeatPriceRequest>;
}


/**
 * One organization\'s subscription and everything recorded against it.  As on the practice\'s own read, everything beyond `status`, `seatsToday` and the collections is optional, because an `UNMANAGED` practice has none of it. 
 */
export interface SubscriptionAdminDetailResponse {
    'organizationId': string;
    'status': SubscriptionStatus;
    /**
     * What the plan is called. Absent while `UNMANAGED`.
     */
    'planName'?: string;
    'billingMode'?: SubscriptionBillingMode;
    /**
     * How the next amount is actually collected: `billingMode`, except that `AUTOMATED` without a chargeable mandate is invoiced. Present whenever `billingMode` is. 
     */
    'collectedAs'?: SubscriptionBillingMode;
    /**
     * Why `collectedAs` is `INVOICE` while `billingMode` is `AUTOMATED`, read off the latest mandate. Present exactly then, and absent whenever the chosen mode is the one collected. 
     */
    'collectionFallbackReason'?: SubscriptionCollectionFallbackReason;
    'nextInvoice'?: SubscriptionNextInvoiceResponse;
    /**
     * The day the subscription was put on a plan. Absent before activation.
     */
    'activatedOn'?: string;
    /**
     * The day the practice signed its contract, where there is one on paper.
     */
    'contractSignedOn'?: string;
    /**
     * The day after the last billed period ends — the earliest a `CANCELED` subscription\'s first period starts when it is put back on a plan, whatever `activationDate` says. Absent when no period was ever billed. 
     */
    'firstUnbilledDay'?: string;
    /**
     * First day of the period being billed. Inclusive.
     */
    'currentPeriodStart'?: string;
    /**
     * Last day of the period being billed. **Inclusive.**
     */
    'currentPeriodEnd'?: string;
    /**
     * Whether the practice has given notice. True exactly when `endsOn` is present.
     */
    'cancelAtPeriodEnd'?: boolean;
    /**
     * The subscription\'s last day once notice is given (agreement art. 9.1), inclusive — billed through, never after. Absent while no notice runs. The day after it, the subscription is ended by itself and `endedOn` takes over. 
     */
    'endsOn'?: string;
    /**
     * The day the organisation ended (#4903) — read-only and no longer billed. Absent while it has not, and for one ended before this was recorded. 
     */
    'endedOn'?: string;
    /**
     * The last day, inclusive, on which the ended practice may still export its records — 30 days after `endedOn` (agreement art. 9.3). Absent exactly when `endedOn` is. 
     */
    'exportAvailableUntil'?: string;
    /**
     * Last day of the free trial that is running today, inclusive.
     */
    'trialEndsOn'?: string;
    /**
     * Enabled behandelaar memberships right now. Counted, never a quota.
     */
    'seatsToday': number;
    /**
     * What this subscription agreed to pay per seat at activation — not the plan\'s price today.
     */
    'seatPrices': Array<SubscriptionSeatPriceResponse>;
    /**
     * Granted, not revoked and not yet ended — including one that starts later. Ordered by start date.
     */
    'activeDiscounts': Array<SubscriptionAdminDiscountResponse>;
    /**
     * Revoked or ended. Kept because an invoice issued under one must keep resolving it. Newest grant first. 
     */
    'pastDiscounts': Array<SubscriptionAdminDiscountResponse>;
    /**
     * The period ledger, newest period first.
     */
    'periods': Array<SubscriptionPeriodResponse>;
    /**
     * Every invoice issued to this practice, credit notes included, newest first.
     */
    'invoices': Array<SubscriptionInvoiceSummaryResponse>;
    /**
     * Every Mollie payment against this subscription, newest first.
     */
    'payments': Array<SubscriptionPaymentResponse>;
    /**
     * Every Mollie mandate, revoked ones included, newest first.
     */
    'mandates': Array<SubscriptionMandateResponse>;
}


/**
 * A dated percentage off one subscription, with who granted it and whether it was revoked.
 */
export interface SubscriptionAdminDiscountResponse {
    'id': string;
    /**
     * 0..100, two decimals. 100 is a free period.
     */
    'percentage': number;
    /**
     * First day the discount applies. Inclusive.
     */
    'startsOn': string;
    /**
     * Last day the discount applies, inclusive. Absent means open-ended.
     */
    'endsOn'?: string;
    /**
     * What the invoice calls it.
     */
    'label': string;
    /**
     * The contract clause it comes from, where there is one.
     */
    'contractArticle'?: string;
    'source': SubscriptionDiscountSource;
    'grantedAt': string;
    /**
     * When the discount stopped applying. Absent unless it was revoked.
     */
    'revokedAt'?: string;
}


/**
 * How Scrivio collects from the practice. Never `BillingMethod`, which is the other direction of money.
 */

export const SubscriptionBillingMode = {
    Invoice: 'INVOICE',
    Automated: 'AUTOMATED'
} as const;

export type SubscriptionBillingMode = typeof SubscriptionBillingMode[keyof typeof SubscriptionBillingMode];


export interface SubscriptionBoekhoudingMailRequest {
    /**
     * The bookkeeping inbox. Null, empty or omitted clears it.
     */
    'email'?: string | null;
}
export interface SubscriptionBoekhoudingMailResponse {
    /**
     * The bookkeeping inbox, or null while none is set.
     */
    'email'?: string | null;
    /**
     * Whether this deployment mails to the address at all — on in production only.
     */
    'mailEnabled': boolean;
}
/**
 * Why an `AUTOMATED` subscription is collected as `INVOICE`. `NO_MANDATE` — the practice has not signed one yet; `MANDATE_PENDING` — a SEPA mandate is signed and awaiting its first collection; `MANDATE_REVOKED` — the mandate was usable and has been withdrawn; `MANDATE_INVALID` — Mollie refused it, so it was never usable. 
 */

export const SubscriptionCollectionFallbackReason = {
    NoMandate: 'NO_MANDATE',
    MandatePending: 'MANDATE_PENDING',
    MandateRevoked: 'MANDATE_REVOKED',
    MandateInvalid: 'MANDATE_INVALID'
} as const;

export type SubscriptionCollectionFallbackReason = typeof SubscriptionCollectionFallbackReason[keyof typeof SubscriptionCollectionFallbackReason];


/**
 * A dated percentage off one subscription, as an admin grants it.
 */
export interface SubscriptionDiscountGrantRequest {
    /**
     * 0..100, at most two decimals. 100 is a free period.
     */
    'percentage': number;
    /**
     * First day the discount applies. Inclusive.
     */
    'startsOn': string;
    /**
     * Last day the discount applies, inclusive, and not before `startsOn`. Omit for open-ended.
     */
    'endsOn'?: string;
    /**
     * What the invoice calls it.
     */
    'label': string;
    /**
     * The contract clause it comes from, where there is one — the reference alone, `4.1`; the invoice adds the word \"artikel\". 
     */
    'contractArticle'?: string;
}
/**
 * A granted discount, and the already-issued invoices whose periods its dates overlap.
 */
export interface SubscriptionDiscountGrantResponse {
    'discount': SubscriptionAdminDiscountResponse;
    /**
     * Invoices already issued for a period the discount\'s dates overlap, oldest period first. None of them was changed: the discount reaches the periods computed after the grant, and what it would have taken off these is `creditEuro`, credited on the next invoice. 
     */
    'affectedInvoices': Array<SubscriptionInvoiceSummaryResponse>;
    /**
     * What the discount gives back on `affectedInvoices`, excl. BTW — credited on the next invoice (#4612). Zero when it overlaps none, and for an invoice written off. 
     */
    'creditEuro': number;
}
/**
 * Who granted a discount — the system\'s own signup trial, or a Scrivio admin.
 */

export const SubscriptionDiscountSource = {
    SelfSignupTrial: 'SELF_SIGNUP_TRIAL',
    Admin: 'ADMIN'
} as const;

export type SubscriptionDiscountSource = typeof SubscriptionDiscountSource[keyof typeof SubscriptionDiscountSource];


/**
 * The day a bank transfer settling the invoice arrived.
 */
export interface SubscriptionInvoiceMarkPaidRequest {
    /**
     * The day the money arrived. Today (Amsterdam) or earlier.
     */
    'paidOn': string;
}
/**
 * Kept in Dutch, the vocabulary of Scrivio\'s own debtor overview.
 */

export const SubscriptionInvoiceStatus = {
    Concept: 'CONCEPT',
    Verstuurd: 'VERSTUURD',
    Betaald: 'BETAALD',
    Oninbaar: 'ONINBAAR'
} as const;

export type SubscriptionInvoiceStatus = typeof SubscriptionInvoiceStatus[keyof typeof SubscriptionInvoiceStatus];


/**
 * An invoice Scrivio issued to the practice. Immutable once issued.
 */
export interface SubscriptionInvoiceSummaryResponse {
    'id': string;
    /**
     * Unique platform-wide; the series is Scrivio\'s.
     */
    'invoiceNumber': string;
    'invoiceDate': string;
    'dueDate': string;
    /**
     * What the invoice calls the period, e.g. \"juli 2026\".
     */
    'periodLabel': string;
    /**
     * The period it was issued from. Absent on a credit note.
     */
    'periodId'?: string;
    'subtotalEuro': number;
    'btwEuro': number;
    'totalEuro': number;
    'status': SubscriptionInvoiceStatus;
    'billingModeAtIssue': SubscriptionBillingMode;
    /**
     * Whether the invoice was issued with an address to mail it to. One without is never sent; the address itself stays out of this list. 
     */
    'hasBilledEmail': boolean;
    /**
     * The earlier invoice this one carries a credit for (#4612): a discount granted after that invoice was issued is credited on the next one. Not a credit note — this invoice still charges its own period. 
     */
    'creditsInvoiceId'?: string;
    'issuedAt': string;
    'sentAt'?: string;
    'paidAt'?: string;
}


/**
 * Why Scrivio has given up collecting the invoice.
 */
export interface SubscriptionInvoiceWriteOffRequest {
    /**
     * Recorded in the admin audit log. Not blank.
     */
    'reason': string;
}
/**
 * A Mollie mandate authorising Scrivio to collect.
 */
export interface SubscriptionMandateResponse {
    'id': string;
    'mollieMandateId': string;
    'method': string;
    'status': SubscriptionMandateStatus;
    /**
     * The masked instrument Mollie returns — never a full account number.
     */
    'displayLabel': string;
    /**
     * When the SEPA mandate was signed. Absent for a card mandate.
     */
    'signatureDate'?: string;
    'revokedAt'?: string;
    'mode': PaymentProviderMode;
    /**
     * Whether it was given in the other Mollie mode than the one Scrivio collects in now — a test-mode mandate after the switch to the live key. The current key cannot reach it, so it is never chargeable and cannot be revoked. 
     */
    'otherMode': boolean;
    /**
     * Whether Scrivio may charge off-session under it now — VALID, not revoked, and not from the other mode.
     */
    'chargeable': boolean;
    'createdAt': string;
}



export const SubscriptionMandateStatus = {
    Pending: 'PENDING',
    Valid: 'VALID',
    Invalid: 'INVALID'
} as const;

export type SubscriptionMandateStatus = typeof SubscriptionMandateStatus[keyof typeof SubscriptionMandateStatus];


/**
 * What the next invoice will come to if nothing changes before it is issued — the same projection the practice\'s own read states (`openapi-subscriptions.yaml`).  Absent where no invoice is coming — a subscription Scrivio collects nothing from, one that stops at the end of the running period, a period that comes to nothing, and an organisation that is suspended or a test organisation, neither of which is invoiced (#4897). 
 */
export interface SubscriptionNextInvoiceResponse {
    /**
     * First day of the period it covers. Inclusive.
     */
    'periodStart': string;
    /**
     * Last day of the period it covers. Inclusive.
     */
    'periodEnd': string;
    /**
     * The day it is expected to be issued: the period\'s first day, or today for a period that has already begun and has not been billed yet. 
     */
    'issuedOn': string;
    /**
     * Excluding BTW, discounts already taken off.
     */
    'subtotalEuro': number;
    'btwEuro': number;
    /**
     * What the practice will be asked to pay.
     */
    'totalEuro': number;
}
export interface SubscriptionOverdueInvoiceResponse {
    'invoiceId': string;
    'invoiceNumber': string;
    'organizationId': string;
    'organizationName': string;
    'dueDate': string;
    'totalEuro': number;
    'status': SubscriptionInvoiceStatus;
    /**
     * Set when the direct debit that had paid this invoice was reversed by the practice, which reopened it. Such an invoice is listed whether or not its due date has passed, and is not debited again: it is settled by talking to the practice, not by a retry. 
     */
    'chargedBackAt'?: string;
}


/**
 * `FIRST` establishes the mandate; `RENEWAL` and `RETRY` are direct debits under it; `ONE_OFF` is a payment the practice started itself for an invoice it owes by transfer. 
 */

export const SubscriptionPaymentKind = {
    First: 'FIRST',
    Renewal: 'RENEWAL',
    Retry: 'RETRY',
    OneOff: 'ONE_OFF'
} as const;

export type SubscriptionPaymentKind = typeof SubscriptionPaymentKind[keyof typeof SubscriptionPaymentKind];


/**
 * A Mollie payment against the subscription. Mollie ids only — no card number and no IBAN.
 */
export interface SubscriptionPaymentResponse {
    'id': string;
    /**
     * Absent on the FIRST payment, which establishes the mandate.
     */
    'invoiceId'?: string;
    'molliePaymentId': string;
    'kind': SubscriptionPaymentKind;
    'status': SubscriptionPaymentStatus;
    'amountEuro': number;
    /**
     * Mollie\'s method string. Absent while the payment is OPEN.
     */
    'method'?: string;
    /**
     * Mollie\'s reason for a failed or canceled payment.
     */
    'failureReason'?: string;
    'paidAt'?: string;
    /**
     * Set when a payment that had been PAID was reversed.
     */
    'chargedBackAt'?: string;
    'createdAt': string;
}


/**
 * Mollie\'s payment state machine, uppercased. A chargeback is `chargedBackAt`, not a status.
 */

export const SubscriptionPaymentStatus = {
    Open: 'OPEN',
    Pending: 'PENDING',
    Authorized: 'AUTHORIZED',
    Paid: 'PAID',
    Canceled: 'CANCELED',
    Expired: 'EXPIRED',
    Failed: 'FAILED'
} as const;

export type SubscriptionPaymentStatus = typeof SubscriptionPaymentStatus[keyof typeof SubscriptionPaymentStatus];



export const SubscriptionPeriodLineKind = {
    Seat: 'SEAT',
    Fee: 'FEE',
    Discount: 'DISCOUNT',
    Credit: 'CREDIT'
} as const;

export type SubscriptionPeriodLineKind = typeof SubscriptionPeriodLineKind[keyof typeof SubscriptionPeriodLineKind];


/**
 * One line of a billed period, as stored and as the invoice renders it.
 */
export interface SubscriptionPeriodLineResponse {
    'id': string;
    'kind': SubscriptionPeriodLineKind;
    'seatType'?: SubscriptionSeatType;
    /**
     * What the invoice says on this line, in Dutch.
     */
    'description': string;
    /**
     * The smaller second line beneath it.
     */
    'subDescription'?: string;
    /**
     * Fractional, four decimals — a seat billed for part of a period is part of a seat.
     */
    'quantity': number;
    /**
     * Negative on a discount line.
     */
    'unitPriceEuro': number;
    /**
     * Signed; a discount line is negative.
     */
    'amountEuro': number;
    /**
     * The grant a discount line came from.
     */
    'discountId'?: string;
}


/**
 * What the subscription was billed for which days, and the lines that add up to it.
 */
export interface SubscriptionPeriodResponse {
    'id': string;
    /**
     * Inclusive.
     */
    'periodStart': string;
    /**
     * Inclusive.
     */
    'periodEnd': string;
    'status': SubscriptionPeriodStatus;
    /**
     * The sum of the lines, excluding BTW.
     */
    'subtotalEuro': number;
    /**
     * Copied from the plan when the period was computed.
     */
    'btwPercentage': number;
    'btwEuro': number;
    /**
     * subtotalEuro + btwEuro.
     */
    'totalEuro': number;
    /**
     * The invoice issued for this period, once there is one.
     */
    'invoiceId'?: string;
    /**
     * In the order the invoice prints them — seats, then the fee, then the discounts in the order they stacked.
     */
    'lines': Array<SubscriptionPeriodLineResponse>;
}


/**
 * Whether a billed period\'s lines and totals are still moving.
 */

export const SubscriptionPeriodStatus = {
    Open: 'OPEN',
    Closed: 'CLOSED'
} as const;

export type SubscriptionPeriodStatus = typeof SubscriptionPeriodStatus[keyof typeof SubscriptionPeriodStatus];


/**
 * A plan in the price book, at what its seats cost today.
 */
export interface SubscriptionPlanResponse {
    'id': string;
    /**
     * The plan\'s stable key.
     */
    'code': string;
    'name': string;
    'licenseType': LicenseType;
    'btwPercentage': number;
    /**
     * What a seat costs on this plan today — the default an admin may change when activating.
     */
    'seatPrices': Array<SubscriptionSeatPriceResponse>;
}


/**
 * A practice one of the portfolio\'s lists names.
 */
export interface SubscriptionPortfolioOrganizationResponse {
    'organizationId': string;
    'organizationName': string;
}
/**
 * What the customers\' subscriptions add up to today. Every figure and list but `suspendedOrganizations` is over one population — the organisations that count as a customer (#4900): kind `CUSTOMER`, not suspended, and in their trial, active, or with notice given. A test organisation, and one that has ended or closed, is in none of it. A suspended customer is in none of the figures and is listed under `suspendedOrganizations` instead. 
 */
export interface SubscriptionPortfolioResponse {
    /**
     * What the paying practices bring in per month at today\'s rates, excluding BTW — the total of `GET /api/admin/subscriptions/revenue`, which lists them: the `ACTIVE` and `PAST_DUE` subscriptions of the customers. Each is today\'s seats at the agreed seat price, a yearly plan divided by twelve, less the discounts that apply today, stacked in the order they were granted. A running free trial is a 100% discount, so a practice on trial adds nothing. `UNMANAGED` practices are invoiced by hand at prices Scrivio has not recorded, so they add nothing either. 
     */
    'monthlyRecurringRevenueEuro': number;
    /**
     * Today\'s seats across the customers\' collected subscriptions.
     */
    'seatsSold': number;
    /**
     * The customers\' subscriptions per status, one entry per status in the order `SubscriptionStatus` declares, zero counts included. 
     */
    'subscriptionsByStatus': Array<SubscriptionStatusCountResponse>;
    /**
     * Customers in their free period whose trial\'s last day falls within the next seven days (today included), soonest first. 
     */
    'trialsEndingSoon': Array<SubscriptionTrialEndingResponse>;
    /**
     * The customers\' invoices still owed — `CONCEPT` or `VERSTUURD` — whose due date has passed, and any a chargeback reopened whatever its due date, oldest due date first. Credit notes are never owed by the practice and are left out. 
     */
    'overdueInvoices': Array<SubscriptionOverdueInvoiceResponse>;
    /**
     * The practices still `UNMANAGED` — invoiced by hand, on no plan, and collected from by nothing — by name. They predate this context (#4463); no creation path produces one any more, so this list only ever shrinks, one activation at a time (#4551). 
     */
    'unmanagedOrganizations': Array<SubscriptionPortfolioOrganizationResponse>;
    /**
     * The customers that have given notice, by name. They are still billed until the end of their notice period, so they are counted in every figure above as well. 
     */
    'endingOrganizations': Array<SubscriptionPortfolioOrganizationResponse>;
    /**
     * The customers on an administrative hold (\"gepauzeerd\"), by name. A suspended customer is not invoiced and is in none of the figures above; what it owed before the hold is on its own subscription ledger. 
     */
    'suspendedOrganizations': Array<SubscriptionPortfolioOrganizationResponse>;
}
/**
 * What one practice brings in per month today, and what that is made of.
 */
export interface SubscriptionPracticeRevenueResponse {
    'organizationId': string;
    'organizationName': string;
    'status': SubscriptionStatus;
    /**
     * A free trial is running today, so `monthlyRevenueEuro` is zero.
     */
    'inTrial': boolean;
    /**
     * The behandelaren occupying its seats today, by name. Their number is the seat count.
     */
    'behandelaren': Array<SubscriptionSeatHolderResponse>;
    /**
     * The agreed price per seat, by seat type — never the plan\'s price today.
     */
    'seatPrices': Array<SubscriptionSeatPriceResponse>;
    /**
     * The discounts applying today, in the order they stack.
     */
    'discounts': Array<SubscriptionAdminDiscountResponse>;
    /**
     * Per month at today\'s rates, excluding BTW: the seats at the agreed seat price, a yearly plan divided by twelve, less the discounts applying today. 
     */
    'monthlyRevenueEuro': number;
}


/**
 * The recurring revenue Scrivio earns today, per practice, and its totals.
 */
export interface SubscriptionRevenueResponse {
    /**
     * The sum of every row\'s `monthlyRevenueEuro`, excluding BTW.
     */
    'monthlyRecurringRevenueEuro': number;
    /**
     * The rows not on a free trial.
     */
    'payingPractices': number;
    /**
     * The behandelaren of the rows not on a free trial — a trial\'s seats are not billed.
     */
    'billedBehandelaren': number;
    /**
     * Paying practices by name, then the ones on a free trial by name.
     */
    'practices': Array<SubscriptionPracticeRevenueResponse>;
}
/**
 * A behandelaar occupying a seat.
 */
export interface SubscriptionSeatHolderResponse {
    'userId': string;
    'name': string;
}
/**
 * The price agreed for one kind of seat.
 */
export interface SubscriptionSeatPriceRequest {
    'seatType': SubscriptionSeatType;
    /**
     * Euros per seat per billing interval, excluding BTW, in whole cents.
     */
    'pricePerSeatEuro': number;
}


/**
 * The agreed price of one kind of seat on this subscription.
 */
export interface SubscriptionSeatPriceResponse {
    'seatType': SubscriptionSeatType;
    /**
     * Euros per seat per billing interval, excluding BTW. Zero is a price, not a gap.
     */
    'pricePerSeatEuro': number;
}


/**
 * What kind of seat is being priced.
 */

export const SubscriptionSeatType = {
    Behandelaar: 'BEHANDELAAR'
} as const;

export type SubscriptionSeatType = typeof SubscriptionSeatType[keyof typeof SubscriptionSeatType];


/**
 * Where the subscription stands. A free trial is a 100% discount, not a status.
 */

export const SubscriptionStatus = {
    Unmanaged: 'UNMANAGED',
    Active: 'ACTIVE',
    PastDue: 'PAST_DUE',
    Unpaid: 'UNPAID',
    Canceled: 'CANCELED'
} as const;

export type SubscriptionStatus = typeof SubscriptionStatus[keyof typeof SubscriptionStatus];


export interface SubscriptionStatusCountResponse {
    'status': SubscriptionStatus;
    'count': number;
}


export interface SubscriptionTrialEndingResponse {
    'organizationId': string;
    'organizationName': string;
    /**
     * Last day of the trial, inclusive.
     */
    'trialEndsOn': string;
}
/**
 * Support partner information
 */
export interface SupportPartnerDto {
    /**
     * Support partner ID
     */
    'id': string;
    /**
     * Support partner name
     */
    'name': string;
}
/**
 * Support partner details
 */
export interface SupportPartnerResponse {
    /**
     * Unique support partner identifier
     */
    'id': string;
    /**
     * Support partner name
     */
    'name': string;
    /**
     * Contact email address
     */
    'email'?: string | null;
    /**
     * Contact phone number
     */
    'phone'?: string | null;
    /**
     * Business address
     */
    'address'?: string | null;
    'status': SupportPartnerStatus;
    /**
     * List of organization IDs assigned to this support partner
     */
    'organizationIds': Array<string>;
    /**
     * Number of admin users linked to this support partner
     */
    'agentCount': number;
    /**
     * Creation timestamp (ISO 8601)
     */
    'createdAt': string;
    /**
     * Last update timestamp (ISO 8601)
     */
    'updatedAt': string;
}


/**
 * Support partner status
 */

export const SupportPartnerStatus = {
    Active: 'ACTIVE',
    Inactive: 'INACTIVE'
} as const;

export type SupportPartnerStatus = typeof SupportPartnerStatus[keyof typeof SupportPartnerStatus];


/**
 * Request to update an admin user
 */
export interface UpdateAdminUserRequest {
    /**
     * New role ID to assign to the admin user
     */
    'roleId': string;
}
/**
 * Request to update organizations assigned to a support partner
 */
export interface UpdateSupportPartnerOrganizationsRequest {
    /**
     * List of organization IDs to assign to this support partner
     */
    'organizationIds': Array<string>;
}
/**
 * Request to update a support partner
 */
export interface UpdateSupportPartnerRequest {
    /**
     * Support partner name
     */
    'name'?: string;
    /**
     * Contact email address
     */
    'email'?: string;
    /**
     * Contact phone number
     */
    'phone'?: string;
    /**
     * Business address
     */
    'address'?: string;
}
/**
 * User status
 */

export const UserStatus = {
    Active: 'ACTIVE',
    Disabled: 'DISABLED'
} as const;

export type UserStatus = typeof UserStatus[keyof typeof UserStatus];


/**
 * How one of an organization\'s VECOZO streams travels (ADR-049 §2): `PORTAAL` is the practice\'s own export/import in VECOZO\'s portal, `BERICHTENSERVICE` is Scrivio sending and pulling the messages itself. Not the VECOZO environment, which is the deployment\'s. 
 */

export const VecozoKanaal = {
    Portaal: 'PORTAAL',
    Berichtenservice: 'BERICHTENSERVICE'
} as const;

export type VecozoKanaal = typeof VecozoKanaal[keyof typeof VecozoKanaal];


/**
 * The channel of each of an organization\'s VECOZO stream families (ADR-049 §2)
 */
export interface VecozoKanalen {
    'gds801': VecozoKanaal;
    'ijw': VecozoKanaal;
}


/**
 * A family of VECOZO berichtstromen that moves between channels as one (ADR-049 §2): `GDS801` (GDS801 out, GDS802 in — toestemmingsverklaring Declareren) and `IJW` (JW301–JW325 — both `VSP-JW` and `Jeugdwet declaraties`). 
 */

export const VecozoStroom = {
    Gds801: 'GDS801',
    Ijw: 'IJW'
} as const;

export type VecozoStroom = typeof VecozoStroom[keyof typeof VecozoStroom];


/**
 * One batch whose verzekeraarfacturen did not bill what it declared.
 */
export interface VerzekeraarfactuurRepairOutcome {
    'batchId': string;
    'batchNumber': string;
    /**
     * What the batch declared, which is what its invoices total once repaired
     */
    'declaredTotal': number;
    'result': VerzekeraarfactuurRepairResult;
    /**
     * What its stored invoices totalled; absent when it had none
     */
    'previousTotal'?: number;
    /**
     * What they total now; present only when `result` is `REGENERATED`
     */
    'regeneratedTotal'?: number;
    /**
     * The boekhouding booked the replaced version. Send this batch to the boekhouding again; the corrected invoice goes out as a correction under the same number. 
     */
    'needsResend': boolean;
    /**
     * Why the invoice cannot be produced; present only when `result` is `REFUSED`
     */
    'refusalReason'?: string;
}


/**
 * What one verzekeraarfactuur repair found (#4730). Only the batches whose invoices did not already bill what they declared are listed. 
 */
export interface VerzekeraarfactuurRepairReport {
    'mode': Gds802ReapplyMode;
    /**
     * Every declared batch of the organization
     */
    'batchesConsidered': number;
    /**
     * The batches that were regenerated, refused or failed, by batch number
     */
    'batches': Array<VerzekeraarfactuurRepairOutcome>;
}


/**
 * What regenerating one batch\'s verzekeraarfacturen came to.  - `REGENERATED`: rebuilt from the batch\'s declarations — or, in a dry run, would be. - `REFUSED`: the invoice cannot be produced; `refusalReason` says why. Nothing was written. - `FAILED`: something unanticipated. The cause is in the log; nothing was written. 
 */

export const VerzekeraarfactuurRepairResult = {
    Regenerated: 'REGENERATED',
    Refused: 'REFUSED',
    Failed: 'FAILED'
} as const;

export type VerzekeraarfactuurRepairResult = typeof VerzekeraarfactuurRepairResult[keyof typeof VerzekeraarfactuurRepairResult];


/**
 * A single validation finding, attributed to a sheet and (where known) a row and column
 */
export interface ZpmFinding {
    /**
     * Sheet the finding applies to
     */
    'sheet': string;
    /**
     * Excel row number (1-based), or null for sheet-level findings
     */
    'row'?: number | null;
    /**
     * Column name, or null when the finding is not tied to one column
     */
    'column'?: string | null;
    'severity': ImportFindingSeverity;
    'code': ImportFindingCode;
    /**
     * Human-readable explanation in Dutch
     */
    'message': string;
    /**
     * The offending cell value, or null when not applicable
     */
    'value'?: string | null;
}


/**
 * A user who may be selected as the author (`created_by`) of a ZPM import.
 */
export interface ZpmImportAuthor {
    /**
     * The user\'s id, passed as `authorUserId` on commit
     */
    'userId': string;
    /**
     * The user\'s display name for the dropdown
     */
    'name': string;
}
/**
 * The organization users eligible to be selected as a ZPM import author (#3048).
 */
export interface ZpmImportAuthorList {
    /**
     * Users of the organization holding the `imports.zpm.commit` permission
     */
    'authors': Array<ZpmImportAuthor>;
}
/**
 * Created/updated/skipped counts for a single master-data entity
 */
export interface ZpmImportEntityResult {
    /**
     * Sheet name of the entity as it appears in the workbook
     */
    'entity': string;
    /**
     * Number of records created for this entity
     */
    'created': number;
    /**
     * Number of records updated for this entity
     */
    'updated': number;
    /**
     * Number of records skipped for this entity
     */
    'skipped': number;
}
/**
 * Result of committing a ZPM GGZ import workbook: how many records were created, updated or skipped, broken down per master-data entity. 
 */
export interface ZpmImportResult {
    /**
     * Name of the committed workbook
     */
    'fileName': string;
    /**
     * Per-entity counts, one entry per master-data sheet
     */
    'entities': Array<ZpmImportEntityResult>;
    /**
     * Total number of records created across all entities
     */
    'totalCreated': number;
    /**
     * Total number of records updated across all entities
     */
    'totalUpdated': number;
    /**
     * Total number of records skipped across all entities
     */
    'totalSkipped': number;
}
/**
 * Row and finding counts for a single sheet
 */
export interface ZpmSheetSummary {
    /**
     * Sheet name as it appears in the workbook
     */
    'sheet': string;
    /**
     * Number of data rows read (spec rows and the example row excluded)
     */
    'rows': number;
    /**
     * Number of ERROR findings on this sheet
     */
    'errors': number;
    /**
     * Number of WARNING findings on this sheet
     */
    'warnings': number;
}
/**
 * Result of validating a ZPM GGZ import workbook
 */
export interface ZpmValidationReport {
    /**
     * Name of the uploaded workbook
     */
    'fileName': string;
    'summary': ZpmValidationSummary;
    /**
     * All findings, ordered by sheet and row
     */
    'findings': Array<ZpmFinding>;
}
/**
 * Aggregated counts over the whole workbook
 */
export interface ZpmValidationSummary {
    /**
     * Per-sheet counts, one entry per validated sheet
     */
    'sheets': Array<ZpmSheetSummary>;
    /**
     * Total number of ERROR findings across all sheets
     */
    'totalErrors': number;
    /**
     * Total number of WARNING findings across all sheets
     */
    'totalWarnings': number;
    /**
     * True when the workbook has no ERROR findings
     */
    'readyToImport': boolean;
}

/**
 * AdminAdminUsersApi - axios parameter creator
 */
export const AdminAdminUsersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Creates a pending admin invitation and emails the invitee a single-use acceptance link. No Firebase account or admin user record is created at invite-time — that is deferred to acceptance, so the invitation is the pending state (mirroring the organization invitation flow).  **Process:** 1. Validates the email is not already registered as an admin and has no    pending invitation 2. Issues a `PENDING` invitation with a secure token and a 7-day expiry 3. Emails the invitee a link to `/admin/accept/{token}` 4. Logs the action to the admin audit log  **Platform admins only** - requires `admin.admin-users.manage` permission. 
         * @summary Invite an admin user
         * @param {CreateAdminUserRequest} createAdminUserRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createAdminUser: async (createAdminUserRequest: CreateAdminUserRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'createAdminUserRequest' is not null or undefined
            assertParamExists('createAdminUser', 'createAdminUserRequest', createAdminUserRequest)
            const localVarPath = `/api/admin/admin-users`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createAdminUserRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Soft deletes an admin user by setting the deletedAt timestamp.  The admin user\'s Firebase account is disabled, preventing login. The record is preserved for audit purposes.  **Platform admins only** - requires `admin.admin-users.manage` permission. 
         * @summary Soft delete admin user
         * @param {string} id Admin user ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteAdminUser: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('deleteAdminUser', 'id', id)
            const localVarPath = `/api/admin/admin-users/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns detailed information about a specific admin user.  **Scoping:** - Platform admins can view any admin user - Support agents can only view admin users in their support partner  Requires `admin.admin-users.view` permission. 
         * @summary Get admin user details
         * @param {string} id Admin user ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAdminUser: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('getAdminUser', 'id', id)
            const localVarPath = `/api/admin/admin-users/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the pending admin invitations — people who have been invited but have not yet accepted, so they no longer exist as admin user rows and are not returned by the admin-user list.  **Scoping:** - Platform admins see all pending invitations, optionally filtered by support partner - Support agents see only their own support partner\'s pending invitations  Requires `admin.admin-users.view` permission. 
         * @summary List pending admin invitations
         * @param {string} [supportPartnerId] Filter by support partner ID (platform admins only)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAdminInvitations: async (supportPartnerId?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/admin/admin-users/invitations`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (supportPartnerId !== undefined) {
                localVarQueryParameter['supportPartnerId'] = supportPartnerId;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns all admin roles, each with its ID and name.  Admin role IDs are environment-specific (seeded with `gen_random_uuid()`), so this endpoint lets clients resolve a valid `roleId` before creating or updating an admin user.  Requires `admin.admin-users.view` permission. 
         * @summary List admin roles
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAdminRoles: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/admin/admin-roles`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns a paginated list of admin users with optional filtering.  **Scoping:** - Platform admins see all admin users - Support agents see only admin users in their support partner  Requires `admin.admin-users.view` permission. 
         * @summary List admin users
         * @param {string} [supportPartnerId] Filter by support partner ID
         * @param {string} [roleId] Filter by role ID
         * @param {string} [search] Filter by email (partial match, case-insensitive)
         * @param {boolean} [includeDeleted] Include soft-deleted admin users
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAdminUsers: async (supportPartnerId?: string, roleId?: string, search?: string, includeDeleted?: boolean, page?: number, size?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/admin/admin-users`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (supportPartnerId !== undefined) {
                localVarQueryParameter['supportPartnerId'] = supportPartnerId;
            }

            if (roleId !== undefined) {
                localVarQueryParameter['roleId'] = roleId;
            }

            if (search !== undefined) {
                localVarQueryParameter['search'] = search;
            }

            if (includeDeleted !== undefined) {
                localVarQueryParameter['includeDeleted'] = includeDeleted;
            }

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Resends a pending admin invitation. Regenerates the invitation token and extends its expiry by another 7 days, then re-sends the acceptance email.  The path `id` is the **invitation** ID (as returned by the invite endpoint), not an admin user ID — admin user records do not exist until an invitation is accepted.  **Platform admins only** - requires `admin.admin-users.manage` permission. 
         * @summary Resend an admin invitation
         * @param {string} id Admin invitation ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resendAdminUserInvite: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('resendAdminUserInvite', 'id', id)
            const localVarPath = `/api/admin/admin-users/{id}/resend-invite`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Clears the target admin user\'s enrolled second factors, forcing them to re-enroll Firebase-native MFA on their next sign-in.  Used by platform admins to recover an admin user who has lost access to their authenticator device.  **Platform admins only** - requires `admin.admin-users.manage` permission. 
         * @summary Reset an admin user\'s MFA
         * @param {string} id Admin user ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resetAdminUserMfa: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('resetAdminUserMfa', 'id', id)
            const localVarPath = `/api/admin/admin-users/{id}/reset-mfa`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json,application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Revokes a pending admin invitation, invalidating its acceptance link. The invitation is marked `REVOKED` (not deleted) so the action stays auditable and the email can be re-invited later.  **Platform admins only** - requires `admin.admin-users.manage` permission. 
         * @summary Revoke a pending admin invitation
         * @param {string} id Admin invitation ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        revokeAdminInvitation: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('revokeAdminInvitation', 'id', id)
            const localVarPath = `/api/admin/admin-users/invitations/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Sets where this admin user signs in to the admin portal (ADR-045 §7). Every admin user is on `FIREBASE` until this moves them; moving them back is the rollback. Nothing in Firebase is changed by either direction.  A request that changes nothing writes no audit entry; a change is written to the admin audit log with the previous and new provider.  **Platform admins only** - requires `admin.admin-users.manage` permission. 
         * @summary Move an admin user\'s admin-portal sign-in between Firebase and Keycloak
         * @param {string} id Admin user ID
         * @param {AdminSetAuthProviderRequest} adminSetAuthProviderRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        setAdminUserAuthProvider: async (id: string, adminSetAuthProviderRequest: AdminSetAuthProviderRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('setAdminUserAuthProvider', 'id', id)
            // verify required parameter 'adminSetAuthProviderRequest' is not null or undefined
            assertParamExists('setAdminUserAuthProvider', 'adminSetAuthProviderRequest', adminSetAuthProviderRequest)
            const localVarPath = `/api/admin/admin-users/{id}/auth-provider`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(adminSetAuthProviderRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Updates an admin user\'s role.  Only the role can be changed after creation. Email and support partner cannot be modified.  **Platform admins only** - requires `admin.admin-users.manage` permission. 
         * @summary Update admin user
         * @param {string} id Admin user ID
         * @param {UpdateAdminUserRequest} updateAdminUserRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateAdminUser: async (id: string, updateAdminUserRequest: UpdateAdminUserRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('updateAdminUser', 'id', id)
            // verify required parameter 'updateAdminUserRequest' is not null or undefined
            assertParamExists('updateAdminUser', 'updateAdminUserRequest', updateAdminUserRequest)
            const localVarPath = `/api/admin/admin-users/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateAdminUserRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AdminAdminUsersApi - functional programming interface
 */
export const AdminAdminUsersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AdminAdminUsersApiAxiosParamCreator(configuration)
    return {
        /**
         * Creates a pending admin invitation and emails the invitee a single-use acceptance link. No Firebase account or admin user record is created at invite-time — that is deferred to acceptance, so the invitation is the pending state (mirroring the organization invitation flow).  **Process:** 1. Validates the email is not already registered as an admin and has no    pending invitation 2. Issues a `PENDING` invitation with a secure token and a 7-day expiry 3. Emails the invitee a link to `/admin/accept/{token}` 4. Logs the action to the admin audit log  **Platform admins only** - requires `admin.admin-users.manage` permission. 
         * @summary Invite an admin user
         * @param {CreateAdminUserRequest} createAdminUserRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createAdminUser(createAdminUserRequest: CreateAdminUserRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminInvitationResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createAdminUser(createAdminUserRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminAdminUsersApi.createAdminUser']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Soft deletes an admin user by setting the deletedAt timestamp.  The admin user\'s Firebase account is disabled, preventing login. The record is preserved for audit purposes.  **Platform admins only** - requires `admin.admin-users.manage` permission. 
         * @summary Soft delete admin user
         * @param {string} id Admin user ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteAdminUser(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteAdminUser(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminAdminUsersApi.deleteAdminUser']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns detailed information about a specific admin user.  **Scoping:** - Platform admins can view any admin user - Support agents can only view admin users in their support partner  Requires `admin.admin-users.view` permission. 
         * @summary Get admin user details
         * @param {string} id Admin user ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getAdminUser(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminUserDto>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getAdminUser(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminAdminUsersApi.getAdminUser']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the pending admin invitations — people who have been invited but have not yet accepted, so they no longer exist as admin user rows and are not returned by the admin-user list.  **Scoping:** - Platform admins see all pending invitations, optionally filtered by support partner - Support agents see only their own support partner\'s pending invitations  Requires `admin.admin-users.view` permission. 
         * @summary List pending admin invitations
         * @param {string} [supportPartnerId] Filter by support partner ID (platform admins only)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listAdminInvitations(supportPartnerId?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<AdminInvitationResponse>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listAdminInvitations(supportPartnerId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminAdminUsersApi.listAdminInvitations']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns all admin roles, each with its ID and name.  Admin role IDs are environment-specific (seeded with `gen_random_uuid()`), so this endpoint lets clients resolve a valid `roleId` before creating or updating an admin user.  Requires `admin.admin-users.view` permission. 
         * @summary List admin roles
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listAdminRoles(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<AdminRoleDto>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listAdminRoles(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminAdminUsersApi.listAdminRoles']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns a paginated list of admin users with optional filtering.  **Scoping:** - Platform admins see all admin users - Support agents see only admin users in their support partner  Requires `admin.admin-users.view` permission. 
         * @summary List admin users
         * @param {string} [supportPartnerId] Filter by support partner ID
         * @param {string} [roleId] Filter by role ID
         * @param {string} [search] Filter by email (partial match, case-insensitive)
         * @param {boolean} [includeDeleted] Include soft-deleted admin users
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listAdminUsers(supportPartnerId?: string, roleId?: string, search?: string, includeDeleted?: boolean, page?: number, size?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminUserListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listAdminUsers(supportPartnerId, roleId, search, includeDeleted, page, size, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminAdminUsersApi.listAdminUsers']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Resends a pending admin invitation. Regenerates the invitation token and extends its expiry by another 7 days, then re-sends the acceptance email.  The path `id` is the **invitation** ID (as returned by the invite endpoint), not an admin user ID — admin user records do not exist until an invitation is accepted.  **Platform admins only** - requires `admin.admin-users.manage` permission. 
         * @summary Resend an admin invitation
         * @param {string} id Admin invitation ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async resendAdminUserInvite(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminInvitationResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.resendAdminUserInvite(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminAdminUsersApi.resendAdminUserInvite']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Clears the target admin user\'s enrolled second factors, forcing them to re-enroll Firebase-native MFA on their next sign-in.  Used by platform admins to recover an admin user who has lost access to their authenticator device.  **Platform admins only** - requires `admin.admin-users.manage` permission. 
         * @summary Reset an admin user\'s MFA
         * @param {string} id Admin user ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async resetAdminUserMfa(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.resetAdminUserMfa(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminAdminUsersApi.resetAdminUserMfa']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Revokes a pending admin invitation, invalidating its acceptance link. The invitation is marked `REVOKED` (not deleted) so the action stays auditable and the email can be re-invited later.  **Platform admins only** - requires `admin.admin-users.manage` permission. 
         * @summary Revoke a pending admin invitation
         * @param {string} id Admin invitation ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async revokeAdminInvitation(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.revokeAdminInvitation(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminAdminUsersApi.revokeAdminInvitation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Sets where this admin user signs in to the admin portal (ADR-045 §7). Every admin user is on `FIREBASE` until this moves them; moving them back is the rollback. Nothing in Firebase is changed by either direction.  A request that changes nothing writes no audit entry; a change is written to the admin audit log with the previous and new provider.  **Platform admins only** - requires `admin.admin-users.manage` permission. 
         * @summary Move an admin user\'s admin-portal sign-in between Firebase and Keycloak
         * @param {string} id Admin user ID
         * @param {AdminSetAuthProviderRequest} adminSetAuthProviderRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async setAdminUserAuthProvider(id: string, adminSetAuthProviderRequest: AdminSetAuthProviderRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminUserDto>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.setAdminUserAuthProvider(id, adminSetAuthProviderRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminAdminUsersApi.setAdminUserAuthProvider']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Updates an admin user\'s role.  Only the role can be changed after creation. Email and support partner cannot be modified.  **Platform admins only** - requires `admin.admin-users.manage` permission. 
         * @summary Update admin user
         * @param {string} id Admin user ID
         * @param {UpdateAdminUserRequest} updateAdminUserRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateAdminUser(id: string, updateAdminUserRequest: UpdateAdminUserRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminUserDto>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateAdminUser(id, updateAdminUserRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminAdminUsersApi.updateAdminUser']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AdminAdminUsersApi - factory interface
 */
export const AdminAdminUsersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AdminAdminUsersApiFp(configuration)
    return {
        /**
         * Creates a pending admin invitation and emails the invitee a single-use acceptance link. No Firebase account or admin user record is created at invite-time — that is deferred to acceptance, so the invitation is the pending state (mirroring the organization invitation flow).  **Process:** 1. Validates the email is not already registered as an admin and has no    pending invitation 2. Issues a `PENDING` invitation with a secure token and a 7-day expiry 3. Emails the invitee a link to `/admin/accept/{token}` 4. Logs the action to the admin audit log  **Platform admins only** - requires `admin.admin-users.manage` permission. 
         * @summary Invite an admin user
         * @param {CreateAdminUserRequest} createAdminUserRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createAdminUser(createAdminUserRequest: CreateAdminUserRequest, options?: RawAxiosRequestConfig): AxiosPromise<AdminInvitationResponse> {
            return localVarFp.createAdminUser(createAdminUserRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Soft deletes an admin user by setting the deletedAt timestamp.  The admin user\'s Firebase account is disabled, preventing login. The record is preserved for audit purposes.  **Platform admins only** - requires `admin.admin-users.manage` permission. 
         * @summary Soft delete admin user
         * @param {string} id Admin user ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteAdminUser(id: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteAdminUser(id, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns detailed information about a specific admin user.  **Scoping:** - Platform admins can view any admin user - Support agents can only view admin users in their support partner  Requires `admin.admin-users.view` permission. 
         * @summary Get admin user details
         * @param {string} id Admin user ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAdminUser(id: string, options?: RawAxiosRequestConfig): AxiosPromise<AdminUserDto> {
            return localVarFp.getAdminUser(id, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the pending admin invitations — people who have been invited but have not yet accepted, so they no longer exist as admin user rows and are not returned by the admin-user list.  **Scoping:** - Platform admins see all pending invitations, optionally filtered by support partner - Support agents see only their own support partner\'s pending invitations  Requires `admin.admin-users.view` permission. 
         * @summary List pending admin invitations
         * @param {string} [supportPartnerId] Filter by support partner ID (platform admins only)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAdminInvitations(supportPartnerId?: string, options?: RawAxiosRequestConfig): AxiosPromise<Array<AdminInvitationResponse>> {
            return localVarFp.listAdminInvitations(supportPartnerId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns all admin roles, each with its ID and name.  Admin role IDs are environment-specific (seeded with `gen_random_uuid()`), so this endpoint lets clients resolve a valid `roleId` before creating or updating an admin user.  Requires `admin.admin-users.view` permission. 
         * @summary List admin roles
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAdminRoles(options?: RawAxiosRequestConfig): AxiosPromise<Array<AdminRoleDto>> {
            return localVarFp.listAdminRoles(options).then((request) => request(axios, basePath));
        },
        /**
         * Returns a paginated list of admin users with optional filtering.  **Scoping:** - Platform admins see all admin users - Support agents see only admin users in their support partner  Requires `admin.admin-users.view` permission. 
         * @summary List admin users
         * @param {string} [supportPartnerId] Filter by support partner ID
         * @param {string} [roleId] Filter by role ID
         * @param {string} [search] Filter by email (partial match, case-insensitive)
         * @param {boolean} [includeDeleted] Include soft-deleted admin users
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAdminUsers(supportPartnerId?: string, roleId?: string, search?: string, includeDeleted?: boolean, page?: number, size?: number, options?: RawAxiosRequestConfig): AxiosPromise<AdminUserListResponse> {
            return localVarFp.listAdminUsers(supportPartnerId, roleId, search, includeDeleted, page, size, options).then((request) => request(axios, basePath));
        },
        /**
         * Resends a pending admin invitation. Regenerates the invitation token and extends its expiry by another 7 days, then re-sends the acceptance email.  The path `id` is the **invitation** ID (as returned by the invite endpoint), not an admin user ID — admin user records do not exist until an invitation is accepted.  **Platform admins only** - requires `admin.admin-users.manage` permission. 
         * @summary Resend an admin invitation
         * @param {string} id Admin invitation ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resendAdminUserInvite(id: string, options?: RawAxiosRequestConfig): AxiosPromise<AdminInvitationResponse> {
            return localVarFp.resendAdminUserInvite(id, options).then((request) => request(axios, basePath));
        },
        /**
         * Clears the target admin user\'s enrolled second factors, forcing them to re-enroll Firebase-native MFA on their next sign-in.  Used by platform admins to recover an admin user who has lost access to their authenticator device.  **Platform admins only** - requires `admin.admin-users.manage` permission. 
         * @summary Reset an admin user\'s MFA
         * @param {string} id Admin user ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resetAdminUserMfa(id: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.resetAdminUserMfa(id, options).then((request) => request(axios, basePath));
        },
        /**
         * Revokes a pending admin invitation, invalidating its acceptance link. The invitation is marked `REVOKED` (not deleted) so the action stays auditable and the email can be re-invited later.  **Platform admins only** - requires `admin.admin-users.manage` permission. 
         * @summary Revoke a pending admin invitation
         * @param {string} id Admin invitation ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        revokeAdminInvitation(id: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.revokeAdminInvitation(id, options).then((request) => request(axios, basePath));
        },
        /**
         * Sets where this admin user signs in to the admin portal (ADR-045 §7). Every admin user is on `FIREBASE` until this moves them; moving them back is the rollback. Nothing in Firebase is changed by either direction.  A request that changes nothing writes no audit entry; a change is written to the admin audit log with the previous and new provider.  **Platform admins only** - requires `admin.admin-users.manage` permission. 
         * @summary Move an admin user\'s admin-portal sign-in between Firebase and Keycloak
         * @param {string} id Admin user ID
         * @param {AdminSetAuthProviderRequest} adminSetAuthProviderRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        setAdminUserAuthProvider(id: string, adminSetAuthProviderRequest: AdminSetAuthProviderRequest, options?: RawAxiosRequestConfig): AxiosPromise<AdminUserDto> {
            return localVarFp.setAdminUserAuthProvider(id, adminSetAuthProviderRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Updates an admin user\'s role.  Only the role can be changed after creation. Email and support partner cannot be modified.  **Platform admins only** - requires `admin.admin-users.manage` permission. 
         * @summary Update admin user
         * @param {string} id Admin user ID
         * @param {UpdateAdminUserRequest} updateAdminUserRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateAdminUser(id: string, updateAdminUserRequest: UpdateAdminUserRequest, options?: RawAxiosRequestConfig): AxiosPromise<AdminUserDto> {
            return localVarFp.updateAdminUser(id, updateAdminUserRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AdminAdminUsersApi - object-oriented interface
 */
export class AdminAdminUsersApi extends BaseAPI {
    /**
     * Creates a pending admin invitation and emails the invitee a single-use acceptance link. No Firebase account or admin user record is created at invite-time — that is deferred to acceptance, so the invitation is the pending state (mirroring the organization invitation flow).  **Process:** 1. Validates the email is not already registered as an admin and has no    pending invitation 2. Issues a `PENDING` invitation with a secure token and a 7-day expiry 3. Emails the invitee a link to `/admin/accept/{token}` 4. Logs the action to the admin audit log  **Platform admins only** - requires `admin.admin-users.manage` permission. 
     * @summary Invite an admin user
     * @param {CreateAdminUserRequest} createAdminUserRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createAdminUser(createAdminUserRequest: CreateAdminUserRequest, options?: RawAxiosRequestConfig) {
        return AdminAdminUsersApiFp(this.configuration).createAdminUser(createAdminUserRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Soft deletes an admin user by setting the deletedAt timestamp.  The admin user\'s Firebase account is disabled, preventing login. The record is preserved for audit purposes.  **Platform admins only** - requires `admin.admin-users.manage` permission. 
     * @summary Soft delete admin user
     * @param {string} id Admin user ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteAdminUser(id: string, options?: RawAxiosRequestConfig) {
        return AdminAdminUsersApiFp(this.configuration).deleteAdminUser(id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns detailed information about a specific admin user.  **Scoping:** - Platform admins can view any admin user - Support agents can only view admin users in their support partner  Requires `admin.admin-users.view` permission. 
     * @summary Get admin user details
     * @param {string} id Admin user ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getAdminUser(id: string, options?: RawAxiosRequestConfig) {
        return AdminAdminUsersApiFp(this.configuration).getAdminUser(id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the pending admin invitations — people who have been invited but have not yet accepted, so they no longer exist as admin user rows and are not returned by the admin-user list.  **Scoping:** - Platform admins see all pending invitations, optionally filtered by support partner - Support agents see only their own support partner\'s pending invitations  Requires `admin.admin-users.view` permission. 
     * @summary List pending admin invitations
     * @param {string} [supportPartnerId] Filter by support partner ID (platform admins only)
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listAdminInvitations(supportPartnerId?: string, options?: RawAxiosRequestConfig) {
        return AdminAdminUsersApiFp(this.configuration).listAdminInvitations(supportPartnerId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns all admin roles, each with its ID and name.  Admin role IDs are environment-specific (seeded with `gen_random_uuid()`), so this endpoint lets clients resolve a valid `roleId` before creating or updating an admin user.  Requires `admin.admin-users.view` permission. 
     * @summary List admin roles
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listAdminRoles(options?: RawAxiosRequestConfig) {
        return AdminAdminUsersApiFp(this.configuration).listAdminRoles(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns a paginated list of admin users with optional filtering.  **Scoping:** - Platform admins see all admin users - Support agents see only admin users in their support partner  Requires `admin.admin-users.view` permission. 
     * @summary List admin users
     * @param {string} [supportPartnerId] Filter by support partner ID
     * @param {string} [roleId] Filter by role ID
     * @param {string} [search] Filter by email (partial match, case-insensitive)
     * @param {boolean} [includeDeleted] Include soft-deleted admin users
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Page size
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listAdminUsers(supportPartnerId?: string, roleId?: string, search?: string, includeDeleted?: boolean, page?: number, size?: number, options?: RawAxiosRequestConfig) {
        return AdminAdminUsersApiFp(this.configuration).listAdminUsers(supportPartnerId, roleId, search, includeDeleted, page, size, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Resends a pending admin invitation. Regenerates the invitation token and extends its expiry by another 7 days, then re-sends the acceptance email.  The path `id` is the **invitation** ID (as returned by the invite endpoint), not an admin user ID — admin user records do not exist until an invitation is accepted.  **Platform admins only** - requires `admin.admin-users.manage` permission. 
     * @summary Resend an admin invitation
     * @param {string} id Admin invitation ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public resendAdminUserInvite(id: string, options?: RawAxiosRequestConfig) {
        return AdminAdminUsersApiFp(this.configuration).resendAdminUserInvite(id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Clears the target admin user\'s enrolled second factors, forcing them to re-enroll Firebase-native MFA on their next sign-in.  Used by platform admins to recover an admin user who has lost access to their authenticator device.  **Platform admins only** - requires `admin.admin-users.manage` permission. 
     * @summary Reset an admin user\'s MFA
     * @param {string} id Admin user ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public resetAdminUserMfa(id: string, options?: RawAxiosRequestConfig) {
        return AdminAdminUsersApiFp(this.configuration).resetAdminUserMfa(id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Revokes a pending admin invitation, invalidating its acceptance link. The invitation is marked `REVOKED` (not deleted) so the action stays auditable and the email can be re-invited later.  **Platform admins only** - requires `admin.admin-users.manage` permission. 
     * @summary Revoke a pending admin invitation
     * @param {string} id Admin invitation ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public revokeAdminInvitation(id: string, options?: RawAxiosRequestConfig) {
        return AdminAdminUsersApiFp(this.configuration).revokeAdminInvitation(id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Sets where this admin user signs in to the admin portal (ADR-045 §7). Every admin user is on `FIREBASE` until this moves them; moving them back is the rollback. Nothing in Firebase is changed by either direction.  A request that changes nothing writes no audit entry; a change is written to the admin audit log with the previous and new provider.  **Platform admins only** - requires `admin.admin-users.manage` permission. 
     * @summary Move an admin user\'s admin-portal sign-in between Firebase and Keycloak
     * @param {string} id Admin user ID
     * @param {AdminSetAuthProviderRequest} adminSetAuthProviderRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public setAdminUserAuthProvider(id: string, adminSetAuthProviderRequest: AdminSetAuthProviderRequest, options?: RawAxiosRequestConfig) {
        return AdminAdminUsersApiFp(this.configuration).setAdminUserAuthProvider(id, adminSetAuthProviderRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Updates an admin user\'s role.  Only the role can be changed after creation. Email and support partner cannot be modified.  **Platform admins only** - requires `admin.admin-users.manage` permission. 
     * @summary Update admin user
     * @param {string} id Admin user ID
     * @param {UpdateAdminUserRequest} updateAdminUserRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateAdminUser(id: string, updateAdminUserRequest: UpdateAdminUserRequest, options?: RawAxiosRequestConfig) {
        return AdminAdminUsersApiFp(this.configuration).updateAdminUser(id, updateAdminUserRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * AdminAgreementsApi - axios parameter creator
 */
export const AdminAgreementsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Streams the signed contract an admin uploaded with a pre-existing contract (#4837). Served by the backend only — the file has no public or signed URL.  `404` for an acceptance of another organization, for one outside a support agent\'s scope, and for one without a file. Every download is written to the admin audit log.  Requires `admin.organizations.view` permission. 
         * @summary The signed PDF of a contract recorded for an organization
         * @param {string} organizationId Organization ID
         * @param {string} acceptanceId The acceptance whose signed contract to download
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAgreementContractFile: async (organizationId: string, acceptanceId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('getAgreementContractFile', 'organizationId', organizationId)
            // verify required parameter 'acceptanceId' is not null or undefined
            assertParamExists('getAgreementContractFile', 'acceptanceId', acceptanceId)
            const localVarPath = `/api/admin/organizations/{organizationId}/agreements/acceptances/{acceptanceId}/contract-file`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)))
                .replace(`{${"acceptanceId"}}`, encodeURIComponent(String(acceptanceId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/pdf,application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Per Scrivio agreement, whether the currently published version is still outstanding, and every acceptance the organization has recorded — document, version, who, when, how, and the party details as they were accepted (#4492).  Requires `admin.organizations.view` permission. 
         * @summary What an organization has agreed to with Scrivio
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getOrganizationAgreements: async (organizationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('getOrganizationAgreements', 'organizationId', organizationId)
            const localVarPath = `/api/admin/organizations/{organizationId}/agreements`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Records that the organization signed a custom Overeenkomst, Verwerkersovereenkomst or both outside Scrivio (#4492, #4837). One acceptance is written per document covered — the one named in `document`, or both when `document` is left out — with method `PRE_EXISTING_CONTRACT`, no version (the contract was individually negotiated, so there is no published version to name), the signing date as when it took effect, and the admin as who recorded it. The party details are the organization\'s as they stand today.  **Per document.** A document the contract does not cover keeps whatever it was bound by — its status, its re-acceptance prompt and its confirmation mail are untouched. Recording both at once is one request without `document`, with one `file` covering both: every row written refers to the same stored PDF.  `file` is the signed PDF. It is checked by its bytes (PDF only, `415` otherwise), scanned for malware before it is stored (`503` `malware-scan-unavailable` when no verdict can be had — the upload is refused, never stored unscanned), and kept privately; it is served only by `getAgreementContractFile`. `reference` is optional when a file is attached and required when none is.  The custom contract stays in force for its documents until an admin records a newer one: a newer published edition does not prompt the organization for them.  The record is append-only: recording a second contract adds entries (and a file of their own) rather than replacing the first, and nothing here can remove one. The action is written to the admin audit log.  Requires `admin.organizations.edit` permission — the same one that edits an organization\'s invoice details. No new permission bit. 
         * @summary Record a contract the organization signed outside Scrivio
         * @param {string} organizationId Organization ID
         * @param {string} signedOn The day the contract was signed. May not lie in the future.
         * @param {AgreementDocument} [document] 
         * @param {string} [reference] Where the signed document is kept outside Scrivio, for example its file name in Drive. Required when no &#x60;file&#x60; is attached; optional when one is. 
         * @param {File} [file] The signed contract, as a PDF of at most 20 MB
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        recordPreExistingContract: async (organizationId: string, signedOn: string, document?: AgreementDocument, reference?: string, file?: File, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('recordPreExistingContract', 'organizationId', organizationId)
            // verify required parameter 'signedOn' is not null or undefined
            assertParamExists('recordPreExistingContract', 'signedOn', signedOn)
            const localVarPath = `/api/admin/organizations/{organizationId}/agreements/pre-existing-contract`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;
            const localVarFormParams = new ((configuration && configuration.formDataCtor) || FormData)();

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


            if (document !== undefined) { 
                localVarFormParams.append('document', document as any);
            }

            if (signedOn !== undefined) { 
                localVarFormParams.append('signedOn', signedOn as any);
            }

            if (reference !== undefined) { 
                localVarFormParams.append('reference', reference as any);
            }

            if (file !== undefined) { 
                localVarFormParams.append('file', file as any);
            }
            localVarHeaderParameter['Content-Type'] = 'multipart/form-data';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = localVarFormParams;

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AdminAgreementsApi - functional programming interface
 */
export const AdminAgreementsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AdminAgreementsApiAxiosParamCreator(configuration)
    return {
        /**
         * Streams the signed contract an admin uploaded with a pre-existing contract (#4837). Served by the backend only — the file has no public or signed URL.  `404` for an acceptance of another organization, for one outside a support agent\'s scope, and for one without a file. Every download is written to the admin audit log.  Requires `admin.organizations.view` permission. 
         * @summary The signed PDF of a contract recorded for an organization
         * @param {string} organizationId Organization ID
         * @param {string} acceptanceId The acceptance whose signed contract to download
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getAgreementContractFile(organizationId: string, acceptanceId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<File>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getAgreementContractFile(organizationId, acceptanceId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminAgreementsApi.getAgreementContractFile']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Per Scrivio agreement, whether the currently published version is still outstanding, and every acceptance the organization has recorded — document, version, who, when, how, and the party details as they were accepted (#4492).  Requires `admin.organizations.view` permission. 
         * @summary What an organization has agreed to with Scrivio
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getOrganizationAgreements(organizationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminOrganizationAgreementsResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getOrganizationAgreements(organizationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminAgreementsApi.getOrganizationAgreements']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Records that the organization signed a custom Overeenkomst, Verwerkersovereenkomst or both outside Scrivio (#4492, #4837). One acceptance is written per document covered — the one named in `document`, or both when `document` is left out — with method `PRE_EXISTING_CONTRACT`, no version (the contract was individually negotiated, so there is no published version to name), the signing date as when it took effect, and the admin as who recorded it. The party details are the organization\'s as they stand today.  **Per document.** A document the contract does not cover keeps whatever it was bound by — its status, its re-acceptance prompt and its confirmation mail are untouched. Recording both at once is one request without `document`, with one `file` covering both: every row written refers to the same stored PDF.  `file` is the signed PDF. It is checked by its bytes (PDF only, `415` otherwise), scanned for malware before it is stored (`503` `malware-scan-unavailable` when no verdict can be had — the upload is refused, never stored unscanned), and kept privately; it is served only by `getAgreementContractFile`. `reference` is optional when a file is attached and required when none is.  The custom contract stays in force for its documents until an admin records a newer one: a newer published edition does not prompt the organization for them.  The record is append-only: recording a second contract adds entries (and a file of their own) rather than replacing the first, and nothing here can remove one. The action is written to the admin audit log.  Requires `admin.organizations.edit` permission — the same one that edits an organization\'s invoice details. No new permission bit. 
         * @summary Record a contract the organization signed outside Scrivio
         * @param {string} organizationId Organization ID
         * @param {string} signedOn The day the contract was signed. May not lie in the future.
         * @param {AgreementDocument} [document] 
         * @param {string} [reference] Where the signed document is kept outside Scrivio, for example its file name in Drive. Required when no &#x60;file&#x60; is attached; optional when one is. 
         * @param {File} [file] The signed contract, as a PDF of at most 20 MB
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async recordPreExistingContract(organizationId: string, signedOn: string, document?: AgreementDocument, reference?: string, file?: File, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminOrganizationAgreementsResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.recordPreExistingContract(organizationId, signedOn, document, reference, file, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminAgreementsApi.recordPreExistingContract']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AdminAgreementsApi - factory interface
 */
export const AdminAgreementsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AdminAgreementsApiFp(configuration)
    return {
        /**
         * Streams the signed contract an admin uploaded with a pre-existing contract (#4837). Served by the backend only — the file has no public or signed URL.  `404` for an acceptance of another organization, for one outside a support agent\'s scope, and for one without a file. Every download is written to the admin audit log.  Requires `admin.organizations.view` permission. 
         * @summary The signed PDF of a contract recorded for an organization
         * @param {string} organizationId Organization ID
         * @param {string} acceptanceId The acceptance whose signed contract to download
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAgreementContractFile(organizationId: string, acceptanceId: string, options?: RawAxiosRequestConfig): AxiosPromise<File> {
            return localVarFp.getAgreementContractFile(organizationId, acceptanceId, options).then((request) => request(axios, basePath));
        },
        /**
         * Per Scrivio agreement, whether the currently published version is still outstanding, and every acceptance the organization has recorded — document, version, who, when, how, and the party details as they were accepted (#4492).  Requires `admin.organizations.view` permission. 
         * @summary What an organization has agreed to with Scrivio
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getOrganizationAgreements(organizationId: string, options?: RawAxiosRequestConfig): AxiosPromise<AdminOrganizationAgreementsResponse> {
            return localVarFp.getOrganizationAgreements(organizationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Records that the organization signed a custom Overeenkomst, Verwerkersovereenkomst or both outside Scrivio (#4492, #4837). One acceptance is written per document covered — the one named in `document`, or both when `document` is left out — with method `PRE_EXISTING_CONTRACT`, no version (the contract was individually negotiated, so there is no published version to name), the signing date as when it took effect, and the admin as who recorded it. The party details are the organization\'s as they stand today.  **Per document.** A document the contract does not cover keeps whatever it was bound by — its status, its re-acceptance prompt and its confirmation mail are untouched. Recording both at once is one request without `document`, with one `file` covering both: every row written refers to the same stored PDF.  `file` is the signed PDF. It is checked by its bytes (PDF only, `415` otherwise), scanned for malware before it is stored (`503` `malware-scan-unavailable` when no verdict can be had — the upload is refused, never stored unscanned), and kept privately; it is served only by `getAgreementContractFile`. `reference` is optional when a file is attached and required when none is.  The custom contract stays in force for its documents until an admin records a newer one: a newer published edition does not prompt the organization for them.  The record is append-only: recording a second contract adds entries (and a file of their own) rather than replacing the first, and nothing here can remove one. The action is written to the admin audit log.  Requires `admin.organizations.edit` permission — the same one that edits an organization\'s invoice details. No new permission bit. 
         * @summary Record a contract the organization signed outside Scrivio
         * @param {string} organizationId Organization ID
         * @param {string} signedOn The day the contract was signed. May not lie in the future.
         * @param {AgreementDocument} [document] 
         * @param {string} [reference] Where the signed document is kept outside Scrivio, for example its file name in Drive. Required when no &#x60;file&#x60; is attached; optional when one is. 
         * @param {File} [file] The signed contract, as a PDF of at most 20 MB
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        recordPreExistingContract(organizationId: string, signedOn: string, document?: AgreementDocument, reference?: string, file?: File, options?: RawAxiosRequestConfig): AxiosPromise<AdminOrganizationAgreementsResponse> {
            return localVarFp.recordPreExistingContract(organizationId, signedOn, document, reference, file, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AdminAgreementsApi - object-oriented interface
 */
export class AdminAgreementsApi extends BaseAPI {
    /**
     * Streams the signed contract an admin uploaded with a pre-existing contract (#4837). Served by the backend only — the file has no public or signed URL.  `404` for an acceptance of another organization, for one outside a support agent\'s scope, and for one without a file. Every download is written to the admin audit log.  Requires `admin.organizations.view` permission. 
     * @summary The signed PDF of a contract recorded for an organization
     * @param {string} organizationId Organization ID
     * @param {string} acceptanceId The acceptance whose signed contract to download
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getAgreementContractFile(organizationId: string, acceptanceId: string, options?: RawAxiosRequestConfig) {
        return AdminAgreementsApiFp(this.configuration).getAgreementContractFile(organizationId, acceptanceId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Per Scrivio agreement, whether the currently published version is still outstanding, and every acceptance the organization has recorded — document, version, who, when, how, and the party details as they were accepted (#4492).  Requires `admin.organizations.view` permission. 
     * @summary What an organization has agreed to with Scrivio
     * @param {string} organizationId Organization ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getOrganizationAgreements(organizationId: string, options?: RawAxiosRequestConfig) {
        return AdminAgreementsApiFp(this.configuration).getOrganizationAgreements(organizationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Records that the organization signed a custom Overeenkomst, Verwerkersovereenkomst or both outside Scrivio (#4492, #4837). One acceptance is written per document covered — the one named in `document`, or both when `document` is left out — with method `PRE_EXISTING_CONTRACT`, no version (the contract was individually negotiated, so there is no published version to name), the signing date as when it took effect, and the admin as who recorded it. The party details are the organization\'s as they stand today.  **Per document.** A document the contract does not cover keeps whatever it was bound by — its status, its re-acceptance prompt and its confirmation mail are untouched. Recording both at once is one request without `document`, with one `file` covering both: every row written refers to the same stored PDF.  `file` is the signed PDF. It is checked by its bytes (PDF only, `415` otherwise), scanned for malware before it is stored (`503` `malware-scan-unavailable` when no verdict can be had — the upload is refused, never stored unscanned), and kept privately; it is served only by `getAgreementContractFile`. `reference` is optional when a file is attached and required when none is.  The custom contract stays in force for its documents until an admin records a newer one: a newer published edition does not prompt the organization for them.  The record is append-only: recording a second contract adds entries (and a file of their own) rather than replacing the first, and nothing here can remove one. The action is written to the admin audit log.  Requires `admin.organizations.edit` permission — the same one that edits an organization\'s invoice details. No new permission bit. 
     * @summary Record a contract the organization signed outside Scrivio
     * @param {string} organizationId Organization ID
     * @param {string} signedOn The day the contract was signed. May not lie in the future.
     * @param {AgreementDocument} [document] 
     * @param {string} [reference] Where the signed document is kept outside Scrivio, for example its file name in Drive. Required when no &#x60;file&#x60; is attached; optional when one is. 
     * @param {File} [file] The signed contract, as a PDF of at most 20 MB
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public recordPreExistingContract(organizationId: string, signedOn: string, document?: AgreementDocument, reference?: string, file?: File, options?: RawAxiosRequestConfig) {
        return AdminAgreementsApiFp(this.configuration).recordPreExistingContract(organizationId, signedOn, document, reference, file, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * AdminAuditApi - axios parameter creator
 */
export const AdminAuditApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Returns a paginated list of admin audit log entries with optional filtering.  **Scoping:** - Platform admins see all audit entries - Support agents see only entries for organizations assigned to their support partner  Requires `admin.audit.view` permission. 
         * @summary List admin audit log entries
         * @param {string} [startDate] Filter from date (inclusive, ISO 8601 date)
         * @param {string} [endDate] Filter to date (inclusive, ISO 8601 date)
         * @param {AdminAuditAction} [action] Filter by action type
         * @param {string} [actorUserId] Filter by actor (admin user or system) who performed the action
         * @param {AdminAuditTargetType} [targetType] Filter by target entity type
         * @param {string} [targetOrganizationId] Filter by target organization ID
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAuditLog: async (startDate?: string, endDate?: string, action?: AdminAuditAction, actorUserId?: string, targetType?: AdminAuditTargetType, targetOrganizationId?: string, page?: number, size?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/admin/audit-log`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (startDate !== undefined) {
                localVarQueryParameter['startDate'] = (startDate as any instanceof Date) ?
                    (startDate as any).toISOString().substring(0,10) :
                    startDate;
            }

            if (endDate !== undefined) {
                localVarQueryParameter['endDate'] = (endDate as any instanceof Date) ?
                    (endDate as any).toISOString().substring(0,10) :
                    endDate;
            }

            if (action !== undefined) {
                localVarQueryParameter['action'] = action;
            }

            if (actorUserId !== undefined) {
                localVarQueryParameter['actorUserId'] = actorUserId;
            }

            if (targetType !== undefined) {
                localVarQueryParameter['targetType'] = targetType;
            }

            if (targetOrganizationId !== undefined) {
                localVarQueryParameter['targetOrganizationId'] = targetOrganizationId;
            }

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AdminAuditApi - functional programming interface
 */
export const AdminAuditApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AdminAuditApiAxiosParamCreator(configuration)
    return {
        /**
         * Returns a paginated list of admin audit log entries with optional filtering.  **Scoping:** - Platform admins see all audit entries - Support agents see only entries for organizations assigned to their support partner  Requires `admin.audit.view` permission. 
         * @summary List admin audit log entries
         * @param {string} [startDate] Filter from date (inclusive, ISO 8601 date)
         * @param {string} [endDate] Filter to date (inclusive, ISO 8601 date)
         * @param {AdminAuditAction} [action] Filter by action type
         * @param {string} [actorUserId] Filter by actor (admin user or system) who performed the action
         * @param {AdminAuditTargetType} [targetType] Filter by target entity type
         * @param {string} [targetOrganizationId] Filter by target organization ID
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listAuditLog(startDate?: string, endDate?: string, action?: AdminAuditAction, actorUserId?: string, targetType?: AdminAuditTargetType, targetOrganizationId?: string, page?: number, size?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminAuditLogListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listAuditLog(startDate, endDate, action, actorUserId, targetType, targetOrganizationId, page, size, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminAuditApi.listAuditLog']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AdminAuditApi - factory interface
 */
export const AdminAuditApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AdminAuditApiFp(configuration)
    return {
        /**
         * Returns a paginated list of admin audit log entries with optional filtering.  **Scoping:** - Platform admins see all audit entries - Support agents see only entries for organizations assigned to their support partner  Requires `admin.audit.view` permission. 
         * @summary List admin audit log entries
         * @param {string} [startDate] Filter from date (inclusive, ISO 8601 date)
         * @param {string} [endDate] Filter to date (inclusive, ISO 8601 date)
         * @param {AdminAuditAction} [action] Filter by action type
         * @param {string} [actorUserId] Filter by actor (admin user or system) who performed the action
         * @param {AdminAuditTargetType} [targetType] Filter by target entity type
         * @param {string} [targetOrganizationId] Filter by target organization ID
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAuditLog(startDate?: string, endDate?: string, action?: AdminAuditAction, actorUserId?: string, targetType?: AdminAuditTargetType, targetOrganizationId?: string, page?: number, size?: number, options?: RawAxiosRequestConfig): AxiosPromise<AdminAuditLogListResponse> {
            return localVarFp.listAuditLog(startDate, endDate, action, actorUserId, targetType, targetOrganizationId, page, size, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AdminAuditApi - object-oriented interface
 */
export class AdminAuditApi extends BaseAPI {
    /**
     * Returns a paginated list of admin audit log entries with optional filtering.  **Scoping:** - Platform admins see all audit entries - Support agents see only entries for organizations assigned to their support partner  Requires `admin.audit.view` permission. 
     * @summary List admin audit log entries
     * @param {string} [startDate] Filter from date (inclusive, ISO 8601 date)
     * @param {string} [endDate] Filter to date (inclusive, ISO 8601 date)
     * @param {AdminAuditAction} [action] Filter by action type
     * @param {string} [actorUserId] Filter by actor (admin user or system) who performed the action
     * @param {AdminAuditTargetType} [targetType] Filter by target entity type
     * @param {string} [targetOrganizationId] Filter by target organization ID
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Page size
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listAuditLog(startDate?: string, endDate?: string, action?: AdminAuditAction, actorUserId?: string, targetType?: AdminAuditTargetType, targetOrganizationId?: string, page?: number, size?: number, options?: RawAxiosRequestConfig) {
        return AdminAuditApiFp(this.configuration).listAuditLog(startDate, endDate, action, actorUserId, targetType, targetOrganizationId, page, size, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * AdminAuthApi - axios parameter creator
 */
export const AdminAuthApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Returns information about the currently authenticated admin user.  This endpoint is used by the admin portal to populate the user profile, show available permissions, and determine organization scoping for support agents.  No permission check required - authentication via admin context is sufficient. 
         * @summary Get current admin user
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCurrentAdmin: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/admin/auth/me`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Starts a new admin session or refreshes an existing one.  This sets the `expiresAt` claim in the user\'s Firebase custom claims to 1 hour from now. The admin portal should call this endpoint after successful authentication and MFA verification to establish a session timeout.  The frontend must refresh the Firebase token after calling this endpoint to pick up the new `expiresAt` claim.  Every other admin endpoint except `GET /api/admin/auth/me` rejects a token whose `expiresAt` has passed or is absent with `401 admin-session-expired`, so this call is what makes a fresh sign-in usable.  No permission check required - authentication via admin context is sufficient. 
         * @summary Start or refresh admin session
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        startSession: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/admin/auth/session`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AdminAuthApi - functional programming interface
 */
export const AdminAuthApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AdminAuthApiAxiosParamCreator(configuration)
    return {
        /**
         * Returns information about the currently authenticated admin user.  This endpoint is used by the admin portal to populate the user profile, show available permissions, and determine organization scoping for support agents.  No permission check required - authentication via admin context is sufficient. 
         * @summary Get current admin user
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getCurrentAdmin(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminCurrentUserDto>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getCurrentAdmin(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminAuthApi.getCurrentAdmin']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Starts a new admin session or refreshes an existing one.  This sets the `expiresAt` claim in the user\'s Firebase custom claims to 1 hour from now. The admin portal should call this endpoint after successful authentication and MFA verification to establish a session timeout.  The frontend must refresh the Firebase token after calling this endpoint to pick up the new `expiresAt` claim.  Every other admin endpoint except `GET /api/admin/auth/me` rejects a token whose `expiresAt` has passed or is absent with `401 admin-session-expired`, so this call is what makes a fresh sign-in usable.  No permission check required - authentication via admin context is sufficient. 
         * @summary Start or refresh admin session
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async startSession(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminSessionResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.startSession(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminAuthApi.startSession']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AdminAuthApi - factory interface
 */
export const AdminAuthApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AdminAuthApiFp(configuration)
    return {
        /**
         * Returns information about the currently authenticated admin user.  This endpoint is used by the admin portal to populate the user profile, show available permissions, and determine organization scoping for support agents.  No permission check required - authentication via admin context is sufficient. 
         * @summary Get current admin user
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCurrentAdmin(options?: RawAxiosRequestConfig): AxiosPromise<AdminCurrentUserDto> {
            return localVarFp.getCurrentAdmin(options).then((request) => request(axios, basePath));
        },
        /**
         * Starts a new admin session or refreshes an existing one.  This sets the `expiresAt` claim in the user\'s Firebase custom claims to 1 hour from now. The admin portal should call this endpoint after successful authentication and MFA verification to establish a session timeout.  The frontend must refresh the Firebase token after calling this endpoint to pick up the new `expiresAt` claim.  Every other admin endpoint except `GET /api/admin/auth/me` rejects a token whose `expiresAt` has passed or is absent with `401 admin-session-expired`, so this call is what makes a fresh sign-in usable.  No permission check required - authentication via admin context is sufficient. 
         * @summary Start or refresh admin session
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        startSession(options?: RawAxiosRequestConfig): AxiosPromise<AdminSessionResponse> {
            return localVarFp.startSession(options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AdminAuthApi - object-oriented interface
 */
export class AdminAuthApi extends BaseAPI {
    /**
     * Returns information about the currently authenticated admin user.  This endpoint is used by the admin portal to populate the user profile, show available permissions, and determine organization scoping for support agents.  No permission check required - authentication via admin context is sufficient. 
     * @summary Get current admin user
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getCurrentAdmin(options?: RawAxiosRequestConfig) {
        return AdminAuthApiFp(this.configuration).getCurrentAdmin(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Starts a new admin session or refreshes an existing one.  This sets the `expiresAt` claim in the user\'s Firebase custom claims to 1 hour from now. The admin portal should call this endpoint after successful authentication and MFA verification to establish a session timeout.  The frontend must refresh the Firebase token after calling this endpoint to pick up the new `expiresAt` claim.  Every other admin endpoint except `GET /api/admin/auth/me` rejects a token whose `expiresAt` has passed or is absent with `401 admin-session-expired`, so this call is what makes a fresh sign-in usable.  No permission check required - authentication via admin context is sufficient. 
     * @summary Start or refresh admin session
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public startSession(options?: RawAxiosRequestConfig) {
        return AdminAuthApiFp(this.configuration).startSession(options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * AdminDashboardApi - axios parameter creator
 */
export const AdminDashboardApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Returns aggregated statistics and recent activity for the admin dashboard.  The organisation and user counts are of **active customer** organisations only: a suspended organisation and one marked `TEST` (#4773) are left out of both.  **Scoping:** - Platform admins see all data - Support agents see only data for organizations assigned to their support partner  Requires `admin.organizations.view` permission. 
         * @summary Get admin dashboard data
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getDashboard: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/admin/dashboard`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AdminDashboardApi - functional programming interface
 */
export const AdminDashboardApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AdminDashboardApiAxiosParamCreator(configuration)
    return {
        /**
         * Returns aggregated statistics and recent activity for the admin dashboard.  The organisation and user counts are of **active customer** organisations only: a suspended organisation and one marked `TEST` (#4773) are left out of both.  **Scoping:** - Platform admins see all data - Support agents see only data for organizations assigned to their support partner  Requires `admin.organizations.view` permission. 
         * @summary Get admin dashboard data
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getDashboard(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminDashboardDto>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getDashboard(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminDashboardApi.getDashboard']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AdminDashboardApi - factory interface
 */
export const AdminDashboardApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AdminDashboardApiFp(configuration)
    return {
        /**
         * Returns aggregated statistics and recent activity for the admin dashboard.  The organisation and user counts are of **active customer** organisations only: a suspended organisation and one marked `TEST` (#4773) are left out of both.  **Scoping:** - Platform admins see all data - Support agents see only data for organizations assigned to their support partner  Requires `admin.organizations.view` permission. 
         * @summary Get admin dashboard data
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getDashboard(options?: RawAxiosRequestConfig): AxiosPromise<AdminDashboardDto> {
            return localVarFp.getDashboard(options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AdminDashboardApi - object-oriented interface
 */
export class AdminDashboardApi extends BaseAPI {
    /**
     * Returns aggregated statistics and recent activity for the admin dashboard.  The organisation and user counts are of **active customer** organisations only: a suspended organisation and one marked `TEST` (#4773) are left out of both.  **Scoping:** - Platform admins see all data - Support agents see only data for organizations assigned to their support partner  Requires `admin.organizations.view` permission. 
     * @summary Get admin dashboard data
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getDashboard(options?: RawAxiosRequestConfig) {
        return AdminDashboardApiFp(this.configuration).getDashboard(options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * AdminHubspotApi - axios parameter creator
 */
export const AdminHubspotApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Which HubSpot company the organization is linked to, when it was last synced and why the last attempt failed — or, while it is not linked, why the link run left it so (#4836). `enabled` says whether the sync is switched on at all; while it is off the link can be read but not changed. Requires `admin.subscriptions.view`: HubSpot records Scrivio\'s commercial relationship with the practice, which is no support partner\'s business. 
         * @summary Get an organization\'s link to HubSpot
         * @param {string} organizationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getOrganizationHubSpotLink: async (organizationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('getOrganizationHubSpotLink', 'organizationId', organizationId)
            const localVarPath = `/api/admin/organizations/{organizationId}/hubspot`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Links the organization to the company `company` names — the company\'s address as HubSpot shows it, or its id — writes the organization id and Scrivio\'s properties onto it and associates the primary contact. The company\'s lifecycle stage is left alone. Replaces any earlier link, and lifts a \"niet gekoppeld\" the link run or an unlink left.  A HubSpot failure after the company was found does not undo the link: it is reported in `lastError` and retried by the nightly sync. Each link is written to the admin audit log. An organization that does not count as a customer — a test organization, or one that is suspended, ended or closed — is refused (422): it is not synced, so a link made now would never be kept current. Requires `admin.subscriptions.manage`. 
         * @summary Link an organization to a HubSpot company by hand
         * @param {string} organizationId 
         * @param {AdminHubSpotLinkRequest} adminHubSpotLinkRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        linkOrganizationToHubSpot: async (organizationId: string, adminHubSpotLinkRequest: AdminHubSpotLinkRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('linkOrganizationToHubSpot', 'organizationId', organizationId)
            // verify required parameter 'adminHubSpotLinkRequest' is not null or undefined
            assertParamExists('linkOrganizationToHubSpot', 'adminHubSpotLinkRequest', adminHubSpotLinkRequest)
            const localVarPath = `/api/admin/organizations/{organizationId}/hubspot`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(adminHubSpotLinkRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Every active customer organization with no HubSpot company, by name, with the reason the link run or an admin left it so where there is one — what the organizations list\'s \"HubSpot: niet gekoppeld\" filter shows. Read from the database alone, so it answers with the sync switched off too. Requires `admin.subscriptions.view`. 
         * @summary List the organizations not linked to a HubSpot company
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listHubSpotUnlinkedOrganizations: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/admin/hubspot/unlinked-organizations`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Matches every customer organization to a HubSpot company on its primary contact\'s email address (#4836) — first match wins:  1. a company is already linked, or already carries the organization id: `LINKED`; 2. the contact has exactly one company, not linked to another organization:    `LINK_TO_EXISTING`; 3. the contact has no company: `CREATE`; 4. anything else: `NOT_LINKED`, left for an admin to link by hand. A company is never    matched on its name.  With `dryRun` it reports what it would do and writes nothing, to HubSpot or to the database. Without, it does it. A lifecycle stage is never moved backwards, and no contact is ever created. Test organizations are not listed. Answered when the whole run is done, paced against HubSpot\'s rate limit; a 429 ends it early with `complete: false`, and running it again carries on. Requires `admin.subscriptions.manage`. 
         * @summary Link the existing organizations to their HubSpot companies
         * @param {AdminHubSpotLinkRunRequest} adminHubSpotLinkRunRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        runHubSpotLinkRun: async (adminHubSpotLinkRunRequest: AdminHubSpotLinkRunRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'adminHubSpotLinkRunRequest' is not null or undefined
            assertParamExists('runHubSpotLinkRun', 'adminHubSpotLinkRunRequest', adminHubSpotLinkRunRequest)
            const localVarPath = `/api/admin/hubspot/link-runs`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(adminHubSpotLinkRunRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Empties the organization id on the HubSpot company and forgets the link here. The organization is then \"niet gekoppeld\" until it is linked by hand again — neither the nightly sync nor the link run links it on its own. Written to the admin audit log. Requires `admin.subscriptions.manage`. 
         * @summary Unlink an organization from its HubSpot company
         * @param {string} organizationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        unlinkOrganizationFromHubSpot: async (organizationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('unlinkOrganizationFromHubSpot', 'organizationId', organizationId)
            const localVarPath = `/api/admin/organizations/{organizationId}/hubspot`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AdminHubspotApi - functional programming interface
 */
export const AdminHubspotApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AdminHubspotApiAxiosParamCreator(configuration)
    return {
        /**
         * Which HubSpot company the organization is linked to, when it was last synced and why the last attempt failed — or, while it is not linked, why the link run left it so (#4836). `enabled` says whether the sync is switched on at all; while it is off the link can be read but not changed. Requires `admin.subscriptions.view`: HubSpot records Scrivio\'s commercial relationship with the practice, which is no support partner\'s business. 
         * @summary Get an organization\'s link to HubSpot
         * @param {string} organizationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getOrganizationHubSpotLink(organizationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminHubSpotLinkResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getOrganizationHubSpotLink(organizationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminHubspotApi.getOrganizationHubSpotLink']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Links the organization to the company `company` names — the company\'s address as HubSpot shows it, or its id — writes the organization id and Scrivio\'s properties onto it and associates the primary contact. The company\'s lifecycle stage is left alone. Replaces any earlier link, and lifts a \"niet gekoppeld\" the link run or an unlink left.  A HubSpot failure after the company was found does not undo the link: it is reported in `lastError` and retried by the nightly sync. Each link is written to the admin audit log. An organization that does not count as a customer — a test organization, or one that is suspended, ended or closed — is refused (422): it is not synced, so a link made now would never be kept current. Requires `admin.subscriptions.manage`. 
         * @summary Link an organization to a HubSpot company by hand
         * @param {string} organizationId 
         * @param {AdminHubSpotLinkRequest} adminHubSpotLinkRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async linkOrganizationToHubSpot(organizationId: string, adminHubSpotLinkRequest: AdminHubSpotLinkRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminHubSpotLinkResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.linkOrganizationToHubSpot(organizationId, adminHubSpotLinkRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminHubspotApi.linkOrganizationToHubSpot']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Every active customer organization with no HubSpot company, by name, with the reason the link run or an admin left it so where there is one — what the organizations list\'s \"HubSpot: niet gekoppeld\" filter shows. Read from the database alone, so it answers with the sync switched off too. Requires `admin.subscriptions.view`. 
         * @summary List the organizations not linked to a HubSpot company
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listHubSpotUnlinkedOrganizations(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<AdminHubSpotUnlinkedOrganization>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listHubSpotUnlinkedOrganizations(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminHubspotApi.listHubSpotUnlinkedOrganizations']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Matches every customer organization to a HubSpot company on its primary contact\'s email address (#4836) — first match wins:  1. a company is already linked, or already carries the organization id: `LINKED`; 2. the contact has exactly one company, not linked to another organization:    `LINK_TO_EXISTING`; 3. the contact has no company: `CREATE`; 4. anything else: `NOT_LINKED`, left for an admin to link by hand. A company is never    matched on its name.  With `dryRun` it reports what it would do and writes nothing, to HubSpot or to the database. Without, it does it. A lifecycle stage is never moved backwards, and no contact is ever created. Test organizations are not listed. Answered when the whole run is done, paced against HubSpot\'s rate limit; a 429 ends it early with `complete: false`, and running it again carries on. Requires `admin.subscriptions.manage`. 
         * @summary Link the existing organizations to their HubSpot companies
         * @param {AdminHubSpotLinkRunRequest} adminHubSpotLinkRunRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async runHubSpotLinkRun(adminHubSpotLinkRunRequest: AdminHubSpotLinkRunRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminHubSpotLinkRunResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.runHubSpotLinkRun(adminHubSpotLinkRunRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminHubspotApi.runHubSpotLinkRun']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Empties the organization id on the HubSpot company and forgets the link here. The organization is then \"niet gekoppeld\" until it is linked by hand again — neither the nightly sync nor the link run links it on its own. Written to the admin audit log. Requires `admin.subscriptions.manage`. 
         * @summary Unlink an organization from its HubSpot company
         * @param {string} organizationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async unlinkOrganizationFromHubSpot(organizationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminHubSpotLinkResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.unlinkOrganizationFromHubSpot(organizationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminHubspotApi.unlinkOrganizationFromHubSpot']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AdminHubspotApi - factory interface
 */
export const AdminHubspotApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AdminHubspotApiFp(configuration)
    return {
        /**
         * Which HubSpot company the organization is linked to, when it was last synced and why the last attempt failed — or, while it is not linked, why the link run left it so (#4836). `enabled` says whether the sync is switched on at all; while it is off the link can be read but not changed. Requires `admin.subscriptions.view`: HubSpot records Scrivio\'s commercial relationship with the practice, which is no support partner\'s business. 
         * @summary Get an organization\'s link to HubSpot
         * @param {string} organizationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getOrganizationHubSpotLink(organizationId: string, options?: RawAxiosRequestConfig): AxiosPromise<AdminHubSpotLinkResponse> {
            return localVarFp.getOrganizationHubSpotLink(organizationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Links the organization to the company `company` names — the company\'s address as HubSpot shows it, or its id — writes the organization id and Scrivio\'s properties onto it and associates the primary contact. The company\'s lifecycle stage is left alone. Replaces any earlier link, and lifts a \"niet gekoppeld\" the link run or an unlink left.  A HubSpot failure after the company was found does not undo the link: it is reported in `lastError` and retried by the nightly sync. Each link is written to the admin audit log. An organization that does not count as a customer — a test organization, or one that is suspended, ended or closed — is refused (422): it is not synced, so a link made now would never be kept current. Requires `admin.subscriptions.manage`. 
         * @summary Link an organization to a HubSpot company by hand
         * @param {string} organizationId 
         * @param {AdminHubSpotLinkRequest} adminHubSpotLinkRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        linkOrganizationToHubSpot(organizationId: string, adminHubSpotLinkRequest: AdminHubSpotLinkRequest, options?: RawAxiosRequestConfig): AxiosPromise<AdminHubSpotLinkResponse> {
            return localVarFp.linkOrganizationToHubSpot(organizationId, adminHubSpotLinkRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Every active customer organization with no HubSpot company, by name, with the reason the link run or an admin left it so where there is one — what the organizations list\'s \"HubSpot: niet gekoppeld\" filter shows. Read from the database alone, so it answers with the sync switched off too. Requires `admin.subscriptions.view`. 
         * @summary List the organizations not linked to a HubSpot company
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listHubSpotUnlinkedOrganizations(options?: RawAxiosRequestConfig): AxiosPromise<Array<AdminHubSpotUnlinkedOrganization>> {
            return localVarFp.listHubSpotUnlinkedOrganizations(options).then((request) => request(axios, basePath));
        },
        /**
         * Matches every customer organization to a HubSpot company on its primary contact\'s email address (#4836) — first match wins:  1. a company is already linked, or already carries the organization id: `LINKED`; 2. the contact has exactly one company, not linked to another organization:    `LINK_TO_EXISTING`; 3. the contact has no company: `CREATE`; 4. anything else: `NOT_LINKED`, left for an admin to link by hand. A company is never    matched on its name.  With `dryRun` it reports what it would do and writes nothing, to HubSpot or to the database. Without, it does it. A lifecycle stage is never moved backwards, and no contact is ever created. Test organizations are not listed. Answered when the whole run is done, paced against HubSpot\'s rate limit; a 429 ends it early with `complete: false`, and running it again carries on. Requires `admin.subscriptions.manage`. 
         * @summary Link the existing organizations to their HubSpot companies
         * @param {AdminHubSpotLinkRunRequest} adminHubSpotLinkRunRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        runHubSpotLinkRun(adminHubSpotLinkRunRequest: AdminHubSpotLinkRunRequest, options?: RawAxiosRequestConfig): AxiosPromise<AdminHubSpotLinkRunResponse> {
            return localVarFp.runHubSpotLinkRun(adminHubSpotLinkRunRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Empties the organization id on the HubSpot company and forgets the link here. The organization is then \"niet gekoppeld\" until it is linked by hand again — neither the nightly sync nor the link run links it on its own. Written to the admin audit log. Requires `admin.subscriptions.manage`. 
         * @summary Unlink an organization from its HubSpot company
         * @param {string} organizationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        unlinkOrganizationFromHubSpot(organizationId: string, options?: RawAxiosRequestConfig): AxiosPromise<AdminHubSpotLinkResponse> {
            return localVarFp.unlinkOrganizationFromHubSpot(organizationId, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AdminHubspotApi - object-oriented interface
 */
export class AdminHubspotApi extends BaseAPI {
    /**
     * Which HubSpot company the organization is linked to, when it was last synced and why the last attempt failed — or, while it is not linked, why the link run left it so (#4836). `enabled` says whether the sync is switched on at all; while it is off the link can be read but not changed. Requires `admin.subscriptions.view`: HubSpot records Scrivio\'s commercial relationship with the practice, which is no support partner\'s business. 
     * @summary Get an organization\'s link to HubSpot
     * @param {string} organizationId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getOrganizationHubSpotLink(organizationId: string, options?: RawAxiosRequestConfig) {
        return AdminHubspotApiFp(this.configuration).getOrganizationHubSpotLink(organizationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Links the organization to the company `company` names — the company\'s address as HubSpot shows it, or its id — writes the organization id and Scrivio\'s properties onto it and associates the primary contact. The company\'s lifecycle stage is left alone. Replaces any earlier link, and lifts a \"niet gekoppeld\" the link run or an unlink left.  A HubSpot failure after the company was found does not undo the link: it is reported in `lastError` and retried by the nightly sync. Each link is written to the admin audit log. An organization that does not count as a customer — a test organization, or one that is suspended, ended or closed — is refused (422): it is not synced, so a link made now would never be kept current. Requires `admin.subscriptions.manage`. 
     * @summary Link an organization to a HubSpot company by hand
     * @param {string} organizationId 
     * @param {AdminHubSpotLinkRequest} adminHubSpotLinkRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public linkOrganizationToHubSpot(organizationId: string, adminHubSpotLinkRequest: AdminHubSpotLinkRequest, options?: RawAxiosRequestConfig) {
        return AdminHubspotApiFp(this.configuration).linkOrganizationToHubSpot(organizationId, adminHubSpotLinkRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Every active customer organization with no HubSpot company, by name, with the reason the link run or an admin left it so where there is one — what the organizations list\'s \"HubSpot: niet gekoppeld\" filter shows. Read from the database alone, so it answers with the sync switched off too. Requires `admin.subscriptions.view`. 
     * @summary List the organizations not linked to a HubSpot company
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listHubSpotUnlinkedOrganizations(options?: RawAxiosRequestConfig) {
        return AdminHubspotApiFp(this.configuration).listHubSpotUnlinkedOrganizations(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Matches every customer organization to a HubSpot company on its primary contact\'s email address (#4836) — first match wins:  1. a company is already linked, or already carries the organization id: `LINKED`; 2. the contact has exactly one company, not linked to another organization:    `LINK_TO_EXISTING`; 3. the contact has no company: `CREATE`; 4. anything else: `NOT_LINKED`, left for an admin to link by hand. A company is never    matched on its name.  With `dryRun` it reports what it would do and writes nothing, to HubSpot or to the database. Without, it does it. A lifecycle stage is never moved backwards, and no contact is ever created. Test organizations are not listed. Answered when the whole run is done, paced against HubSpot\'s rate limit; a 429 ends it early with `complete: false`, and running it again carries on. Requires `admin.subscriptions.manage`. 
     * @summary Link the existing organizations to their HubSpot companies
     * @param {AdminHubSpotLinkRunRequest} adminHubSpotLinkRunRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public runHubSpotLinkRun(adminHubSpotLinkRunRequest: AdminHubSpotLinkRunRequest, options?: RawAxiosRequestConfig) {
        return AdminHubspotApiFp(this.configuration).runHubSpotLinkRun(adminHubSpotLinkRunRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Empties the organization id on the HubSpot company and forgets the link here. The organization is then \"niet gekoppeld\" until it is linked by hand again — neither the nightly sync nor the link run links it on its own. Written to the admin audit log. Requires `admin.subscriptions.manage`. 
     * @summary Unlink an organization from its HubSpot company
     * @param {string} organizationId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public unlinkOrganizationFromHubSpot(organizationId: string, options?: RawAxiosRequestConfig) {
        return AdminHubspotApiFp(this.configuration).unlinkOrganizationFromHubSpot(organizationId, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * AdminImportsApi - axios parameter creator
 */
export const AdminImportsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Uploads the filled-in ZPM GGZ import workbook (`.xlsx`) and writes its records to the organization in the path — not to the caller. Platform operators use this during customer onboarding to commit a tenant\'s dump; practitioner resolution runs against the target organization\'s users.  The workbook is re-parsed and re-validated with the same rules as the validate endpoint; the commit is refused with a `422` problem detail when the workbook still contains any ERROR finding. The whole write is all-or-nothing: if any row fails, nothing is persisted.  **Scoping:** - Platform admins can commit against any organization - Support agents can only commit against organizations assigned to their support partner  Requires the `admin.imports.zpm.commit` permission. 
         * @summary Commit a ZPM GGZ import workbook against a specific organization
         * @param {string} organizationId The organization to commit the workbook to
         * @param {string} authorUserId The organization user the imported records are attributed to (&#x60;created_by&#x60; / audit). Must be a member of the target organization holding the &#x60;imports.zpm.commit&#x60; permission (see the eligible-authors endpoint). A platform admin driving the import is not an app user, so their id cannot be used. 
         * @param {File} file The filled-in ZPM GGZ import workbook (&#x60;.xlsx&#x60;)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        adminCommitZpmImport: async (organizationId: string, authorUserId: string, file: File, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('adminCommitZpmImport', 'organizationId', organizationId)
            // verify required parameter 'authorUserId' is not null or undefined
            assertParamExists('adminCommitZpmImport', 'authorUserId', authorUserId)
            // verify required parameter 'file' is not null or undefined
            assertParamExists('adminCommitZpmImport', 'file', file)
            const localVarPath = `/api/admin/organizations/{organizationId}/imports/zpm/commit`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;
            const localVarFormParams = new ((configuration && configuration.formDataCtor) || FormData)();

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


            if (authorUserId !== undefined) { 
                localVarFormParams.append('authorUserId', authorUserId as any);
            }

            if (file !== undefined) { 
                localVarFormParams.append('file', file as any);
            }
            localVarHeaderParameter['Content-Type'] = 'multipart/form-data';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = localVarFormParams;

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the users of the target organization who hold the `imports.zpm.commit` permission — i.e. who may be selected as the `authorUserId` when committing a ZPM import (#3048). The admin portal populates the \"Aangemaakt door\" dropdown from this list.  Requires the `admin.imports.zpm.commit` permission. 
         * @summary List the users eligible to be the author of a ZPM import
         * @param {string} organizationId The organization whose eligible import authors to list
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        adminListZpmImportEligibleAuthors: async (organizationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('adminListZpmImportEligibleAuthors', 'organizationId', organizationId)
            const localVarPath = `/api/admin/organizations/{organizationId}/imports/zpm/eligible-authors`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Uploads the filled-in ZPM GGZ import workbook (`.xlsx`) and returns a validation report, scoped to the organization in the path — not to the caller. Platform operators use this during customer onboarding to validate a tenant\'s dump; practitioner resolution runs against the target organization\'s users.  The workbook is parsed and validated in-process; no data is persisted and no business table is written to.  **Scoping:** - Platform admins can validate against any organization - Support agents can only validate against organizations assigned to their support partner  Requires the `admin.imports.zpm` permission. 
         * @summary Validate a ZPM GGZ import workbook against a specific organization
         * @param {string} organizationId The organization to validate the workbook against
         * @param {File} file The filled-in ZPM GGZ import workbook (&#x60;.xlsx&#x60;)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        adminValidateZpmImport: async (organizationId: string, file: File, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('adminValidateZpmImport', 'organizationId', organizationId)
            // verify required parameter 'file' is not null or undefined
            assertParamExists('adminValidateZpmImport', 'file', file)
            const localVarPath = `/api/admin/organizations/{organizationId}/imports/zpm/validate`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;
            const localVarFormParams = new ((configuration && configuration.formDataCtor) || FormData)();

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


            if (file !== undefined) { 
                localVarFormParams.append('file', file as any);
            }
            localVarHeaderParameter['Content-Type'] = 'multipart/form-data';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = localVarFormParams;

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AdminImportsApi - functional programming interface
 */
export const AdminImportsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AdminImportsApiAxiosParamCreator(configuration)
    return {
        /**
         * Uploads the filled-in ZPM GGZ import workbook (`.xlsx`) and writes its records to the organization in the path — not to the caller. Platform operators use this during customer onboarding to commit a tenant\'s dump; practitioner resolution runs against the target organization\'s users.  The workbook is re-parsed and re-validated with the same rules as the validate endpoint; the commit is refused with a `422` problem detail when the workbook still contains any ERROR finding. The whole write is all-or-nothing: if any row fails, nothing is persisted.  **Scoping:** - Platform admins can commit against any organization - Support agents can only commit against organizations assigned to their support partner  Requires the `admin.imports.zpm.commit` permission. 
         * @summary Commit a ZPM GGZ import workbook against a specific organization
         * @param {string} organizationId The organization to commit the workbook to
         * @param {string} authorUserId The organization user the imported records are attributed to (&#x60;created_by&#x60; / audit). Must be a member of the target organization holding the &#x60;imports.zpm.commit&#x60; permission (see the eligible-authors endpoint). A platform admin driving the import is not an app user, so their id cannot be used. 
         * @param {File} file The filled-in ZPM GGZ import workbook (&#x60;.xlsx&#x60;)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async adminCommitZpmImport(organizationId: string, authorUserId: string, file: File, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ZpmImportResult>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.adminCommitZpmImport(organizationId, authorUserId, file, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminImportsApi.adminCommitZpmImport']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the users of the target organization who hold the `imports.zpm.commit` permission — i.e. who may be selected as the `authorUserId` when committing a ZPM import (#3048). The admin portal populates the \"Aangemaakt door\" dropdown from this list.  Requires the `admin.imports.zpm.commit` permission. 
         * @summary List the users eligible to be the author of a ZPM import
         * @param {string} organizationId The organization whose eligible import authors to list
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async adminListZpmImportEligibleAuthors(organizationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ZpmImportAuthorList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.adminListZpmImportEligibleAuthors(organizationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminImportsApi.adminListZpmImportEligibleAuthors']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Uploads the filled-in ZPM GGZ import workbook (`.xlsx`) and returns a validation report, scoped to the organization in the path — not to the caller. Platform operators use this during customer onboarding to validate a tenant\'s dump; practitioner resolution runs against the target organization\'s users.  The workbook is parsed and validated in-process; no data is persisted and no business table is written to.  **Scoping:** - Platform admins can validate against any organization - Support agents can only validate against organizations assigned to their support partner  Requires the `admin.imports.zpm` permission. 
         * @summary Validate a ZPM GGZ import workbook against a specific organization
         * @param {string} organizationId The organization to validate the workbook against
         * @param {File} file The filled-in ZPM GGZ import workbook (&#x60;.xlsx&#x60;)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async adminValidateZpmImport(organizationId: string, file: File, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ZpmValidationReport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.adminValidateZpmImport(organizationId, file, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminImportsApi.adminValidateZpmImport']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AdminImportsApi - factory interface
 */
export const AdminImportsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AdminImportsApiFp(configuration)
    return {
        /**
         * Uploads the filled-in ZPM GGZ import workbook (`.xlsx`) and writes its records to the organization in the path — not to the caller. Platform operators use this during customer onboarding to commit a tenant\'s dump; practitioner resolution runs against the target organization\'s users.  The workbook is re-parsed and re-validated with the same rules as the validate endpoint; the commit is refused with a `422` problem detail when the workbook still contains any ERROR finding. The whole write is all-or-nothing: if any row fails, nothing is persisted.  **Scoping:** - Platform admins can commit against any organization - Support agents can only commit against organizations assigned to their support partner  Requires the `admin.imports.zpm.commit` permission. 
         * @summary Commit a ZPM GGZ import workbook against a specific organization
         * @param {string} organizationId The organization to commit the workbook to
         * @param {string} authorUserId The organization user the imported records are attributed to (&#x60;created_by&#x60; / audit). Must be a member of the target organization holding the &#x60;imports.zpm.commit&#x60; permission (see the eligible-authors endpoint). A platform admin driving the import is not an app user, so their id cannot be used. 
         * @param {File} file The filled-in ZPM GGZ import workbook (&#x60;.xlsx&#x60;)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        adminCommitZpmImport(organizationId: string, authorUserId: string, file: File, options?: RawAxiosRequestConfig): AxiosPromise<ZpmImportResult> {
            return localVarFp.adminCommitZpmImport(organizationId, authorUserId, file, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the users of the target organization who hold the `imports.zpm.commit` permission — i.e. who may be selected as the `authorUserId` when committing a ZPM import (#3048). The admin portal populates the \"Aangemaakt door\" dropdown from this list.  Requires the `admin.imports.zpm.commit` permission. 
         * @summary List the users eligible to be the author of a ZPM import
         * @param {string} organizationId The organization whose eligible import authors to list
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        adminListZpmImportEligibleAuthors(organizationId: string, options?: RawAxiosRequestConfig): AxiosPromise<ZpmImportAuthorList> {
            return localVarFp.adminListZpmImportEligibleAuthors(organizationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Uploads the filled-in ZPM GGZ import workbook (`.xlsx`) and returns a validation report, scoped to the organization in the path — not to the caller. Platform operators use this during customer onboarding to validate a tenant\'s dump; practitioner resolution runs against the target organization\'s users.  The workbook is parsed and validated in-process; no data is persisted and no business table is written to.  **Scoping:** - Platform admins can validate against any organization - Support agents can only validate against organizations assigned to their support partner  Requires the `admin.imports.zpm` permission. 
         * @summary Validate a ZPM GGZ import workbook against a specific organization
         * @param {string} organizationId The organization to validate the workbook against
         * @param {File} file The filled-in ZPM GGZ import workbook (&#x60;.xlsx&#x60;)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        adminValidateZpmImport(organizationId: string, file: File, options?: RawAxiosRequestConfig): AxiosPromise<ZpmValidationReport> {
            return localVarFp.adminValidateZpmImport(organizationId, file, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AdminImportsApi - object-oriented interface
 */
export class AdminImportsApi extends BaseAPI {
    /**
     * Uploads the filled-in ZPM GGZ import workbook (`.xlsx`) and writes its records to the organization in the path — not to the caller. Platform operators use this during customer onboarding to commit a tenant\'s dump; practitioner resolution runs against the target organization\'s users.  The workbook is re-parsed and re-validated with the same rules as the validate endpoint; the commit is refused with a `422` problem detail when the workbook still contains any ERROR finding. The whole write is all-or-nothing: if any row fails, nothing is persisted.  **Scoping:** - Platform admins can commit against any organization - Support agents can only commit against organizations assigned to their support partner  Requires the `admin.imports.zpm.commit` permission. 
     * @summary Commit a ZPM GGZ import workbook against a specific organization
     * @param {string} organizationId The organization to commit the workbook to
     * @param {string} authorUserId The organization user the imported records are attributed to (&#x60;created_by&#x60; / audit). Must be a member of the target organization holding the &#x60;imports.zpm.commit&#x60; permission (see the eligible-authors endpoint). A platform admin driving the import is not an app user, so their id cannot be used. 
     * @param {File} file The filled-in ZPM GGZ import workbook (&#x60;.xlsx&#x60;)
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public adminCommitZpmImport(organizationId: string, authorUserId: string, file: File, options?: RawAxiosRequestConfig) {
        return AdminImportsApiFp(this.configuration).adminCommitZpmImport(organizationId, authorUserId, file, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the users of the target organization who hold the `imports.zpm.commit` permission — i.e. who may be selected as the `authorUserId` when committing a ZPM import (#3048). The admin portal populates the \"Aangemaakt door\" dropdown from this list.  Requires the `admin.imports.zpm.commit` permission. 
     * @summary List the users eligible to be the author of a ZPM import
     * @param {string} organizationId The organization whose eligible import authors to list
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public adminListZpmImportEligibleAuthors(organizationId: string, options?: RawAxiosRequestConfig) {
        return AdminImportsApiFp(this.configuration).adminListZpmImportEligibleAuthors(organizationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Uploads the filled-in ZPM GGZ import workbook (`.xlsx`) and returns a validation report, scoped to the organization in the path — not to the caller. Platform operators use this during customer onboarding to validate a tenant\'s dump; practitioner resolution runs against the target organization\'s users.  The workbook is parsed and validated in-process; no data is persisted and no business table is written to.  **Scoping:** - Platform admins can validate against any organization - Support agents can only validate against organizations assigned to their support partner  Requires the `admin.imports.zpm` permission. 
     * @summary Validate a ZPM GGZ import workbook against a specific organization
     * @param {string} organizationId The organization to validate the workbook against
     * @param {File} file The filled-in ZPM GGZ import workbook (&#x60;.xlsx&#x60;)
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public adminValidateZpmImport(organizationId: string, file: File, options?: RawAxiosRequestConfig) {
        return AdminImportsApiFp(this.configuration).adminValidateZpmImport(organizationId, file, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * AdminInsurerReturnsApi - axios parameter creator
 */
export const AdminInsurerReturnsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Fills in what each declared batch remembers about the regels it declared and has since released for a redeclaration (#4624), and reports what cannot be filled in.  The migration recorded a declaration for every regel still on a batch and for every released regel that still carried its old `Referentienummer`. A regel that was released *and batched again* before declarations were recorded carries only its new references, so for a batch with fewer declarations than it declared regels this reads the GDS801 message it was sent with and writes the missing declarations from it — matched to a regel on cliënt, date, prestatiecode, uitvoerder and begintijd, with the message\'s references and amount. A prestatie no regel matches that way is tried again without the prestatiecode, among redeclared regels only (declared on another batch, batched again, or carrying a herdeclaratiecode), and such a match is reported (`looseMatches`) with the sent and the current code. A prestatie that matches no regel or several is reported (`unmatchedPrestaties`) and nothing is written for it. A released regel lost its verdict on the way out, and this then reads it back from the batch\'s newest applied retour — through the import\'s own reading of a retour, from its stored `original_xml`.  Moves no batch status and no regel — it writes only the batches\' own history. `DRY_RUN` runs the same code and rolls it back; `APPLY` writes. A second `APPLY` writes nothing new. One transaction per batch. Requires the `admin.insurer-returns.reapply` permission. 
         * @summary Complete the declaration history of an organization\'s batches
         * @param {string} organizationId The organization whose batches to complete
         * @param {Gds802ReapplyRequest} gds802ReapplyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        adminBackfillDeclarationHistory: async (organizationId: string, gds802ReapplyRequest: Gds802ReapplyRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('adminBackfillDeclarationHistory', 'organizationId', organizationId)
            // verify required parameter 'gds802ReapplyRequest' is not null or undefined
            assertParamExists('adminBackfillDeclarationHistory', 'gds802ReapplyRequest', gds802ReapplyRequest)
            const localVarPath = `/api/admin/organizations/{organizationId}/insurer-returns/declaration-history`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(gds802ReapplyRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Runs every GDS802 retourbericht this organization has stored back through today\'s import rules, reading each one from its own stored `original_xml`. A change to what a retour *means* repairs nothing already imported, so this is how batches answered before such a change come to carry today\'s reading of the insurer\'s answer — #4421\'s whole-invoice verdicts (#4511), and #4626\'s regels a retour did not name but whose totals settle them. A retour today\'s rules already judged correctly re-applies to the same verdict.  **`DRY_RUN` first.** It works out what would change with exactly the same code the write uses and then rolls the transaction back, so the report is what the write will do rather than a prediction of it. Nothing is written. `APPLY` is a second, explicit call: this moves billing statuses on data the practice has been looking at.  **Idempotent.** A second `APPLY` re-derives the same verdict from the same bytes. A batch whose declaratieregels were released for a redeclaratie (#4427) is reported as `SKIPPED`, and a regel the practice has acted on since — credited, released, excluded — is never overwritten.  One transaction per batch, so one malformed stored retour costs that batch and nothing else. Requires the `admin.insurer-returns.reapply` permission. 
         * @summary Re-apply the retourberichten an organization already imported
         * @param {string} organizationId The organization whose stored retourberichten to re-apply
         * @param {Gds802ReapplyRequest} gds802ReapplyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        adminReapplyInsurerReturns: async (organizationId: string, gds802ReapplyRequest: Gds802ReapplyRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('adminReapplyInsurerReturns', 'organizationId', organizationId)
            // verify required parameter 'gds802ReapplyRequest' is not null or undefined
            assertParamExists('adminReapplyInsurerReturns', 'gds802ReapplyRequest', gds802ReapplyRequest)
            const localVarPath = `/api/admin/organizations/{organizationId}/insurer-returns/reapply`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(gds802ReapplyRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Brings every declared batch\'s verzekeraarfacturen into line with what the batch declared (#4730). Until #4730 a verzekeraarfactuur was built from the regels still on its batch, and a release for redeclaration took regels off one that existed — so a partially approved or redeclared batch\'s invoice came out at roughly what the insurer paid, and a batch whose every regel was released had none.  A batch is regenerated when its stored invoices do not bill exactly its declarations, or when it has none but the boekhouding was sent one. The factuurnummer, factuurdatum and the per-cliënt split stay what they were. A batch whose declaration history is incomplete is reported as `REFUSED`, with the reason, and never regenerated short.  A regenerated invoice whose replaced version the boekhouding already booked is left unsent and marked as a correction: the practice\'s next \"Naar boekhouding\" for that batch mails it once, as a correction under the same number, and skips the invoices that were already right. `needsResend` lists those batches.  `DRY_RUN` runs the same code and rolls it back; `APPLY` writes. A second `APPLY` finds nothing left to regenerate. One transaction per batch. Requires the `admin.insurer-returns.reapply` permission. 
         * @summary Regenerate the verzekeraarfacturen that do not bill what their batch declared
         * @param {string} organizationId The organization whose verzekeraarfacturen to regenerate
         * @param {Gds802ReapplyRequest} gds802ReapplyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        adminRepairVerkoopfacturen: async (organizationId: string, gds802ReapplyRequest: Gds802ReapplyRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('adminRepairVerkoopfacturen', 'organizationId', organizationId)
            // verify required parameter 'gds802ReapplyRequest' is not null or undefined
            assertParamExists('adminRepairVerkoopfacturen', 'gds802ReapplyRequest', gds802ReapplyRequest)
            const localVarPath = `/api/admin/organizations/{organizationId}/insurer-returns/verkoopfacturen`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(gds802ReapplyRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AdminInsurerReturnsApi - functional programming interface
 */
export const AdminInsurerReturnsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AdminInsurerReturnsApiAxiosParamCreator(configuration)
    return {
        /**
         * Fills in what each declared batch remembers about the regels it declared and has since released for a redeclaration (#4624), and reports what cannot be filled in.  The migration recorded a declaration for every regel still on a batch and for every released regel that still carried its old `Referentienummer`. A regel that was released *and batched again* before declarations were recorded carries only its new references, so for a batch with fewer declarations than it declared regels this reads the GDS801 message it was sent with and writes the missing declarations from it — matched to a regel on cliënt, date, prestatiecode, uitvoerder and begintijd, with the message\'s references and amount. A prestatie no regel matches that way is tried again without the prestatiecode, among redeclared regels only (declared on another batch, batched again, or carrying a herdeclaratiecode), and such a match is reported (`looseMatches`) with the sent and the current code. A prestatie that matches no regel or several is reported (`unmatchedPrestaties`) and nothing is written for it. A released regel lost its verdict on the way out, and this then reads it back from the batch\'s newest applied retour — through the import\'s own reading of a retour, from its stored `original_xml`.  Moves no batch status and no regel — it writes only the batches\' own history. `DRY_RUN` runs the same code and rolls it back; `APPLY` writes. A second `APPLY` writes nothing new. One transaction per batch. Requires the `admin.insurer-returns.reapply` permission. 
         * @summary Complete the declaration history of an organization\'s batches
         * @param {string} organizationId The organization whose batches to complete
         * @param {Gds802ReapplyRequest} gds802ReapplyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async adminBackfillDeclarationHistory(organizationId: string, gds802ReapplyRequest: Gds802ReapplyRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DeclarationHistoryReport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.adminBackfillDeclarationHistory(organizationId, gds802ReapplyRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminInsurerReturnsApi.adminBackfillDeclarationHistory']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Runs every GDS802 retourbericht this organization has stored back through today\'s import rules, reading each one from its own stored `original_xml`. A change to what a retour *means* repairs nothing already imported, so this is how batches answered before such a change come to carry today\'s reading of the insurer\'s answer — #4421\'s whole-invoice verdicts (#4511), and #4626\'s regels a retour did not name but whose totals settle them. A retour today\'s rules already judged correctly re-applies to the same verdict.  **`DRY_RUN` first.** It works out what would change with exactly the same code the write uses and then rolls the transaction back, so the report is what the write will do rather than a prediction of it. Nothing is written. `APPLY` is a second, explicit call: this moves billing statuses on data the practice has been looking at.  **Idempotent.** A second `APPLY` re-derives the same verdict from the same bytes. A batch whose declaratieregels were released for a redeclaratie (#4427) is reported as `SKIPPED`, and a regel the practice has acted on since — credited, released, excluded — is never overwritten.  One transaction per batch, so one malformed stored retour costs that batch and nothing else. Requires the `admin.insurer-returns.reapply` permission. 
         * @summary Re-apply the retourberichten an organization already imported
         * @param {string} organizationId The organization whose stored retourberichten to re-apply
         * @param {Gds802ReapplyRequest} gds802ReapplyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async adminReapplyInsurerReturns(organizationId: string, gds802ReapplyRequest: Gds802ReapplyRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Gds802ReapplyReport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.adminReapplyInsurerReturns(organizationId, gds802ReapplyRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminInsurerReturnsApi.adminReapplyInsurerReturns']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Brings every declared batch\'s verzekeraarfacturen into line with what the batch declared (#4730). Until #4730 a verzekeraarfactuur was built from the regels still on its batch, and a release for redeclaration took regels off one that existed — so a partially approved or redeclared batch\'s invoice came out at roughly what the insurer paid, and a batch whose every regel was released had none.  A batch is regenerated when its stored invoices do not bill exactly its declarations, or when it has none but the boekhouding was sent one. The factuurnummer, factuurdatum and the per-cliënt split stay what they were. A batch whose declaration history is incomplete is reported as `REFUSED`, with the reason, and never regenerated short.  A regenerated invoice whose replaced version the boekhouding already booked is left unsent and marked as a correction: the practice\'s next \"Naar boekhouding\" for that batch mails it once, as a correction under the same number, and skips the invoices that were already right. `needsResend` lists those batches.  `DRY_RUN` runs the same code and rolls it back; `APPLY` writes. A second `APPLY` finds nothing left to regenerate. One transaction per batch. Requires the `admin.insurer-returns.reapply` permission. 
         * @summary Regenerate the verzekeraarfacturen that do not bill what their batch declared
         * @param {string} organizationId The organization whose verzekeraarfacturen to regenerate
         * @param {Gds802ReapplyRequest} gds802ReapplyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async adminRepairVerkoopfacturen(organizationId: string, gds802ReapplyRequest: Gds802ReapplyRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<VerzekeraarfactuurRepairReport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.adminRepairVerkoopfacturen(organizationId, gds802ReapplyRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminInsurerReturnsApi.adminRepairVerkoopfacturen']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AdminInsurerReturnsApi - factory interface
 */
export const AdminInsurerReturnsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AdminInsurerReturnsApiFp(configuration)
    return {
        /**
         * Fills in what each declared batch remembers about the regels it declared and has since released for a redeclaration (#4624), and reports what cannot be filled in.  The migration recorded a declaration for every regel still on a batch and for every released regel that still carried its old `Referentienummer`. A regel that was released *and batched again* before declarations were recorded carries only its new references, so for a batch with fewer declarations than it declared regels this reads the GDS801 message it was sent with and writes the missing declarations from it — matched to a regel on cliënt, date, prestatiecode, uitvoerder and begintijd, with the message\'s references and amount. A prestatie no regel matches that way is tried again without the prestatiecode, among redeclared regels only (declared on another batch, batched again, or carrying a herdeclaratiecode), and such a match is reported (`looseMatches`) with the sent and the current code. A prestatie that matches no regel or several is reported (`unmatchedPrestaties`) and nothing is written for it. A released regel lost its verdict on the way out, and this then reads it back from the batch\'s newest applied retour — through the import\'s own reading of a retour, from its stored `original_xml`.  Moves no batch status and no regel — it writes only the batches\' own history. `DRY_RUN` runs the same code and rolls it back; `APPLY` writes. A second `APPLY` writes nothing new. One transaction per batch. Requires the `admin.insurer-returns.reapply` permission. 
         * @summary Complete the declaration history of an organization\'s batches
         * @param {string} organizationId The organization whose batches to complete
         * @param {Gds802ReapplyRequest} gds802ReapplyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        adminBackfillDeclarationHistory(organizationId: string, gds802ReapplyRequest: Gds802ReapplyRequest, options?: RawAxiosRequestConfig): AxiosPromise<DeclarationHistoryReport> {
            return localVarFp.adminBackfillDeclarationHistory(organizationId, gds802ReapplyRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Runs every GDS802 retourbericht this organization has stored back through today\'s import rules, reading each one from its own stored `original_xml`. A change to what a retour *means* repairs nothing already imported, so this is how batches answered before such a change come to carry today\'s reading of the insurer\'s answer — #4421\'s whole-invoice verdicts (#4511), and #4626\'s regels a retour did not name but whose totals settle them. A retour today\'s rules already judged correctly re-applies to the same verdict.  **`DRY_RUN` first.** It works out what would change with exactly the same code the write uses and then rolls the transaction back, so the report is what the write will do rather than a prediction of it. Nothing is written. `APPLY` is a second, explicit call: this moves billing statuses on data the practice has been looking at.  **Idempotent.** A second `APPLY` re-derives the same verdict from the same bytes. A batch whose declaratieregels were released for a redeclaratie (#4427) is reported as `SKIPPED`, and a regel the practice has acted on since — credited, released, excluded — is never overwritten.  One transaction per batch, so one malformed stored retour costs that batch and nothing else. Requires the `admin.insurer-returns.reapply` permission. 
         * @summary Re-apply the retourberichten an organization already imported
         * @param {string} organizationId The organization whose stored retourberichten to re-apply
         * @param {Gds802ReapplyRequest} gds802ReapplyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        adminReapplyInsurerReturns(organizationId: string, gds802ReapplyRequest: Gds802ReapplyRequest, options?: RawAxiosRequestConfig): AxiosPromise<Gds802ReapplyReport> {
            return localVarFp.adminReapplyInsurerReturns(organizationId, gds802ReapplyRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Brings every declared batch\'s verzekeraarfacturen into line with what the batch declared (#4730). Until #4730 a verzekeraarfactuur was built from the regels still on its batch, and a release for redeclaration took regels off one that existed — so a partially approved or redeclared batch\'s invoice came out at roughly what the insurer paid, and a batch whose every regel was released had none.  A batch is regenerated when its stored invoices do not bill exactly its declarations, or when it has none but the boekhouding was sent one. The factuurnummer, factuurdatum and the per-cliënt split stay what they were. A batch whose declaration history is incomplete is reported as `REFUSED`, with the reason, and never regenerated short.  A regenerated invoice whose replaced version the boekhouding already booked is left unsent and marked as a correction: the practice\'s next \"Naar boekhouding\" for that batch mails it once, as a correction under the same number, and skips the invoices that were already right. `needsResend` lists those batches.  `DRY_RUN` runs the same code and rolls it back; `APPLY` writes. A second `APPLY` finds nothing left to regenerate. One transaction per batch. Requires the `admin.insurer-returns.reapply` permission. 
         * @summary Regenerate the verzekeraarfacturen that do not bill what their batch declared
         * @param {string} organizationId The organization whose verzekeraarfacturen to regenerate
         * @param {Gds802ReapplyRequest} gds802ReapplyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        adminRepairVerkoopfacturen(organizationId: string, gds802ReapplyRequest: Gds802ReapplyRequest, options?: RawAxiosRequestConfig): AxiosPromise<VerzekeraarfactuurRepairReport> {
            return localVarFp.adminRepairVerkoopfacturen(organizationId, gds802ReapplyRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AdminInsurerReturnsApi - object-oriented interface
 */
export class AdminInsurerReturnsApi extends BaseAPI {
    /**
     * Fills in what each declared batch remembers about the regels it declared and has since released for a redeclaration (#4624), and reports what cannot be filled in.  The migration recorded a declaration for every regel still on a batch and for every released regel that still carried its old `Referentienummer`. A regel that was released *and batched again* before declarations were recorded carries only its new references, so for a batch with fewer declarations than it declared regels this reads the GDS801 message it was sent with and writes the missing declarations from it — matched to a regel on cliënt, date, prestatiecode, uitvoerder and begintijd, with the message\'s references and amount. A prestatie no regel matches that way is tried again without the prestatiecode, among redeclared regels only (declared on another batch, batched again, or carrying a herdeclaratiecode), and such a match is reported (`looseMatches`) with the sent and the current code. A prestatie that matches no regel or several is reported (`unmatchedPrestaties`) and nothing is written for it. A released regel lost its verdict on the way out, and this then reads it back from the batch\'s newest applied retour — through the import\'s own reading of a retour, from its stored `original_xml`.  Moves no batch status and no regel — it writes only the batches\' own history. `DRY_RUN` runs the same code and rolls it back; `APPLY` writes. A second `APPLY` writes nothing new. One transaction per batch. Requires the `admin.insurer-returns.reapply` permission. 
     * @summary Complete the declaration history of an organization\'s batches
     * @param {string} organizationId The organization whose batches to complete
     * @param {Gds802ReapplyRequest} gds802ReapplyRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public adminBackfillDeclarationHistory(organizationId: string, gds802ReapplyRequest: Gds802ReapplyRequest, options?: RawAxiosRequestConfig) {
        return AdminInsurerReturnsApiFp(this.configuration).adminBackfillDeclarationHistory(organizationId, gds802ReapplyRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Runs every GDS802 retourbericht this organization has stored back through today\'s import rules, reading each one from its own stored `original_xml`. A change to what a retour *means* repairs nothing already imported, so this is how batches answered before such a change come to carry today\'s reading of the insurer\'s answer — #4421\'s whole-invoice verdicts (#4511), and #4626\'s regels a retour did not name but whose totals settle them. A retour today\'s rules already judged correctly re-applies to the same verdict.  **`DRY_RUN` first.** It works out what would change with exactly the same code the write uses and then rolls the transaction back, so the report is what the write will do rather than a prediction of it. Nothing is written. `APPLY` is a second, explicit call: this moves billing statuses on data the practice has been looking at.  **Idempotent.** A second `APPLY` re-derives the same verdict from the same bytes. A batch whose declaratieregels were released for a redeclaratie (#4427) is reported as `SKIPPED`, and a regel the practice has acted on since — credited, released, excluded — is never overwritten.  One transaction per batch, so one malformed stored retour costs that batch and nothing else. Requires the `admin.insurer-returns.reapply` permission. 
     * @summary Re-apply the retourberichten an organization already imported
     * @param {string} organizationId The organization whose stored retourberichten to re-apply
     * @param {Gds802ReapplyRequest} gds802ReapplyRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public adminReapplyInsurerReturns(organizationId: string, gds802ReapplyRequest: Gds802ReapplyRequest, options?: RawAxiosRequestConfig) {
        return AdminInsurerReturnsApiFp(this.configuration).adminReapplyInsurerReturns(organizationId, gds802ReapplyRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Brings every declared batch\'s verzekeraarfacturen into line with what the batch declared (#4730). Until #4730 a verzekeraarfactuur was built from the regels still on its batch, and a release for redeclaration took regels off one that existed — so a partially approved or redeclared batch\'s invoice came out at roughly what the insurer paid, and a batch whose every regel was released had none.  A batch is regenerated when its stored invoices do not bill exactly its declarations, or when it has none but the boekhouding was sent one. The factuurnummer, factuurdatum and the per-cliënt split stay what they were. A batch whose declaration history is incomplete is reported as `REFUSED`, with the reason, and never regenerated short.  A regenerated invoice whose replaced version the boekhouding already booked is left unsent and marked as a correction: the practice\'s next \"Naar boekhouding\" for that batch mails it once, as a correction under the same number, and skips the invoices that were already right. `needsResend` lists those batches.  `DRY_RUN` runs the same code and rolls it back; `APPLY` writes. A second `APPLY` finds nothing left to regenerate. One transaction per batch. Requires the `admin.insurer-returns.reapply` permission. 
     * @summary Regenerate the verzekeraarfacturen that do not bill what their batch declared
     * @param {string} organizationId The organization whose verzekeraarfacturen to regenerate
     * @param {Gds802ReapplyRequest} gds802ReapplyRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public adminRepairVerkoopfacturen(organizationId: string, gds802ReapplyRequest: Gds802ReapplyRequest, options?: RawAxiosRequestConfig) {
        return AdminInsurerReturnsApiFp(this.configuration).adminRepairVerkoopfacturen(organizationId, gds802ReapplyRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * AdminInvitationsApi - axios parameter creator
 */
export const AdminInvitationsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Accepts a pending admin invitation. The invitee chooses their password here; the backend provisions the Firebase account with that password (creating a fresh account, or — when one already exists for the invited email, e.g. a dual app/admin user or an orphaned credential-less shell — setting the password on the existing account and reusing its UID). It then provisions the admin user (role + support partner scope), contributes admin custom claims, and marks the single-use token `ACCEPTED`.  Provisioning the password server-side means activation works identically for brand-new and pre-existing emails and never depends on the invitee knowing any prior password.  This endpoint is **public** — the invitation token is the capability that proves ownership of the invited inbox. 
         * @summary Accept an admin invitation (public)
         * @param {string} token The invitation token from the email link
         * @param {AcceptAdminInvitationRequest} acceptAdminInvitationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        acceptAdminInvitation: async (token: string, acceptAdminInvitationRequest: AcceptAdminInvitationRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'token' is not null or undefined
            assertParamExists('acceptAdminInvitation', 'token', token)
            // verify required parameter 'acceptAdminInvitationRequest' is not null or undefined
            assertParamExists('acceptAdminInvitation', 'acceptAdminInvitationRequest', acceptAdminInvitationRequest)
            const localVarPath = `/api/admin/admin-invitations/{token}/accept`
                .replace(`{${"token"}}`, encodeURIComponent(String(token)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(acceptAdminInvitationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the non-sensitive details of a pending admin invitation by its token, so the accept page can show what the invitee is accepting before they set a password.  This endpoint is **public** — no authentication required. 
         * @summary Get admin invitation details (public)
         * @param {string} token The invitation token from the email link
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAdminInvitationByToken: async (token: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'token' is not null or undefined
            assertParamExists('getAdminInvitationByToken', 'token', token)
            const localVarPath = `/api/admin/admin-invitations/{token}`
                .replace(`{${"token"}}`, encodeURIComponent(String(token)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AdminInvitationsApi - functional programming interface
 */
export const AdminInvitationsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AdminInvitationsApiAxiosParamCreator(configuration)
    return {
        /**
         * Accepts a pending admin invitation. The invitee chooses their password here; the backend provisions the Firebase account with that password (creating a fresh account, or — when one already exists for the invited email, e.g. a dual app/admin user or an orphaned credential-less shell — setting the password on the existing account and reusing its UID). It then provisions the admin user (role + support partner scope), contributes admin custom claims, and marks the single-use token `ACCEPTED`.  Provisioning the password server-side means activation works identically for brand-new and pre-existing emails and never depends on the invitee knowing any prior password.  This endpoint is **public** — the invitation token is the capability that proves ownership of the invited inbox. 
         * @summary Accept an admin invitation (public)
         * @param {string} token The invitation token from the email link
         * @param {AcceptAdminInvitationRequest} acceptAdminInvitationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async acceptAdminInvitation(token: string, acceptAdminInvitationRequest: AcceptAdminInvitationRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AcceptAdminInvitationResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.acceptAdminInvitation(token, acceptAdminInvitationRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminInvitationsApi.acceptAdminInvitation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the non-sensitive details of a pending admin invitation by its token, so the accept page can show what the invitee is accepting before they set a password.  This endpoint is **public** — no authentication required. 
         * @summary Get admin invitation details (public)
         * @param {string} token The invitation token from the email link
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getAdminInvitationByToken(token: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PublicAdminInvitationResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getAdminInvitationByToken(token, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminInvitationsApi.getAdminInvitationByToken']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AdminInvitationsApi - factory interface
 */
export const AdminInvitationsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AdminInvitationsApiFp(configuration)
    return {
        /**
         * Accepts a pending admin invitation. The invitee chooses their password here; the backend provisions the Firebase account with that password (creating a fresh account, or — when one already exists for the invited email, e.g. a dual app/admin user or an orphaned credential-less shell — setting the password on the existing account and reusing its UID). It then provisions the admin user (role + support partner scope), contributes admin custom claims, and marks the single-use token `ACCEPTED`.  Provisioning the password server-side means activation works identically for brand-new and pre-existing emails and never depends on the invitee knowing any prior password.  This endpoint is **public** — the invitation token is the capability that proves ownership of the invited inbox. 
         * @summary Accept an admin invitation (public)
         * @param {string} token The invitation token from the email link
         * @param {AcceptAdminInvitationRequest} acceptAdminInvitationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        acceptAdminInvitation(token: string, acceptAdminInvitationRequest: AcceptAdminInvitationRequest, options?: RawAxiosRequestConfig): AxiosPromise<AcceptAdminInvitationResponse> {
            return localVarFp.acceptAdminInvitation(token, acceptAdminInvitationRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the non-sensitive details of a pending admin invitation by its token, so the accept page can show what the invitee is accepting before they set a password.  This endpoint is **public** — no authentication required. 
         * @summary Get admin invitation details (public)
         * @param {string} token The invitation token from the email link
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAdminInvitationByToken(token: string, options?: RawAxiosRequestConfig): AxiosPromise<PublicAdminInvitationResponse> {
            return localVarFp.getAdminInvitationByToken(token, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AdminInvitationsApi - object-oriented interface
 */
export class AdminInvitationsApi extends BaseAPI {
    /**
     * Accepts a pending admin invitation. The invitee chooses their password here; the backend provisions the Firebase account with that password (creating a fresh account, or — when one already exists for the invited email, e.g. a dual app/admin user or an orphaned credential-less shell — setting the password on the existing account and reusing its UID). It then provisions the admin user (role + support partner scope), contributes admin custom claims, and marks the single-use token `ACCEPTED`.  Provisioning the password server-side means activation works identically for brand-new and pre-existing emails and never depends on the invitee knowing any prior password.  This endpoint is **public** — the invitation token is the capability that proves ownership of the invited inbox. 
     * @summary Accept an admin invitation (public)
     * @param {string} token The invitation token from the email link
     * @param {AcceptAdminInvitationRequest} acceptAdminInvitationRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public acceptAdminInvitation(token: string, acceptAdminInvitationRequest: AcceptAdminInvitationRequest, options?: RawAxiosRequestConfig) {
        return AdminInvitationsApiFp(this.configuration).acceptAdminInvitation(token, acceptAdminInvitationRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the non-sensitive details of a pending admin invitation by its token, so the accept page can show what the invitee is accepting before they set a password.  This endpoint is **public** — no authentication required. 
     * @summary Get admin invitation details (public)
     * @param {string} token The invitation token from the email link
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getAdminInvitationByToken(token: string, options?: RawAxiosRequestConfig) {
        return AdminInvitationsApiFp(this.configuration).getAdminInvitationByToken(token, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * AdminOrganizationClosureApi - axios parameter creator
 */
export const AdminOrganizationClosureApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * The organisation becomes `CLOSED` (#4905): it is gone from every list and job, and out of its members\' reach — dropped from their organisation switcher and their sign-in claims. A member of another organisation keeps that one; one whose only organisation this was may sign up a new practice.  **Nothing is deleted.** Its records, its subscription ledger and its invoices stay, and purging them is a separate act. Nothing in this API reopens a closed organisation.  Allowed for an organisation that has **ended** (its subscription was cancelled), and for one Scrivio **never billed**: no subscription, or an `UNMANAGED` one with no invoice ever issued. Anything else is refused with 409 — cancel its subscription first.  Audited in the admin audit log with the reason given. Requires `admin.organizations.close`, which only the Platform Administrator holds. 
         * @summary Close an organization, keeping all its data
         * @param {string} organizationId Organization ID
         * @param {AdminCloseOrganizationRequest} adminCloseOrganizationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        closeOrganization: async (organizationId: string, adminCloseOrganizationRequest: AdminCloseOrganizationRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('closeOrganization', 'organizationId', organizationId)
            // verify required parameter 'adminCloseOrganizationRequest' is not null or undefined
            assertParamExists('closeOrganization', 'adminCloseOrganizationRequest', adminCloseOrganizationRequest)
            const localVarPath = `/api/admin/organizations/{organizationId}/closure`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(adminCloseOrganizationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AdminOrganizationClosureApi - functional programming interface
 */
export const AdminOrganizationClosureApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AdminOrganizationClosureApiAxiosParamCreator(configuration)
    return {
        /**
         * The organisation becomes `CLOSED` (#4905): it is gone from every list and job, and out of its members\' reach — dropped from their organisation switcher and their sign-in claims. A member of another organisation keeps that one; one whose only organisation this was may sign up a new practice.  **Nothing is deleted.** Its records, its subscription ledger and its invoices stay, and purging them is a separate act. Nothing in this API reopens a closed organisation.  Allowed for an organisation that has **ended** (its subscription was cancelled), and for one Scrivio **never billed**: no subscription, or an `UNMANAGED` one with no invoice ever issued. Anything else is refused with 409 — cancel its subscription first.  Audited in the admin audit log with the reason given. Requires `admin.organizations.close`, which only the Platform Administrator holds. 
         * @summary Close an organization, keeping all its data
         * @param {string} organizationId Organization ID
         * @param {AdminCloseOrganizationRequest} adminCloseOrganizationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async closeOrganization(organizationId: string, adminCloseOrganizationRequest: AdminCloseOrganizationRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.closeOrganization(organizationId, adminCloseOrganizationRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminOrganizationClosureApi.closeOrganization']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AdminOrganizationClosureApi - factory interface
 */
export const AdminOrganizationClosureApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AdminOrganizationClosureApiFp(configuration)
    return {
        /**
         * The organisation becomes `CLOSED` (#4905): it is gone from every list and job, and out of its members\' reach — dropped from their organisation switcher and their sign-in claims. A member of another organisation keeps that one; one whose only organisation this was may sign up a new practice.  **Nothing is deleted.** Its records, its subscription ledger and its invoices stay, and purging them is a separate act. Nothing in this API reopens a closed organisation.  Allowed for an organisation that has **ended** (its subscription was cancelled), and for one Scrivio **never billed**: no subscription, or an `UNMANAGED` one with no invoice ever issued. Anything else is refused with 409 — cancel its subscription first.  Audited in the admin audit log with the reason given. Requires `admin.organizations.close`, which only the Platform Administrator holds. 
         * @summary Close an organization, keeping all its data
         * @param {string} organizationId Organization ID
         * @param {AdminCloseOrganizationRequest} adminCloseOrganizationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        closeOrganization(organizationId: string, adminCloseOrganizationRequest: AdminCloseOrganizationRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.closeOrganization(organizationId, adminCloseOrganizationRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AdminOrganizationClosureApi - object-oriented interface
 */
export class AdminOrganizationClosureApi extends BaseAPI {
    /**
     * The organisation becomes `CLOSED` (#4905): it is gone from every list and job, and out of its members\' reach — dropped from their organisation switcher and their sign-in claims. A member of another organisation keeps that one; one whose only organisation this was may sign up a new practice.  **Nothing is deleted.** Its records, its subscription ledger and its invoices stay, and purging them is a separate act. Nothing in this API reopens a closed organisation.  Allowed for an organisation that has **ended** (its subscription was cancelled), and for one Scrivio **never billed**: no subscription, or an `UNMANAGED` one with no invoice ever issued. Anything else is refused with 409 — cancel its subscription first.  Audited in the admin audit log with the reason given. Requires `admin.organizations.close`, which only the Platform Administrator holds. 
     * @summary Close an organization, keeping all its data
     * @param {string} organizationId Organization ID
     * @param {AdminCloseOrganizationRequest} adminCloseOrganizationRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public closeOrganization(organizationId: string, adminCloseOrganizationRequest: AdminCloseOrganizationRequest, options?: RawAxiosRequestConfig) {
        return AdminOrganizationClosureApiFp(this.configuration).closeOrganization(organizationId, adminCloseOrganizationRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * AdminOrganizationsApi - axios parameter creator
 */
export const AdminOrganizationsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Cancels a license type change (past or future).  Cancelling a past change may affect the computed effective license type.  **Scoping:** - Platform admins can cancel changes for any organization - Support agents can only cancel changes for organizations assigned to their support partner  Requires `admin.organizations.manage` permission. 
         * @summary Cancel a license change
         * @param {string} id Organization ID
         * @param {string} changeId Scheduled license change ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        cancelLicenseChange: async (id: string, changeId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('cancelLicenseChange', 'id', id)
            // verify required parameter 'changeId' is not null or undefined
            assertParamExists('cancelLicenseChange', 'changeId', changeId)
            const localVarPath = `/api/admin/organizations/{id}/license-changes/{changeId}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)))
                .replace(`{${"changeId"}}`, encodeURIComponent(String(changeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Creates a new organization, invites its first beheerder and puts it on a plan.  **Process:** 1. Validates domain uniqueness 2. Creates the organization entity 3. Sets up default roles via OrganizationService.createDefaultRoles() 4. Creates and invites the initial admin user 5. Activates the subscription on the plan and agreed prices in `subscription` 6. Logs the action to admin audit log  All of it is one transaction: an organization is never left without a first beheerder or on an `UNMANAGED` subscription Scrivio collects nothing from (#4551).  Requires `admin.organizations.create` **and** `admin.subscriptions.manage`: creating an organization now agrees what the practice pays, which is the same decision activating a subscription is (V20260913184130). 
         * @summary Create a new organization
         * @param {AdminCreateOrganizationRequest} adminCreateOrganizationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createOrganization: async (adminCreateOrganizationRequest: AdminCreateOrganizationRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'adminCreateOrganizationRequest' is not null or undefined
            assertParamExists('createOrganization', 'adminCreateOrganizationRequest', adminCreateOrganizationRequest)
            const localVarPath = `/api/admin/organizations`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(adminCreateOrganizationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns detailed information about a specific organization.  **Scoping:** - Platform admins can view any organization - Support agents can only view organizations assigned to their support partner  Requires `admin.organizations.view` permission. 
         * @summary Get organization details
         * @param {string} id Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getOrganization: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('getOrganization', 'id', id)
            const localVarPath = `/api/admin/organizations/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Every invitation to the organization that is still `PENDING`, including those whose link has expired — an expired invitation stays pending until it is resent or revoked, and is the one an admin is most likely looking for (#4418). `expired` says which.  This is how an organization Scrivio created can be reached when its first beheerder never joined: nobody inside it can see or resend the invitation.  **Scoping:** support agents only see organizations assigned to their support partner.  Requires `admin.users.view` permission. 
         * @summary List an organization\'s pending invitations
         * @param {string} organizationId The organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listOrganizationInvitations: async (organizationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('listOrganizationInvitations', 'organizationId', organizationId)
            const localVarPath = `/api/admin/organizations/{organizationId}/invitations`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns a paginated list of users within a specific organization.  **Scoping:** - Platform admins can view users in any organization - Support agents can only view users in organizations assigned to their support partner  **Privacy:** - Other organization names are not revealed; only the count of other organizations is shown  Requires `admin.users.view` permission. 
         * @summary List users in an organization
         * @param {string} organizationId The organization ID
         * @param {string} [search] Filter by name or email (partial match, case-insensitive)
         * @param {UserStatus} [status] Filter by user status
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listOrganizationUsers: async (organizationId: string, search?: string, status?: UserStatus, page?: number, size?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('listOrganizationUsers', 'organizationId', organizationId)
            const localVarPath = `/api/admin/organizations/{organizationId}/users`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (search !== undefined) {
                localVarQueryParameter['search'] = search;
            }

            if (status !== undefined) {
                localVarQueryParameter['status'] = status;
            }

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns a paginated list of all organizations with optional filtering.  **Scoping:** - Platform admins see all organizations - Support agents see only organizations assigned to their support partner  Requires `admin.organizations.view` permission. 
         * @summary List all organizations
         * @param {string} [search] Filter by name, domain, or the primary contact\&#39;s email address or name (partial match, case-insensitive). An organization with no primary contact yet also matches on the address of a pending invitation (#4834). 
         * @param {LicenseType} [licenseType] Filter by license type
         * @param {OrganizationStatus} [status] Filter by organization status
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {OrganizationKind} [kind] Filter by organization kind; omit it to list customers and test organizations alike (#4773)
         * @param {AuthProvider} [authProvider] Filter by where the organization\&#39;s behandelaars sign in (ADR-045 §7)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listOrganizations: async (search?: string, licenseType?: LicenseType, status?: OrganizationStatus, page?: number, size?: number, kind?: OrganizationKind, authProvider?: AuthProvider, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/admin/organizations`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (search !== undefined) {
                localVarQueryParameter['search'] = search;
            }

            if (licenseType !== undefined) {
                localVarQueryParameter['licenseType'] = licenseType;
            }

            if (status !== undefined) {
                localVarQueryParameter['status'] = status;
            }

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            if (kind !== undefined) {
                localVarQueryParameter['kind'] = kind;
            }

            if (authProvider !== undefined) {
                localVarQueryParameter['authProvider'] = authProvider;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Irreversibly deletes all operational data belonging to an organization (clients, care trajectories, appointments, documents, billing, signals, …). Reference data and the organization record itself are preserved.  **Scope — GDPR / end-of-contract only.** Use this strictly for end-of-contract decommissioning and GDPR right-to-erasure. It is NOT the way to correct incorrect billing or clear test data; use the scoped discard of not-yet-issued billing or the void + credit-note flow for issued billing instead.  This is a destructive, platform-super-admin-only operation:  - Pass `dryRun: true` to get a preview (per-table counts) without deleting anything. - A real run (`dryRun: false`) requires `confirmationName` to exactly match the   organization\'s name and a non-empty `reason` (logged to the admin audit log).  **Guardrails:** - An admin cannot purge an organization they themselves are a member of   (the org they would impersonate into). - Only an organization that has left — lifecycle `ENDED` or `CLOSED` — can be purged,   so never one Scrivio still bills. A real run on any other is refused (409); a dry run   still answers, with `purgeAllowed: false` and the `lifecycle` that refuses it. - A purge is taken only at the practice\'s own request: a real run records who asked   (`requestedBy`) and on which day (`requestedOn`) in the admin audit log, and is   refused (400) without them. A closed organization is still found here.  Requires `admin.organizations.purge-dossier` permission. 
         * @summary Permanently purge an organization\'s dossier data
         * @param {string} id Organization ID
         * @param {AdminPurgeDossierRequest} adminPurgeDossierRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        purgeDossier: async (id: string, adminPurgeDossierRequest: AdminPurgeDossierRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('purgeDossier', 'id', id)
            // verify required parameter 'adminPurgeDossierRequest' is not null or undefined
            assertParamExists('purgeDossier', 'adminPurgeDossierRequest', adminPurgeDossierRequest)
            const localVarPath = `/api/admin/organizations/{id}/purge-dossier`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(adminPurgeDossierRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Reactivates a previously suspended organization, restoring full access.  **Scoping:** - Platform admins can reactivate any organization - Support agents can only reactivate organizations assigned to their support partner  Requires `admin.organizations.manage` permission. 
         * @summary Reactivate a suspended organization
         * @param {string} id Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        reactivateOrganization: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('reactivateOrganization', 'id', id)
            const localVarPath = `/api/admin/organizations/{id}/reactivate`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Sends the invitation email again with a fresh link and a new expiry window (#4418). The previous link stops working. Works on an expired invitation as well — that is what it is for — but not on one that has been accepted or revoked (409).  Recorded in the organization\'s audit log and in the admin audit log.  **Scoping:** support agents only reach organizations assigned to their support partner.  Requires `admin.organizations.edit` permission. 
         * @summary Resend a pending invitation
         * @param {string} organizationId The organization ID
         * @param {string} invitationId The invitation ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resendOrganizationInvitation: async (organizationId: string, invitationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('resendOrganizationInvitation', 'organizationId', organizationId)
            // verify required parameter 'invitationId' is not null or undefined
            assertParamExists('resendOrganizationInvitation', 'invitationId', invitationId)
            const localVarPath = `/api/admin/organizations/{organizationId}/invitations/{invitationId}/resend`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)))
                .replace(`{${"invitationId"}}`, encodeURIComponent(String(invitationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Schedules a license type change for an organization.  The change will take effect at midnight (server time) on the specified effective date. Past dates are allowed for correcting historical records. Only one change can be scheduled per effective date.  **Scoping:** - Platform admins can schedule changes for any organization - Support agents can only schedule changes for organizations assigned to their support partner  Requires `admin.organizations.manage` permission. 
         * @summary Schedule a license type change
         * @param {string} id Organization ID
         * @param {AdminScheduleLicenseChangeRequest} adminScheduleLicenseChangeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        scheduleLicenseChange: async (id: string, adminScheduleLicenseChangeRequest: AdminScheduleLicenseChangeRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('scheduleLicenseChange', 'id', id)
            // verify required parameter 'adminScheduleLicenseChangeRequest' is not null or undefined
            assertParamExists('scheduleLicenseChange', 'adminScheduleLicenseChangeRequest', adminScheduleLicenseChangeRequest)
            const localVarPath = `/api/admin/organizations/{id}/license-changes`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(adminScheduleLicenseChangeRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Sets where this organization\'s behandelaars sign in (ADR-045 §7). Every organization is on `FIREBASE` until this moves it; moving it back is the rollback, and returns its behandelaars to the Firebase accounts they had — nothing in Firebase is changed by either direction.  A request that changes nothing writes no audit entry; a change is written to the admin audit log with the previous and new provider.  Requires `admin.organizations.auth-provider`, held by the Platform Administrator alone: the switch is a cutover for every behandelaar of the organization at once. 
         * @summary Move an organization\'s behandelaars between Firebase and Keycloak sign-in
         * @param {string} id Organization ID
         * @param {AdminSetAuthProviderRequest} adminSetAuthProviderRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        setOrganizationAuthProvider: async (id: string, adminSetAuthProviderRequest: AdminSetAuthProviderRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('setOrganizationAuthProvider', 'id', id)
            // verify required parameter 'adminSetAuthProviderRequest' is not null or undefined
            assertParamExists('setOrganizationAuthProvider', 'adminSetAuthProviderRequest', adminSetAuthProviderRequest)
            const localVarPath = `/api/admin/organizations/{id}/auth-provider`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(adminSetAuthProviderRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Chooses how one stream family travels for this organization (ADR-049 §2): through the practice\'s own export/import in VECOZO\'s portal, or sent and pulled by Scrivio over the deployment\'s VECOZO berichtenservice. Every organization starts on `PORTAAL` for both.  Set it to `BERICHTENSERVICE` only once VECOZO has granted the stream\'s toestemmingsverklaring for the organization\'s AGB — Declareren for `GDS801`, **both** `VSP-JW` and `Jeugdwet declaraties` for `IJW`. Scrivio cannot read that grant, and a stream moved without it has every message refused (`BRS03`).  Switching is a **cutover**: a message that arrived through the portal carries no envelope Scrivio saw, so it is still answered through the portal.  This is not the VECOZO environment, which is the deployment\'s (ADR-044).  `BERICHTENSERVICE` is refused with a 409 while this deployment has no berichtenservice connection configured (`berichtenserviceConfigured` on the organization). A request that changes nothing writes no audit entry; a change is written to the admin audit log with the stream and the previous and new channel.  Requires `admin.organizations.vecozo-kanaal` (Platform Administrator only). 
         * @summary Move one of an organization\'s VECOZO streams between the portal and the berichtenservice
         * @param {string} id Organization ID
         * @param {VecozoStroom} stroom The stream family to move
         * @param {AdminSetVecozoKanaalRequest} adminSetVecozoKanaalRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        setVecozoKanaal: async (id: string, stroom: VecozoStroom, adminSetVecozoKanaalRequest: AdminSetVecozoKanaalRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('setVecozoKanaal', 'id', id)
            // verify required parameter 'stroom' is not null or undefined
            assertParamExists('setVecozoKanaal', 'stroom', stroom)
            // verify required parameter 'adminSetVecozoKanaalRequest' is not null or undefined
            assertParamExists('setVecozoKanaal', 'adminSetVecozoKanaalRequest', adminSetVecozoKanaalRequest)
            const localVarPath = `/api/admin/organizations/{id}/vecozo-kanalen/{stroom}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)))
                .replace(`{${"stroom"}}`, encodeURIComponent(String(stroom)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(adminSetVecozoKanaalRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Suspends an organization, preventing all users from accessing the platform.  Suspended organizations: - Users cannot log in - API calls are rejected with 403 - Data is preserved (not deleted)  **Scoping:** - Platform admins can suspend any organization - Support agents can only suspend organizations assigned to their support partner  Requires `admin.organizations.manage` permission. 
         * @summary Suspend an organization
         * @param {string} id Organization ID
         * @param {AdminSuspendOrganizationRequest} adminSuspendOrganizationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        suspendOrganization: async (id: string, adminSuspendOrganizationRequest: AdminSuspendOrganizationRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('suspendOrganization', 'id', id)
            // verify required parameter 'adminSuspendOrganizationRequest' is not null or undefined
            assertParamExists('suspendOrganization', 'adminSuspendOrganizationRequest', adminSuspendOrganizationRequest)
            const localVarPath = `/api/admin/organizations/{id}/suspend`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(adminSuspendOrganizationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Records the organization\'s tenaamstelling, T.a.v. line, billing e-mail address and BTW-identificatienummer — what a Scrivio invoice to this practice renders in its Factuuradres block (#4448).  The practice maintains the same four fields itself through `PUT /api/v1/organizations/me`. This is the admin portal\'s door onto them, for a practice that has not filled them in and an invoice that has to go out: it writes them through the same normalisation, so an admin and a praktijkhouder typing the same thing store the same thing.  Every property is optional: omit one to leave it alone, send null to clear it. The change is written to the admin audit log with the previous and new values.  Requires `admin.organizations.edit` permission — the same one that schedules a license change or suspends an organization. No new permission bit. 
         * @summary Set who a Scrivio invoice to this organization is addressed to
         * @param {string} id Organization ID
         * @param {AdminUpdateOrganizationInvoiceDetailsRequest} adminUpdateOrganizationInvoiceDetailsRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateOrganizationInvoiceDetails: async (id: string, adminUpdateOrganizationInvoiceDetailsRequest: AdminUpdateOrganizationInvoiceDetailsRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('updateOrganizationInvoiceDetails', 'id', id)
            // verify required parameter 'adminUpdateOrganizationInvoiceDetailsRequest' is not null or undefined
            assertParamExists('updateOrganizationInvoiceDetails', 'adminUpdateOrganizationInvoiceDetailsRequest', adminUpdateOrganizationInvoiceDetailsRequest)
            const localVarPath = `/api/admin/organizations/{id}/invoice-details`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(adminUpdateOrganizationInvoiceDetailsRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Records whether Scrivio made this organization for testing or demos (#4773). A `TEST` organization stays fully usable, but is left out of the admin dashboard\'s counts and the subscription portfolio, is never renewed and is sent no payment reminder.  A request that changes nothing writes no audit entry; a change is written to the admin audit log with the previous and new kind.  Requires `admin.organizations.edit` **and** `admin.subscriptions.manage`: the kind decides whether a practice is billed, which is the Platform Administrator\'s decision and not a support partner\'s — the same pair that creating an organization requires. 
         * @summary Mark an organization as a customer or a test organization
         * @param {string} id Organization ID
         * @param {AdminUpdateOrganizationKindRequest} adminUpdateOrganizationKindRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateOrganizationKind: async (id: string, adminUpdateOrganizationKindRequest: AdminUpdateOrganizationKindRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('updateOrganizationKind', 'id', id)
            // verify required parameter 'adminUpdateOrganizationKindRequest' is not null or undefined
            assertParamExists('updateOrganizationKind', 'adminUpdateOrganizationKindRequest', adminUpdateOrganizationKindRequest)
            const localVarPath = `/api/admin/organizations/{id}/kind`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(adminUpdateOrganizationKindRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Chooses which member of the organization is its primary contact person (#4834), or clears it when `userId` is null or omitted. A user who is not a member of the organization is refused with a 400 — a membership that has since been disabled still counts, so the contact standing can always be re-chosen.  A request that changes nothing writes no audit entry; a change is written to the admin audit log with the previous and new user.  Requires `admin.organizations.edit` permission. The practice app neither shows nor changes the primary contact. 
         * @summary Set or clear an organization\'s primary contact
         * @param {string} id Organization ID
         * @param {AdminUpdateOrganizationPrimaryContactRequest} adminUpdateOrganizationPrimaryContactRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateOrganizationPrimaryContact: async (id: string, adminUpdateOrganizationPrimaryContactRequest: AdminUpdateOrganizationPrimaryContactRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('updateOrganizationPrimaryContact', 'id', id)
            // verify required parameter 'adminUpdateOrganizationPrimaryContactRequest' is not null or undefined
            assertParamExists('updateOrganizationPrimaryContact', 'adminUpdateOrganizationPrimaryContactRequest', adminUpdateOrganizationPrimaryContactRequest)
            const localVarPath = `/api/admin/organizations/{id}/primary-contact`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(adminUpdateOrganizationPrimaryContactRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AdminOrganizationsApi - functional programming interface
 */
export const AdminOrganizationsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AdminOrganizationsApiAxiosParamCreator(configuration)
    return {
        /**
         * Cancels a license type change (past or future).  Cancelling a past change may affect the computed effective license type.  **Scoping:** - Platform admins can cancel changes for any organization - Support agents can only cancel changes for organizations assigned to their support partner  Requires `admin.organizations.manage` permission. 
         * @summary Cancel a license change
         * @param {string} id Organization ID
         * @param {string} changeId Scheduled license change ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async cancelLicenseChange(id: string, changeId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.cancelLicenseChange(id, changeId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminOrganizationsApi.cancelLicenseChange']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Creates a new organization, invites its first beheerder and puts it on a plan.  **Process:** 1. Validates domain uniqueness 2. Creates the organization entity 3. Sets up default roles via OrganizationService.createDefaultRoles() 4. Creates and invites the initial admin user 5. Activates the subscription on the plan and agreed prices in `subscription` 6. Logs the action to admin audit log  All of it is one transaction: an organization is never left without a first beheerder or on an `UNMANAGED` subscription Scrivio collects nothing from (#4551).  Requires `admin.organizations.create` **and** `admin.subscriptions.manage`: creating an organization now agrees what the practice pays, which is the same decision activating a subscription is (V20260913184130). 
         * @summary Create a new organization
         * @param {AdminCreateOrganizationRequest} adminCreateOrganizationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createOrganization(adminCreateOrganizationRequest: AdminCreateOrganizationRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminOrganizationDto>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createOrganization(adminCreateOrganizationRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminOrganizationsApi.createOrganization']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns detailed information about a specific organization.  **Scoping:** - Platform admins can view any organization - Support agents can only view organizations assigned to their support partner  Requires `admin.organizations.view` permission. 
         * @summary Get organization details
         * @param {string} id Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getOrganization(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminOrganizationDetailDto>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getOrganization(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminOrganizationsApi.getOrganization']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Every invitation to the organization that is still `PENDING`, including those whose link has expired — an expired invitation stays pending until it is resent or revoked, and is the one an admin is most likely looking for (#4418). `expired` says which.  This is how an organization Scrivio created can be reached when its first beheerder never joined: nobody inside it can see or resend the invitation.  **Scoping:** support agents only see organizations assigned to their support partner.  Requires `admin.users.view` permission. 
         * @summary List an organization\'s pending invitations
         * @param {string} organizationId The organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listOrganizationInvitations(organizationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminOrganizationInvitationListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listOrganizationInvitations(organizationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminOrganizationsApi.listOrganizationInvitations']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns a paginated list of users within a specific organization.  **Scoping:** - Platform admins can view users in any organization - Support agents can only view users in organizations assigned to their support partner  **Privacy:** - Other organization names are not revealed; only the count of other organizations is shown  Requires `admin.users.view` permission. 
         * @summary List users in an organization
         * @param {string} organizationId The organization ID
         * @param {string} [search] Filter by name or email (partial match, case-insensitive)
         * @param {UserStatus} [status] Filter by user status
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listOrganizationUsers(organizationId: string, search?: string, status?: UserStatus, page?: number, size?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminOrganizationUserListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listOrganizationUsers(organizationId, search, status, page, size, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminOrganizationsApi.listOrganizationUsers']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns a paginated list of all organizations with optional filtering.  **Scoping:** - Platform admins see all organizations - Support agents see only organizations assigned to their support partner  Requires `admin.organizations.view` permission. 
         * @summary List all organizations
         * @param {string} [search] Filter by name, domain, or the primary contact\&#39;s email address or name (partial match, case-insensitive). An organization with no primary contact yet also matches on the address of a pending invitation (#4834). 
         * @param {LicenseType} [licenseType] Filter by license type
         * @param {OrganizationStatus} [status] Filter by organization status
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {OrganizationKind} [kind] Filter by organization kind; omit it to list customers and test organizations alike (#4773)
         * @param {AuthProvider} [authProvider] Filter by where the organization\&#39;s behandelaars sign in (ADR-045 §7)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listOrganizations(search?: string, licenseType?: LicenseType, status?: OrganizationStatus, page?: number, size?: number, kind?: OrganizationKind, authProvider?: AuthProvider, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminOrganizationListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listOrganizations(search, licenseType, status, page, size, kind, authProvider, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminOrganizationsApi.listOrganizations']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Irreversibly deletes all operational data belonging to an organization (clients, care trajectories, appointments, documents, billing, signals, …). Reference data and the organization record itself are preserved.  **Scope — GDPR / end-of-contract only.** Use this strictly for end-of-contract decommissioning and GDPR right-to-erasure. It is NOT the way to correct incorrect billing or clear test data; use the scoped discard of not-yet-issued billing or the void + credit-note flow for issued billing instead.  This is a destructive, platform-super-admin-only operation:  - Pass `dryRun: true` to get a preview (per-table counts) without deleting anything. - A real run (`dryRun: false`) requires `confirmationName` to exactly match the   organization\'s name and a non-empty `reason` (logged to the admin audit log).  **Guardrails:** - An admin cannot purge an organization they themselves are a member of   (the org they would impersonate into). - Only an organization that has left — lifecycle `ENDED` or `CLOSED` — can be purged,   so never one Scrivio still bills. A real run on any other is refused (409); a dry run   still answers, with `purgeAllowed: false` and the `lifecycle` that refuses it. - A purge is taken only at the practice\'s own request: a real run records who asked   (`requestedBy`) and on which day (`requestedOn`) in the admin audit log, and is   refused (400) without them. A closed organization is still found here.  Requires `admin.organizations.purge-dossier` permission. 
         * @summary Permanently purge an organization\'s dossier data
         * @param {string} id Organization ID
         * @param {AdminPurgeDossierRequest} adminPurgeDossierRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async purgeDossier(id: string, adminPurgeDossierRequest: AdminPurgeDossierRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PurgeReportDto>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.purgeDossier(id, adminPurgeDossierRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminOrganizationsApi.purgeDossier']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Reactivates a previously suspended organization, restoring full access.  **Scoping:** - Platform admins can reactivate any organization - Support agents can only reactivate organizations assigned to their support partner  Requires `admin.organizations.manage` permission. 
         * @summary Reactivate a suspended organization
         * @param {string} id Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async reactivateOrganization(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminOrganizationDto>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.reactivateOrganization(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminOrganizationsApi.reactivateOrganization']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Sends the invitation email again with a fresh link and a new expiry window (#4418). The previous link stops working. Works on an expired invitation as well — that is what it is for — but not on one that has been accepted or revoked (409).  Recorded in the organization\'s audit log and in the admin audit log.  **Scoping:** support agents only reach organizations assigned to their support partner.  Requires `admin.organizations.edit` permission. 
         * @summary Resend a pending invitation
         * @param {string} organizationId The organization ID
         * @param {string} invitationId The invitation ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async resendOrganizationInvitation(organizationId: string, invitationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminOrganizationInvitationDto>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.resendOrganizationInvitation(organizationId, invitationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminOrganizationsApi.resendOrganizationInvitation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Schedules a license type change for an organization.  The change will take effect at midnight (server time) on the specified effective date. Past dates are allowed for correcting historical records. Only one change can be scheduled per effective date.  **Scoping:** - Platform admins can schedule changes for any organization - Support agents can only schedule changes for organizations assigned to their support partner  Requires `admin.organizations.manage` permission. 
         * @summary Schedule a license type change
         * @param {string} id Organization ID
         * @param {AdminScheduleLicenseChangeRequest} adminScheduleLicenseChangeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async scheduleLicenseChange(id: string, adminScheduleLicenseChangeRequest: AdminScheduleLicenseChangeRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ScheduledLicenseChangeDto>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.scheduleLicenseChange(id, adminScheduleLicenseChangeRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminOrganizationsApi.scheduleLicenseChange']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Sets where this organization\'s behandelaars sign in (ADR-045 §7). Every organization is on `FIREBASE` until this moves it; moving it back is the rollback, and returns its behandelaars to the Firebase accounts they had — nothing in Firebase is changed by either direction.  A request that changes nothing writes no audit entry; a change is written to the admin audit log with the previous and new provider.  Requires `admin.organizations.auth-provider`, held by the Platform Administrator alone: the switch is a cutover for every behandelaar of the organization at once. 
         * @summary Move an organization\'s behandelaars between Firebase and Keycloak sign-in
         * @param {string} id Organization ID
         * @param {AdminSetAuthProviderRequest} adminSetAuthProviderRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async setOrganizationAuthProvider(id: string, adminSetAuthProviderRequest: AdminSetAuthProviderRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminOrganizationDetailDto>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.setOrganizationAuthProvider(id, adminSetAuthProviderRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminOrganizationsApi.setOrganizationAuthProvider']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Chooses how one stream family travels for this organization (ADR-049 §2): through the practice\'s own export/import in VECOZO\'s portal, or sent and pulled by Scrivio over the deployment\'s VECOZO berichtenservice. Every organization starts on `PORTAAL` for both.  Set it to `BERICHTENSERVICE` only once VECOZO has granted the stream\'s toestemmingsverklaring for the organization\'s AGB — Declareren for `GDS801`, **both** `VSP-JW` and `Jeugdwet declaraties` for `IJW`. Scrivio cannot read that grant, and a stream moved without it has every message refused (`BRS03`).  Switching is a **cutover**: a message that arrived through the portal carries no envelope Scrivio saw, so it is still answered through the portal.  This is not the VECOZO environment, which is the deployment\'s (ADR-044).  `BERICHTENSERVICE` is refused with a 409 while this deployment has no berichtenservice connection configured (`berichtenserviceConfigured` on the organization). A request that changes nothing writes no audit entry; a change is written to the admin audit log with the stream and the previous and new channel.  Requires `admin.organizations.vecozo-kanaal` (Platform Administrator only). 
         * @summary Move one of an organization\'s VECOZO streams between the portal and the berichtenservice
         * @param {string} id Organization ID
         * @param {VecozoStroom} stroom The stream family to move
         * @param {AdminSetVecozoKanaalRequest} adminSetVecozoKanaalRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async setVecozoKanaal(id: string, stroom: VecozoStroom, adminSetVecozoKanaalRequest: AdminSetVecozoKanaalRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminOrganizationDetailDto>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.setVecozoKanaal(id, stroom, adminSetVecozoKanaalRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminOrganizationsApi.setVecozoKanaal']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Suspends an organization, preventing all users from accessing the platform.  Suspended organizations: - Users cannot log in - API calls are rejected with 403 - Data is preserved (not deleted)  **Scoping:** - Platform admins can suspend any organization - Support agents can only suspend organizations assigned to their support partner  Requires `admin.organizations.manage` permission. 
         * @summary Suspend an organization
         * @param {string} id Organization ID
         * @param {AdminSuspendOrganizationRequest} adminSuspendOrganizationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async suspendOrganization(id: string, adminSuspendOrganizationRequest: AdminSuspendOrganizationRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminOrganizationDto>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.suspendOrganization(id, adminSuspendOrganizationRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminOrganizationsApi.suspendOrganization']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Records the organization\'s tenaamstelling, T.a.v. line, billing e-mail address and BTW-identificatienummer — what a Scrivio invoice to this practice renders in its Factuuradres block (#4448).  The practice maintains the same four fields itself through `PUT /api/v1/organizations/me`. This is the admin portal\'s door onto them, for a practice that has not filled them in and an invoice that has to go out: it writes them through the same normalisation, so an admin and a praktijkhouder typing the same thing store the same thing.  Every property is optional: omit one to leave it alone, send null to clear it. The change is written to the admin audit log with the previous and new values.  Requires `admin.organizations.edit` permission — the same one that schedules a license change or suspends an organization. No new permission bit. 
         * @summary Set who a Scrivio invoice to this organization is addressed to
         * @param {string} id Organization ID
         * @param {AdminUpdateOrganizationInvoiceDetailsRequest} adminUpdateOrganizationInvoiceDetailsRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateOrganizationInvoiceDetails(id: string, adminUpdateOrganizationInvoiceDetailsRequest: AdminUpdateOrganizationInvoiceDetailsRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminOrganizationDetailDto>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateOrganizationInvoiceDetails(id, adminUpdateOrganizationInvoiceDetailsRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminOrganizationsApi.updateOrganizationInvoiceDetails']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Records whether Scrivio made this organization for testing or demos (#4773). A `TEST` organization stays fully usable, but is left out of the admin dashboard\'s counts and the subscription portfolio, is never renewed and is sent no payment reminder.  A request that changes nothing writes no audit entry; a change is written to the admin audit log with the previous and new kind.  Requires `admin.organizations.edit` **and** `admin.subscriptions.manage`: the kind decides whether a practice is billed, which is the Platform Administrator\'s decision and not a support partner\'s — the same pair that creating an organization requires. 
         * @summary Mark an organization as a customer or a test organization
         * @param {string} id Organization ID
         * @param {AdminUpdateOrganizationKindRequest} adminUpdateOrganizationKindRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateOrganizationKind(id: string, adminUpdateOrganizationKindRequest: AdminUpdateOrganizationKindRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminOrganizationDetailDto>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateOrganizationKind(id, adminUpdateOrganizationKindRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminOrganizationsApi.updateOrganizationKind']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Chooses which member of the organization is its primary contact person (#4834), or clears it when `userId` is null or omitted. A user who is not a member of the organization is refused with a 400 — a membership that has since been disabled still counts, so the contact standing can always be re-chosen.  A request that changes nothing writes no audit entry; a change is written to the admin audit log with the previous and new user.  Requires `admin.organizations.edit` permission. The practice app neither shows nor changes the primary contact. 
         * @summary Set or clear an organization\'s primary contact
         * @param {string} id Organization ID
         * @param {AdminUpdateOrganizationPrimaryContactRequest} adminUpdateOrganizationPrimaryContactRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateOrganizationPrimaryContact(id: string, adminUpdateOrganizationPrimaryContactRequest: AdminUpdateOrganizationPrimaryContactRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminOrganizationDetailDto>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateOrganizationPrimaryContact(id, adminUpdateOrganizationPrimaryContactRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminOrganizationsApi.updateOrganizationPrimaryContact']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AdminOrganizationsApi - factory interface
 */
export const AdminOrganizationsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AdminOrganizationsApiFp(configuration)
    return {
        /**
         * Cancels a license type change (past or future).  Cancelling a past change may affect the computed effective license type.  **Scoping:** - Platform admins can cancel changes for any organization - Support agents can only cancel changes for organizations assigned to their support partner  Requires `admin.organizations.manage` permission. 
         * @summary Cancel a license change
         * @param {string} id Organization ID
         * @param {string} changeId Scheduled license change ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        cancelLicenseChange(id: string, changeId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.cancelLicenseChange(id, changeId, options).then((request) => request(axios, basePath));
        },
        /**
         * Creates a new organization, invites its first beheerder and puts it on a plan.  **Process:** 1. Validates domain uniqueness 2. Creates the organization entity 3. Sets up default roles via OrganizationService.createDefaultRoles() 4. Creates and invites the initial admin user 5. Activates the subscription on the plan and agreed prices in `subscription` 6. Logs the action to admin audit log  All of it is one transaction: an organization is never left without a first beheerder or on an `UNMANAGED` subscription Scrivio collects nothing from (#4551).  Requires `admin.organizations.create` **and** `admin.subscriptions.manage`: creating an organization now agrees what the practice pays, which is the same decision activating a subscription is (V20260913184130). 
         * @summary Create a new organization
         * @param {AdminCreateOrganizationRequest} adminCreateOrganizationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createOrganization(adminCreateOrganizationRequest: AdminCreateOrganizationRequest, options?: RawAxiosRequestConfig): AxiosPromise<AdminOrganizationDto> {
            return localVarFp.createOrganization(adminCreateOrganizationRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns detailed information about a specific organization.  **Scoping:** - Platform admins can view any organization - Support agents can only view organizations assigned to their support partner  Requires `admin.organizations.view` permission. 
         * @summary Get organization details
         * @param {string} id Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getOrganization(id: string, options?: RawAxiosRequestConfig): AxiosPromise<AdminOrganizationDetailDto> {
            return localVarFp.getOrganization(id, options).then((request) => request(axios, basePath));
        },
        /**
         * Every invitation to the organization that is still `PENDING`, including those whose link has expired — an expired invitation stays pending until it is resent or revoked, and is the one an admin is most likely looking for (#4418). `expired` says which.  This is how an organization Scrivio created can be reached when its first beheerder never joined: nobody inside it can see or resend the invitation.  **Scoping:** support agents only see organizations assigned to their support partner.  Requires `admin.users.view` permission. 
         * @summary List an organization\'s pending invitations
         * @param {string} organizationId The organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listOrganizationInvitations(organizationId: string, options?: RawAxiosRequestConfig): AxiosPromise<AdminOrganizationInvitationListResponse> {
            return localVarFp.listOrganizationInvitations(organizationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns a paginated list of users within a specific organization.  **Scoping:** - Platform admins can view users in any organization - Support agents can only view users in organizations assigned to their support partner  **Privacy:** - Other organization names are not revealed; only the count of other organizations is shown  Requires `admin.users.view` permission. 
         * @summary List users in an organization
         * @param {string} organizationId The organization ID
         * @param {string} [search] Filter by name or email (partial match, case-insensitive)
         * @param {UserStatus} [status] Filter by user status
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listOrganizationUsers(organizationId: string, search?: string, status?: UserStatus, page?: number, size?: number, options?: RawAxiosRequestConfig): AxiosPromise<AdminOrganizationUserListResponse> {
            return localVarFp.listOrganizationUsers(organizationId, search, status, page, size, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns a paginated list of all organizations with optional filtering.  **Scoping:** - Platform admins see all organizations - Support agents see only organizations assigned to their support partner  Requires `admin.organizations.view` permission. 
         * @summary List all organizations
         * @param {string} [search] Filter by name, domain, or the primary contact\&#39;s email address or name (partial match, case-insensitive). An organization with no primary contact yet also matches on the address of a pending invitation (#4834). 
         * @param {LicenseType} [licenseType] Filter by license type
         * @param {OrganizationStatus} [status] Filter by organization status
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {OrganizationKind} [kind] Filter by organization kind; omit it to list customers and test organizations alike (#4773)
         * @param {AuthProvider} [authProvider] Filter by where the organization\&#39;s behandelaars sign in (ADR-045 §7)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listOrganizations(search?: string, licenseType?: LicenseType, status?: OrganizationStatus, page?: number, size?: number, kind?: OrganizationKind, authProvider?: AuthProvider, options?: RawAxiosRequestConfig): AxiosPromise<AdminOrganizationListResponse> {
            return localVarFp.listOrganizations(search, licenseType, status, page, size, kind, authProvider, options).then((request) => request(axios, basePath));
        },
        /**
         * Irreversibly deletes all operational data belonging to an organization (clients, care trajectories, appointments, documents, billing, signals, …). Reference data and the organization record itself are preserved.  **Scope — GDPR / end-of-contract only.** Use this strictly for end-of-contract decommissioning and GDPR right-to-erasure. It is NOT the way to correct incorrect billing or clear test data; use the scoped discard of not-yet-issued billing or the void + credit-note flow for issued billing instead.  This is a destructive, platform-super-admin-only operation:  - Pass `dryRun: true` to get a preview (per-table counts) without deleting anything. - A real run (`dryRun: false`) requires `confirmationName` to exactly match the   organization\'s name and a non-empty `reason` (logged to the admin audit log).  **Guardrails:** - An admin cannot purge an organization they themselves are a member of   (the org they would impersonate into). - Only an organization that has left — lifecycle `ENDED` or `CLOSED` — can be purged,   so never one Scrivio still bills. A real run on any other is refused (409); a dry run   still answers, with `purgeAllowed: false` and the `lifecycle` that refuses it. - A purge is taken only at the practice\'s own request: a real run records who asked   (`requestedBy`) and on which day (`requestedOn`) in the admin audit log, and is   refused (400) without them. A closed organization is still found here.  Requires `admin.organizations.purge-dossier` permission. 
         * @summary Permanently purge an organization\'s dossier data
         * @param {string} id Organization ID
         * @param {AdminPurgeDossierRequest} adminPurgeDossierRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        purgeDossier(id: string, adminPurgeDossierRequest: AdminPurgeDossierRequest, options?: RawAxiosRequestConfig): AxiosPromise<PurgeReportDto> {
            return localVarFp.purgeDossier(id, adminPurgeDossierRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Reactivates a previously suspended organization, restoring full access.  **Scoping:** - Platform admins can reactivate any organization - Support agents can only reactivate organizations assigned to their support partner  Requires `admin.organizations.manage` permission. 
         * @summary Reactivate a suspended organization
         * @param {string} id Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        reactivateOrganization(id: string, options?: RawAxiosRequestConfig): AxiosPromise<AdminOrganizationDto> {
            return localVarFp.reactivateOrganization(id, options).then((request) => request(axios, basePath));
        },
        /**
         * Sends the invitation email again with a fresh link and a new expiry window (#4418). The previous link stops working. Works on an expired invitation as well — that is what it is for — but not on one that has been accepted or revoked (409).  Recorded in the organization\'s audit log and in the admin audit log.  **Scoping:** support agents only reach organizations assigned to their support partner.  Requires `admin.organizations.edit` permission. 
         * @summary Resend a pending invitation
         * @param {string} organizationId The organization ID
         * @param {string} invitationId The invitation ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resendOrganizationInvitation(organizationId: string, invitationId: string, options?: RawAxiosRequestConfig): AxiosPromise<AdminOrganizationInvitationDto> {
            return localVarFp.resendOrganizationInvitation(organizationId, invitationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Schedules a license type change for an organization.  The change will take effect at midnight (server time) on the specified effective date. Past dates are allowed for correcting historical records. Only one change can be scheduled per effective date.  **Scoping:** - Platform admins can schedule changes for any organization - Support agents can only schedule changes for organizations assigned to their support partner  Requires `admin.organizations.manage` permission. 
         * @summary Schedule a license type change
         * @param {string} id Organization ID
         * @param {AdminScheduleLicenseChangeRequest} adminScheduleLicenseChangeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        scheduleLicenseChange(id: string, adminScheduleLicenseChangeRequest: AdminScheduleLicenseChangeRequest, options?: RawAxiosRequestConfig): AxiosPromise<ScheduledLicenseChangeDto> {
            return localVarFp.scheduleLicenseChange(id, adminScheduleLicenseChangeRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Sets where this organization\'s behandelaars sign in (ADR-045 §7). Every organization is on `FIREBASE` until this moves it; moving it back is the rollback, and returns its behandelaars to the Firebase accounts they had — nothing in Firebase is changed by either direction.  A request that changes nothing writes no audit entry; a change is written to the admin audit log with the previous and new provider.  Requires `admin.organizations.auth-provider`, held by the Platform Administrator alone: the switch is a cutover for every behandelaar of the organization at once. 
         * @summary Move an organization\'s behandelaars between Firebase and Keycloak sign-in
         * @param {string} id Organization ID
         * @param {AdminSetAuthProviderRequest} adminSetAuthProviderRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        setOrganizationAuthProvider(id: string, adminSetAuthProviderRequest: AdminSetAuthProviderRequest, options?: RawAxiosRequestConfig): AxiosPromise<AdminOrganizationDetailDto> {
            return localVarFp.setOrganizationAuthProvider(id, adminSetAuthProviderRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Chooses how one stream family travels for this organization (ADR-049 §2): through the practice\'s own export/import in VECOZO\'s portal, or sent and pulled by Scrivio over the deployment\'s VECOZO berichtenservice. Every organization starts on `PORTAAL` for both.  Set it to `BERICHTENSERVICE` only once VECOZO has granted the stream\'s toestemmingsverklaring for the organization\'s AGB — Declareren for `GDS801`, **both** `VSP-JW` and `Jeugdwet declaraties` for `IJW`. Scrivio cannot read that grant, and a stream moved without it has every message refused (`BRS03`).  Switching is a **cutover**: a message that arrived through the portal carries no envelope Scrivio saw, so it is still answered through the portal.  This is not the VECOZO environment, which is the deployment\'s (ADR-044).  `BERICHTENSERVICE` is refused with a 409 while this deployment has no berichtenservice connection configured (`berichtenserviceConfigured` on the organization). A request that changes nothing writes no audit entry; a change is written to the admin audit log with the stream and the previous and new channel.  Requires `admin.organizations.vecozo-kanaal` (Platform Administrator only). 
         * @summary Move one of an organization\'s VECOZO streams between the portal and the berichtenservice
         * @param {string} id Organization ID
         * @param {VecozoStroom} stroom The stream family to move
         * @param {AdminSetVecozoKanaalRequest} adminSetVecozoKanaalRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        setVecozoKanaal(id: string, stroom: VecozoStroom, adminSetVecozoKanaalRequest: AdminSetVecozoKanaalRequest, options?: RawAxiosRequestConfig): AxiosPromise<AdminOrganizationDetailDto> {
            return localVarFp.setVecozoKanaal(id, stroom, adminSetVecozoKanaalRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Suspends an organization, preventing all users from accessing the platform.  Suspended organizations: - Users cannot log in - API calls are rejected with 403 - Data is preserved (not deleted)  **Scoping:** - Platform admins can suspend any organization - Support agents can only suspend organizations assigned to their support partner  Requires `admin.organizations.manage` permission. 
         * @summary Suspend an organization
         * @param {string} id Organization ID
         * @param {AdminSuspendOrganizationRequest} adminSuspendOrganizationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        suspendOrganization(id: string, adminSuspendOrganizationRequest: AdminSuspendOrganizationRequest, options?: RawAxiosRequestConfig): AxiosPromise<AdminOrganizationDto> {
            return localVarFp.suspendOrganization(id, adminSuspendOrganizationRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Records the organization\'s tenaamstelling, T.a.v. line, billing e-mail address and BTW-identificatienummer — what a Scrivio invoice to this practice renders in its Factuuradres block (#4448).  The practice maintains the same four fields itself through `PUT /api/v1/organizations/me`. This is the admin portal\'s door onto them, for a practice that has not filled them in and an invoice that has to go out: it writes them through the same normalisation, so an admin and a praktijkhouder typing the same thing store the same thing.  Every property is optional: omit one to leave it alone, send null to clear it. The change is written to the admin audit log with the previous and new values.  Requires `admin.organizations.edit` permission — the same one that schedules a license change or suspends an organization. No new permission bit. 
         * @summary Set who a Scrivio invoice to this organization is addressed to
         * @param {string} id Organization ID
         * @param {AdminUpdateOrganizationInvoiceDetailsRequest} adminUpdateOrganizationInvoiceDetailsRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateOrganizationInvoiceDetails(id: string, adminUpdateOrganizationInvoiceDetailsRequest: AdminUpdateOrganizationInvoiceDetailsRequest, options?: RawAxiosRequestConfig): AxiosPromise<AdminOrganizationDetailDto> {
            return localVarFp.updateOrganizationInvoiceDetails(id, adminUpdateOrganizationInvoiceDetailsRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Records whether Scrivio made this organization for testing or demos (#4773). A `TEST` organization stays fully usable, but is left out of the admin dashboard\'s counts and the subscription portfolio, is never renewed and is sent no payment reminder.  A request that changes nothing writes no audit entry; a change is written to the admin audit log with the previous and new kind.  Requires `admin.organizations.edit` **and** `admin.subscriptions.manage`: the kind decides whether a practice is billed, which is the Platform Administrator\'s decision and not a support partner\'s — the same pair that creating an organization requires. 
         * @summary Mark an organization as a customer or a test organization
         * @param {string} id Organization ID
         * @param {AdminUpdateOrganizationKindRequest} adminUpdateOrganizationKindRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateOrganizationKind(id: string, adminUpdateOrganizationKindRequest: AdminUpdateOrganizationKindRequest, options?: RawAxiosRequestConfig): AxiosPromise<AdminOrganizationDetailDto> {
            return localVarFp.updateOrganizationKind(id, adminUpdateOrganizationKindRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Chooses which member of the organization is its primary contact person (#4834), or clears it when `userId` is null or omitted. A user who is not a member of the organization is refused with a 400 — a membership that has since been disabled still counts, so the contact standing can always be re-chosen.  A request that changes nothing writes no audit entry; a change is written to the admin audit log with the previous and new user.  Requires `admin.organizations.edit` permission. The practice app neither shows nor changes the primary contact. 
         * @summary Set or clear an organization\'s primary contact
         * @param {string} id Organization ID
         * @param {AdminUpdateOrganizationPrimaryContactRequest} adminUpdateOrganizationPrimaryContactRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateOrganizationPrimaryContact(id: string, adminUpdateOrganizationPrimaryContactRequest: AdminUpdateOrganizationPrimaryContactRequest, options?: RawAxiosRequestConfig): AxiosPromise<AdminOrganizationDetailDto> {
            return localVarFp.updateOrganizationPrimaryContact(id, adminUpdateOrganizationPrimaryContactRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AdminOrganizationsApi - object-oriented interface
 */
export class AdminOrganizationsApi extends BaseAPI {
    /**
     * Cancels a license type change (past or future).  Cancelling a past change may affect the computed effective license type.  **Scoping:** - Platform admins can cancel changes for any organization - Support agents can only cancel changes for organizations assigned to their support partner  Requires `admin.organizations.manage` permission. 
     * @summary Cancel a license change
     * @param {string} id Organization ID
     * @param {string} changeId Scheduled license change ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public cancelLicenseChange(id: string, changeId: string, options?: RawAxiosRequestConfig) {
        return AdminOrganizationsApiFp(this.configuration).cancelLicenseChange(id, changeId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Creates a new organization, invites its first beheerder and puts it on a plan.  **Process:** 1. Validates domain uniqueness 2. Creates the organization entity 3. Sets up default roles via OrganizationService.createDefaultRoles() 4. Creates and invites the initial admin user 5. Activates the subscription on the plan and agreed prices in `subscription` 6. Logs the action to admin audit log  All of it is one transaction: an organization is never left without a first beheerder or on an `UNMANAGED` subscription Scrivio collects nothing from (#4551).  Requires `admin.organizations.create` **and** `admin.subscriptions.manage`: creating an organization now agrees what the practice pays, which is the same decision activating a subscription is (V20260913184130). 
     * @summary Create a new organization
     * @param {AdminCreateOrganizationRequest} adminCreateOrganizationRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createOrganization(adminCreateOrganizationRequest: AdminCreateOrganizationRequest, options?: RawAxiosRequestConfig) {
        return AdminOrganizationsApiFp(this.configuration).createOrganization(adminCreateOrganizationRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns detailed information about a specific organization.  **Scoping:** - Platform admins can view any organization - Support agents can only view organizations assigned to their support partner  Requires `admin.organizations.view` permission. 
     * @summary Get organization details
     * @param {string} id Organization ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getOrganization(id: string, options?: RawAxiosRequestConfig) {
        return AdminOrganizationsApiFp(this.configuration).getOrganization(id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Every invitation to the organization that is still `PENDING`, including those whose link has expired — an expired invitation stays pending until it is resent or revoked, and is the one an admin is most likely looking for (#4418). `expired` says which.  This is how an organization Scrivio created can be reached when its first beheerder never joined: nobody inside it can see or resend the invitation.  **Scoping:** support agents only see organizations assigned to their support partner.  Requires `admin.users.view` permission. 
     * @summary List an organization\'s pending invitations
     * @param {string} organizationId The organization ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listOrganizationInvitations(organizationId: string, options?: RawAxiosRequestConfig) {
        return AdminOrganizationsApiFp(this.configuration).listOrganizationInvitations(organizationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns a paginated list of users within a specific organization.  **Scoping:** - Platform admins can view users in any organization - Support agents can only view users in organizations assigned to their support partner  **Privacy:** - Other organization names are not revealed; only the count of other organizations is shown  Requires `admin.users.view` permission. 
     * @summary List users in an organization
     * @param {string} organizationId The organization ID
     * @param {string} [search] Filter by name or email (partial match, case-insensitive)
     * @param {UserStatus} [status] Filter by user status
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Page size
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listOrganizationUsers(organizationId: string, search?: string, status?: UserStatus, page?: number, size?: number, options?: RawAxiosRequestConfig) {
        return AdminOrganizationsApiFp(this.configuration).listOrganizationUsers(organizationId, search, status, page, size, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns a paginated list of all organizations with optional filtering.  **Scoping:** - Platform admins see all organizations - Support agents see only organizations assigned to their support partner  Requires `admin.organizations.view` permission. 
     * @summary List all organizations
     * @param {string} [search] Filter by name, domain, or the primary contact\&#39;s email address or name (partial match, case-insensitive). An organization with no primary contact yet also matches on the address of a pending invitation (#4834). 
     * @param {LicenseType} [licenseType] Filter by license type
     * @param {OrganizationStatus} [status] Filter by organization status
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Page size
     * @param {OrganizationKind} [kind] Filter by organization kind; omit it to list customers and test organizations alike (#4773)
     * @param {AuthProvider} [authProvider] Filter by where the organization\&#39;s behandelaars sign in (ADR-045 §7)
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listOrganizations(search?: string, licenseType?: LicenseType, status?: OrganizationStatus, page?: number, size?: number, kind?: OrganizationKind, authProvider?: AuthProvider, options?: RawAxiosRequestConfig) {
        return AdminOrganizationsApiFp(this.configuration).listOrganizations(search, licenseType, status, page, size, kind, authProvider, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Irreversibly deletes all operational data belonging to an organization (clients, care trajectories, appointments, documents, billing, signals, …). Reference data and the organization record itself are preserved.  **Scope — GDPR / end-of-contract only.** Use this strictly for end-of-contract decommissioning and GDPR right-to-erasure. It is NOT the way to correct incorrect billing or clear test data; use the scoped discard of not-yet-issued billing or the void + credit-note flow for issued billing instead.  This is a destructive, platform-super-admin-only operation:  - Pass `dryRun: true` to get a preview (per-table counts) without deleting anything. - A real run (`dryRun: false`) requires `confirmationName` to exactly match the   organization\'s name and a non-empty `reason` (logged to the admin audit log).  **Guardrails:** - An admin cannot purge an organization they themselves are a member of   (the org they would impersonate into). - Only an organization that has left — lifecycle `ENDED` or `CLOSED` — can be purged,   so never one Scrivio still bills. A real run on any other is refused (409); a dry run   still answers, with `purgeAllowed: false` and the `lifecycle` that refuses it. - A purge is taken only at the practice\'s own request: a real run records who asked   (`requestedBy`) and on which day (`requestedOn`) in the admin audit log, and is   refused (400) without them. A closed organization is still found here.  Requires `admin.organizations.purge-dossier` permission. 
     * @summary Permanently purge an organization\'s dossier data
     * @param {string} id Organization ID
     * @param {AdminPurgeDossierRequest} adminPurgeDossierRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public purgeDossier(id: string, adminPurgeDossierRequest: AdminPurgeDossierRequest, options?: RawAxiosRequestConfig) {
        return AdminOrganizationsApiFp(this.configuration).purgeDossier(id, adminPurgeDossierRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Reactivates a previously suspended organization, restoring full access.  **Scoping:** - Platform admins can reactivate any organization - Support agents can only reactivate organizations assigned to their support partner  Requires `admin.organizations.manage` permission. 
     * @summary Reactivate a suspended organization
     * @param {string} id Organization ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public reactivateOrganization(id: string, options?: RawAxiosRequestConfig) {
        return AdminOrganizationsApiFp(this.configuration).reactivateOrganization(id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Sends the invitation email again with a fresh link and a new expiry window (#4418). The previous link stops working. Works on an expired invitation as well — that is what it is for — but not on one that has been accepted or revoked (409).  Recorded in the organization\'s audit log and in the admin audit log.  **Scoping:** support agents only reach organizations assigned to their support partner.  Requires `admin.organizations.edit` permission. 
     * @summary Resend a pending invitation
     * @param {string} organizationId The organization ID
     * @param {string} invitationId The invitation ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public resendOrganizationInvitation(organizationId: string, invitationId: string, options?: RawAxiosRequestConfig) {
        return AdminOrganizationsApiFp(this.configuration).resendOrganizationInvitation(organizationId, invitationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Schedules a license type change for an organization.  The change will take effect at midnight (server time) on the specified effective date. Past dates are allowed for correcting historical records. Only one change can be scheduled per effective date.  **Scoping:** - Platform admins can schedule changes for any organization - Support agents can only schedule changes for organizations assigned to their support partner  Requires `admin.organizations.manage` permission. 
     * @summary Schedule a license type change
     * @param {string} id Organization ID
     * @param {AdminScheduleLicenseChangeRequest} adminScheduleLicenseChangeRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public scheduleLicenseChange(id: string, adminScheduleLicenseChangeRequest: AdminScheduleLicenseChangeRequest, options?: RawAxiosRequestConfig) {
        return AdminOrganizationsApiFp(this.configuration).scheduleLicenseChange(id, adminScheduleLicenseChangeRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Sets where this organization\'s behandelaars sign in (ADR-045 §7). Every organization is on `FIREBASE` until this moves it; moving it back is the rollback, and returns its behandelaars to the Firebase accounts they had — nothing in Firebase is changed by either direction.  A request that changes nothing writes no audit entry; a change is written to the admin audit log with the previous and new provider.  Requires `admin.organizations.auth-provider`, held by the Platform Administrator alone: the switch is a cutover for every behandelaar of the organization at once. 
     * @summary Move an organization\'s behandelaars between Firebase and Keycloak sign-in
     * @param {string} id Organization ID
     * @param {AdminSetAuthProviderRequest} adminSetAuthProviderRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public setOrganizationAuthProvider(id: string, adminSetAuthProviderRequest: AdminSetAuthProviderRequest, options?: RawAxiosRequestConfig) {
        return AdminOrganizationsApiFp(this.configuration).setOrganizationAuthProvider(id, adminSetAuthProviderRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Chooses how one stream family travels for this organization (ADR-049 §2): through the practice\'s own export/import in VECOZO\'s portal, or sent and pulled by Scrivio over the deployment\'s VECOZO berichtenservice. Every organization starts on `PORTAAL` for both.  Set it to `BERICHTENSERVICE` only once VECOZO has granted the stream\'s toestemmingsverklaring for the organization\'s AGB — Declareren for `GDS801`, **both** `VSP-JW` and `Jeugdwet declaraties` for `IJW`. Scrivio cannot read that grant, and a stream moved without it has every message refused (`BRS03`).  Switching is a **cutover**: a message that arrived through the portal carries no envelope Scrivio saw, so it is still answered through the portal.  This is not the VECOZO environment, which is the deployment\'s (ADR-044).  `BERICHTENSERVICE` is refused with a 409 while this deployment has no berichtenservice connection configured (`berichtenserviceConfigured` on the organization). A request that changes nothing writes no audit entry; a change is written to the admin audit log with the stream and the previous and new channel.  Requires `admin.organizations.vecozo-kanaal` (Platform Administrator only). 
     * @summary Move one of an organization\'s VECOZO streams between the portal and the berichtenservice
     * @param {string} id Organization ID
     * @param {VecozoStroom} stroom The stream family to move
     * @param {AdminSetVecozoKanaalRequest} adminSetVecozoKanaalRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public setVecozoKanaal(id: string, stroom: VecozoStroom, adminSetVecozoKanaalRequest: AdminSetVecozoKanaalRequest, options?: RawAxiosRequestConfig) {
        return AdminOrganizationsApiFp(this.configuration).setVecozoKanaal(id, stroom, adminSetVecozoKanaalRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Suspends an organization, preventing all users from accessing the platform.  Suspended organizations: - Users cannot log in - API calls are rejected with 403 - Data is preserved (not deleted)  **Scoping:** - Platform admins can suspend any organization - Support agents can only suspend organizations assigned to their support partner  Requires `admin.organizations.manage` permission. 
     * @summary Suspend an organization
     * @param {string} id Organization ID
     * @param {AdminSuspendOrganizationRequest} adminSuspendOrganizationRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public suspendOrganization(id: string, adminSuspendOrganizationRequest: AdminSuspendOrganizationRequest, options?: RawAxiosRequestConfig) {
        return AdminOrganizationsApiFp(this.configuration).suspendOrganization(id, adminSuspendOrganizationRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Records the organization\'s tenaamstelling, T.a.v. line, billing e-mail address and BTW-identificatienummer — what a Scrivio invoice to this practice renders in its Factuuradres block (#4448).  The practice maintains the same four fields itself through `PUT /api/v1/organizations/me`. This is the admin portal\'s door onto them, for a practice that has not filled them in and an invoice that has to go out: it writes them through the same normalisation, so an admin and a praktijkhouder typing the same thing store the same thing.  Every property is optional: omit one to leave it alone, send null to clear it. The change is written to the admin audit log with the previous and new values.  Requires `admin.organizations.edit` permission — the same one that schedules a license change or suspends an organization. No new permission bit. 
     * @summary Set who a Scrivio invoice to this organization is addressed to
     * @param {string} id Organization ID
     * @param {AdminUpdateOrganizationInvoiceDetailsRequest} adminUpdateOrganizationInvoiceDetailsRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateOrganizationInvoiceDetails(id: string, adminUpdateOrganizationInvoiceDetailsRequest: AdminUpdateOrganizationInvoiceDetailsRequest, options?: RawAxiosRequestConfig) {
        return AdminOrganizationsApiFp(this.configuration).updateOrganizationInvoiceDetails(id, adminUpdateOrganizationInvoiceDetailsRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Records whether Scrivio made this organization for testing or demos (#4773). A `TEST` organization stays fully usable, but is left out of the admin dashboard\'s counts and the subscription portfolio, is never renewed and is sent no payment reminder.  A request that changes nothing writes no audit entry; a change is written to the admin audit log with the previous and new kind.  Requires `admin.organizations.edit` **and** `admin.subscriptions.manage`: the kind decides whether a practice is billed, which is the Platform Administrator\'s decision and not a support partner\'s — the same pair that creating an organization requires. 
     * @summary Mark an organization as a customer or a test organization
     * @param {string} id Organization ID
     * @param {AdminUpdateOrganizationKindRequest} adminUpdateOrganizationKindRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateOrganizationKind(id: string, adminUpdateOrganizationKindRequest: AdminUpdateOrganizationKindRequest, options?: RawAxiosRequestConfig) {
        return AdminOrganizationsApiFp(this.configuration).updateOrganizationKind(id, adminUpdateOrganizationKindRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Chooses which member of the organization is its primary contact person (#4834), or clears it when `userId` is null or omitted. A user who is not a member of the organization is refused with a 400 — a membership that has since been disabled still counts, so the contact standing can always be re-chosen.  A request that changes nothing writes no audit entry; a change is written to the admin audit log with the previous and new user.  Requires `admin.organizations.edit` permission. The practice app neither shows nor changes the primary contact. 
     * @summary Set or clear an organization\'s primary contact
     * @param {string} id Organization ID
     * @param {AdminUpdateOrganizationPrimaryContactRequest} adminUpdateOrganizationPrimaryContactRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateOrganizationPrimaryContact(id: string, adminUpdateOrganizationPrimaryContactRequest: AdminUpdateOrganizationPrimaryContactRequest, options?: RawAxiosRequestConfig) {
        return AdminOrganizationsApiFp(this.configuration).updateOrganizationPrimaryContact(id, adminUpdateOrganizationPrimaryContactRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * AdminSnelstartApi - axios parameter creator
 */
export const AdminSnelstartApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Deletes the key from Secret Manager, after which a `MAATWERK` connection reports the key as missing. Idempotent; a removal is audited without the value. Requires the `admin.snelstart.manage` permission. 
         * @summary Remove the SnelStart subscription key of an organization\'s maatwerk connection
         * @param {string} organizationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteAdminSnelStartSubscriptionKey: async (organizationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('deleteAdminSnelStartSubscriptionKey', 'organizationId', organizationId)
            const localVarPath = `/api/admin/organizations/{organizationId}/snelstart/subscription-key`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Which route the practice\'s SnelStart connection is on, and whether the maatwerk subscription key Scrivio holds for it is stored. Never a key. Requires the `admin.snelstart.manage` permission. 
         * @summary Get an organization\'s SnelStart connection, as an admin sees it
         * @param {string} organizationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAdminSnelStart: async (organizationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('getAdminSnelStart', 'organizationId', organizationId)
            const localVarPath = `/api/admin/organizations/{organizationId}/snelstart`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Stores, or replaces, the subscription key SnelStart issued to Scrivio\'s B2B-portal account for this one practice\'s administratie (a permanent maatwerksleutel). A `MAATWERK` connection sends it, and only it: it never falls back to the platform key, nor the reverse.  Written to Secret Manager; never returned. Setting and replacing it are recorded in the admin audit log without the value. Requires the `admin.snelstart.manage` permission. 
         * @summary Store the SnelStart subscription key for an organization\'s maatwerk connection
         * @param {string} organizationId 
         * @param {AdminSnelStartSubscriptionKeyRequest} adminSnelStartSubscriptionKeyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        storeAdminSnelStartSubscriptionKey: async (organizationId: string, adminSnelStartSubscriptionKeyRequest: AdminSnelStartSubscriptionKeyRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('storeAdminSnelStartSubscriptionKey', 'organizationId', organizationId)
            // verify required parameter 'adminSnelStartSubscriptionKeyRequest' is not null or undefined
            assertParamExists('storeAdminSnelStartSubscriptionKey', 'adminSnelStartSubscriptionKeyRequest', adminSnelStartSubscriptionKeyRequest)
            const localVarPath = `/api/admin/organizations/{organizationId}/snelstart/subscription-key`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(adminSnelStartSubscriptionKeyRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AdminSnelstartApi - functional programming interface
 */
export const AdminSnelstartApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AdminSnelstartApiAxiosParamCreator(configuration)
    return {
        /**
         * Deletes the key from Secret Manager, after which a `MAATWERK` connection reports the key as missing. Idempotent; a removal is audited without the value. Requires the `admin.snelstart.manage` permission. 
         * @summary Remove the SnelStart subscription key of an organization\'s maatwerk connection
         * @param {string} organizationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteAdminSnelStartSubscriptionKey(organizationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteAdminSnelStartSubscriptionKey(organizationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminSnelstartApi.deleteAdminSnelStartSubscriptionKey']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Which route the practice\'s SnelStart connection is on, and whether the maatwerk subscription key Scrivio holds for it is stored. Never a key. Requires the `admin.snelstart.manage` permission. 
         * @summary Get an organization\'s SnelStart connection, as an admin sees it
         * @param {string} organizationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getAdminSnelStart(organizationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminSnelStartResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getAdminSnelStart(organizationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminSnelstartApi.getAdminSnelStart']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Stores, or replaces, the subscription key SnelStart issued to Scrivio\'s B2B-portal account for this one practice\'s administratie (a permanent maatwerksleutel). A `MAATWERK` connection sends it, and only it: it never falls back to the platform key, nor the reverse.  Written to Secret Manager; never returned. Setting and replacing it are recorded in the admin audit log without the value. Requires the `admin.snelstart.manage` permission. 
         * @summary Store the SnelStart subscription key for an organization\'s maatwerk connection
         * @param {string} organizationId 
         * @param {AdminSnelStartSubscriptionKeyRequest} adminSnelStartSubscriptionKeyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async storeAdminSnelStartSubscriptionKey(organizationId: string, adminSnelStartSubscriptionKeyRequest: AdminSnelStartSubscriptionKeyRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminSnelStartResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.storeAdminSnelStartSubscriptionKey(organizationId, adminSnelStartSubscriptionKeyRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminSnelstartApi.storeAdminSnelStartSubscriptionKey']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AdminSnelstartApi - factory interface
 */
export const AdminSnelstartApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AdminSnelstartApiFp(configuration)
    return {
        /**
         * Deletes the key from Secret Manager, after which a `MAATWERK` connection reports the key as missing. Idempotent; a removal is audited without the value. Requires the `admin.snelstart.manage` permission. 
         * @summary Remove the SnelStart subscription key of an organization\'s maatwerk connection
         * @param {string} organizationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteAdminSnelStartSubscriptionKey(organizationId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteAdminSnelStartSubscriptionKey(organizationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Which route the practice\'s SnelStart connection is on, and whether the maatwerk subscription key Scrivio holds for it is stored. Never a key. Requires the `admin.snelstart.manage` permission. 
         * @summary Get an organization\'s SnelStart connection, as an admin sees it
         * @param {string} organizationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAdminSnelStart(organizationId: string, options?: RawAxiosRequestConfig): AxiosPromise<AdminSnelStartResponse> {
            return localVarFp.getAdminSnelStart(organizationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Stores, or replaces, the subscription key SnelStart issued to Scrivio\'s B2B-portal account for this one practice\'s administratie (a permanent maatwerksleutel). A `MAATWERK` connection sends it, and only it: it never falls back to the platform key, nor the reverse.  Written to Secret Manager; never returned. Setting and replacing it are recorded in the admin audit log without the value. Requires the `admin.snelstart.manage` permission. 
         * @summary Store the SnelStart subscription key for an organization\'s maatwerk connection
         * @param {string} organizationId 
         * @param {AdminSnelStartSubscriptionKeyRequest} adminSnelStartSubscriptionKeyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        storeAdminSnelStartSubscriptionKey(organizationId: string, adminSnelStartSubscriptionKeyRequest: AdminSnelStartSubscriptionKeyRequest, options?: RawAxiosRequestConfig): AxiosPromise<AdminSnelStartResponse> {
            return localVarFp.storeAdminSnelStartSubscriptionKey(organizationId, adminSnelStartSubscriptionKeyRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AdminSnelstartApi - object-oriented interface
 */
export class AdminSnelstartApi extends BaseAPI {
    /**
     * Deletes the key from Secret Manager, after which a `MAATWERK` connection reports the key as missing. Idempotent; a removal is audited without the value. Requires the `admin.snelstart.manage` permission. 
     * @summary Remove the SnelStart subscription key of an organization\'s maatwerk connection
     * @param {string} organizationId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteAdminSnelStartSubscriptionKey(organizationId: string, options?: RawAxiosRequestConfig) {
        return AdminSnelstartApiFp(this.configuration).deleteAdminSnelStartSubscriptionKey(organizationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Which route the practice\'s SnelStart connection is on, and whether the maatwerk subscription key Scrivio holds for it is stored. Never a key. Requires the `admin.snelstart.manage` permission. 
     * @summary Get an organization\'s SnelStart connection, as an admin sees it
     * @param {string} organizationId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getAdminSnelStart(organizationId: string, options?: RawAxiosRequestConfig) {
        return AdminSnelstartApiFp(this.configuration).getAdminSnelStart(organizationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Stores, or replaces, the subscription key SnelStart issued to Scrivio\'s B2B-portal account for this one practice\'s administratie (a permanent maatwerksleutel). A `MAATWERK` connection sends it, and only it: it never falls back to the platform key, nor the reverse.  Written to Secret Manager; never returned. Setting and replacing it are recorded in the admin audit log without the value. Requires the `admin.snelstart.manage` permission. 
     * @summary Store the SnelStart subscription key for an organization\'s maatwerk connection
     * @param {string} organizationId 
     * @param {AdminSnelStartSubscriptionKeyRequest} adminSnelStartSubscriptionKeyRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public storeAdminSnelStartSubscriptionKey(organizationId: string, adminSnelStartSubscriptionKeyRequest: AdminSnelStartSubscriptionKeyRequest, options?: RawAxiosRequestConfig) {
        return AdminSnelstartApiFp(this.configuration).storeAdminSnelStartSubscriptionKey(organizationId, adminSnelStartSubscriptionKeyRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * AdminSubscriptionBoekhoudingMailApi - axios parameter creator
 */
export const AdminSubscriptionBoekhoudingMailApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Scrivio B.V.\'s own bookkeeping inbox: every invoice Scrivio issues a practice is mailed there as a message of its own, PDF attached, beside the practice\'s mail. One address for the whole platform; `email` is null while none is set, and nothing is mailed. `mailEnabled` says whether this deployment sends it at all — off everywhere but production.  Not the practice-side boekhouding route, which runs the other way (ADR-042). Requires `admin.subscriptions.boekhouding-mail`, which only the Platform Administrator holds. 
         * @summary Where every issued subscription invoice is also mailed
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getSubscriptionBoekhoudingMail: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/admin/subscriptions/boekhouding-mail`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Sets the address, or clears it with a null or empty `email`. **Every invoice issued so far that has not had its bookkeeping copy yet goes out once** on the next morning\'s run — on the first set, that is every issued invoice of every billed practice. A changed address resends nothing.  Audited in the admin audit log as configured or not, without the address. Requires `admin.subscriptions.boekhouding-mail`. 
         * @summary Set or clear where every issued subscription invoice is also mailed
         * @param {SubscriptionBoekhoudingMailRequest} subscriptionBoekhoudingMailRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        setSubscriptionBoekhoudingMail: async (subscriptionBoekhoudingMailRequest: SubscriptionBoekhoudingMailRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'subscriptionBoekhoudingMailRequest' is not null or undefined
            assertParamExists('setSubscriptionBoekhoudingMail', 'subscriptionBoekhoudingMailRequest', subscriptionBoekhoudingMailRequest)
            const localVarPath = `/api/admin/subscriptions/boekhouding-mail`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(subscriptionBoekhoudingMailRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AdminSubscriptionBoekhoudingMailApi - functional programming interface
 */
export const AdminSubscriptionBoekhoudingMailApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AdminSubscriptionBoekhoudingMailApiAxiosParamCreator(configuration)
    return {
        /**
         * Scrivio B.V.\'s own bookkeeping inbox: every invoice Scrivio issues a practice is mailed there as a message of its own, PDF attached, beside the practice\'s mail. One address for the whole platform; `email` is null while none is set, and nothing is mailed. `mailEnabled` says whether this deployment sends it at all — off everywhere but production.  Not the practice-side boekhouding route, which runs the other way (ADR-042). Requires `admin.subscriptions.boekhouding-mail`, which only the Platform Administrator holds. 
         * @summary Where every issued subscription invoice is also mailed
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getSubscriptionBoekhoudingMail(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SubscriptionBoekhoudingMailResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getSubscriptionBoekhoudingMail(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminSubscriptionBoekhoudingMailApi.getSubscriptionBoekhoudingMail']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Sets the address, or clears it with a null or empty `email`. **Every invoice issued so far that has not had its bookkeeping copy yet goes out once** on the next morning\'s run — on the first set, that is every issued invoice of every billed practice. A changed address resends nothing.  Audited in the admin audit log as configured or not, without the address. Requires `admin.subscriptions.boekhouding-mail`. 
         * @summary Set or clear where every issued subscription invoice is also mailed
         * @param {SubscriptionBoekhoudingMailRequest} subscriptionBoekhoudingMailRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async setSubscriptionBoekhoudingMail(subscriptionBoekhoudingMailRequest: SubscriptionBoekhoudingMailRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SubscriptionBoekhoudingMailResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.setSubscriptionBoekhoudingMail(subscriptionBoekhoudingMailRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminSubscriptionBoekhoudingMailApi.setSubscriptionBoekhoudingMail']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AdminSubscriptionBoekhoudingMailApi - factory interface
 */
export const AdminSubscriptionBoekhoudingMailApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AdminSubscriptionBoekhoudingMailApiFp(configuration)
    return {
        /**
         * Scrivio B.V.\'s own bookkeeping inbox: every invoice Scrivio issues a practice is mailed there as a message of its own, PDF attached, beside the practice\'s mail. One address for the whole platform; `email` is null while none is set, and nothing is mailed. `mailEnabled` says whether this deployment sends it at all — off everywhere but production.  Not the practice-side boekhouding route, which runs the other way (ADR-042). Requires `admin.subscriptions.boekhouding-mail`, which only the Platform Administrator holds. 
         * @summary Where every issued subscription invoice is also mailed
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getSubscriptionBoekhoudingMail(options?: RawAxiosRequestConfig): AxiosPromise<SubscriptionBoekhoudingMailResponse> {
            return localVarFp.getSubscriptionBoekhoudingMail(options).then((request) => request(axios, basePath));
        },
        /**
         * Sets the address, or clears it with a null or empty `email`. **Every invoice issued so far that has not had its bookkeeping copy yet goes out once** on the next morning\'s run — on the first set, that is every issued invoice of every billed practice. A changed address resends nothing.  Audited in the admin audit log as configured or not, without the address. Requires `admin.subscriptions.boekhouding-mail`. 
         * @summary Set or clear where every issued subscription invoice is also mailed
         * @param {SubscriptionBoekhoudingMailRequest} subscriptionBoekhoudingMailRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        setSubscriptionBoekhoudingMail(subscriptionBoekhoudingMailRequest: SubscriptionBoekhoudingMailRequest, options?: RawAxiosRequestConfig): AxiosPromise<SubscriptionBoekhoudingMailResponse> {
            return localVarFp.setSubscriptionBoekhoudingMail(subscriptionBoekhoudingMailRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AdminSubscriptionBoekhoudingMailApi - object-oriented interface
 */
export class AdminSubscriptionBoekhoudingMailApi extends BaseAPI {
    /**
     * Scrivio B.V.\'s own bookkeeping inbox: every invoice Scrivio issues a practice is mailed there as a message of its own, PDF attached, beside the practice\'s mail. One address for the whole platform; `email` is null while none is set, and nothing is mailed. `mailEnabled` says whether this deployment sends it at all — off everywhere but production.  Not the practice-side boekhouding route, which runs the other way (ADR-042). Requires `admin.subscriptions.boekhouding-mail`, which only the Platform Administrator holds. 
     * @summary Where every issued subscription invoice is also mailed
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getSubscriptionBoekhoudingMail(options?: RawAxiosRequestConfig) {
        return AdminSubscriptionBoekhoudingMailApiFp(this.configuration).getSubscriptionBoekhoudingMail(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Sets the address, or clears it with a null or empty `email`. **Every invoice issued so far that has not had its bookkeeping copy yet goes out once** on the next morning\'s run — on the first set, that is every issued invoice of every billed practice. A changed address resends nothing.  Audited in the admin audit log as configured or not, without the address. Requires `admin.subscriptions.boekhouding-mail`. 
     * @summary Set or clear where every issued subscription invoice is also mailed
     * @param {SubscriptionBoekhoudingMailRequest} subscriptionBoekhoudingMailRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public setSubscriptionBoekhoudingMail(subscriptionBoekhoudingMailRequest: SubscriptionBoekhoudingMailRequest, options?: RawAxiosRequestConfig) {
        return AdminSubscriptionBoekhoudingMailApiFp(this.configuration).setSubscriptionBoekhoudingMail(subscriptionBoekhoudingMailRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * AdminSubscriptionDiscountsApi - axios parameter creator
 */
export const AdminSubscriptionDiscountsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * A dated percentage off what the practice pays Scrivio (#4476) — an introduction discount, goodwill, \"x days free\". One mechanism, the same one the self-signup trial uses.  - **Dated to the day.** `startsOn` and `endsOn` are both inclusive, and without `endsOn`   the discount is open-ended. Either may lie in the past or in the future. - **Also on a running subscription** — `ACTIVE`, `PAST_DUE` or `UNPAID`. Every period   computed after the grant is discounted for each day it overlaps, each grant taken off   what the grants before it left. - **An issued invoice is never changed.** A grant whose dates overlap a period that has   already been invoiced is accepted, and that invoice stands as issued: `affectedInvoices`   lists them. What the discount would have taken off them is credited on the **next**   invoice instead (#4612) — per day of overlap, at the amount those invoices charged — and   `creditEuro` states it. A credit worth more than the next invoice carries forward to the   one after; an invoice written off (`ONINBAAR`) is not credited.  Audited in the admin audit log. Requires `admin.subscriptions.manage`, which only the Platform Administrator holds. 
         * @summary Grant a discount on an organization\'s subscription
         * @param {string} organizationId Organization ID
         * @param {SubscriptionDiscountGrantRequest} subscriptionDiscountGrantRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        grantSubscriptionDiscount: async (organizationId: string, subscriptionDiscountGrantRequest: SubscriptionDiscountGrantRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('grantSubscriptionDiscount', 'organizationId', organizationId)
            // verify required parameter 'subscriptionDiscountGrantRequest' is not null or undefined
            assertParamExists('grantSubscriptionDiscount', 'subscriptionDiscountGrantRequest', subscriptionDiscountGrantRequest)
            const localVarPath = `/api/admin/organizations/{organizationId}/subscription/discounts`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(subscriptionDiscountGrantRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Stops a discount applying (#4476). The discount is **kept**, with the moment it was revoked and the admin who revoked it: an invoice already issued under it must go on resolving it.  Every period computed after the revocation is no longer discounted by it; a period computed before it, and the invoice issued from that period, is not rewritten. The same holds for a credit the discount owed on earlier invoices (#4612): what no invoice has carried yet is withdrawn, and a credit already on an issued invoice stays there.  Audited in the admin audit log. Requires `admin.subscriptions.manage`, which only the Platform Administrator holds. 
         * @summary Revoke a discount on an organization\'s subscription
         * @param {string} organizationId Organization ID
         * @param {string} discountId A discount on this organization\&#39;s subscription
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        revokeSubscriptionDiscount: async (organizationId: string, discountId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('revokeSubscriptionDiscount', 'organizationId', organizationId)
            // verify required parameter 'discountId' is not null or undefined
            assertParamExists('revokeSubscriptionDiscount', 'discountId', discountId)
            const localVarPath = `/api/admin/organizations/{organizationId}/subscription/discounts/{discountId}/revocation`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)))
                .replace(`{${"discountId"}}`, encodeURIComponent(String(discountId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AdminSubscriptionDiscountsApi - functional programming interface
 */
export const AdminSubscriptionDiscountsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AdminSubscriptionDiscountsApiAxiosParamCreator(configuration)
    return {
        /**
         * A dated percentage off what the practice pays Scrivio (#4476) — an introduction discount, goodwill, \"x days free\". One mechanism, the same one the self-signup trial uses.  - **Dated to the day.** `startsOn` and `endsOn` are both inclusive, and without `endsOn`   the discount is open-ended. Either may lie in the past or in the future. - **Also on a running subscription** — `ACTIVE`, `PAST_DUE` or `UNPAID`. Every period   computed after the grant is discounted for each day it overlaps, each grant taken off   what the grants before it left. - **An issued invoice is never changed.** A grant whose dates overlap a period that has   already been invoiced is accepted, and that invoice stands as issued: `affectedInvoices`   lists them. What the discount would have taken off them is credited on the **next**   invoice instead (#4612) — per day of overlap, at the amount those invoices charged — and   `creditEuro` states it. A credit worth more than the next invoice carries forward to the   one after; an invoice written off (`ONINBAAR`) is not credited.  Audited in the admin audit log. Requires `admin.subscriptions.manage`, which only the Platform Administrator holds. 
         * @summary Grant a discount on an organization\'s subscription
         * @param {string} organizationId Organization ID
         * @param {SubscriptionDiscountGrantRequest} subscriptionDiscountGrantRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async grantSubscriptionDiscount(organizationId: string, subscriptionDiscountGrantRequest: SubscriptionDiscountGrantRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SubscriptionDiscountGrantResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.grantSubscriptionDiscount(organizationId, subscriptionDiscountGrantRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminSubscriptionDiscountsApi.grantSubscriptionDiscount']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Stops a discount applying (#4476). The discount is **kept**, with the moment it was revoked and the admin who revoked it: an invoice already issued under it must go on resolving it.  Every period computed after the revocation is no longer discounted by it; a period computed before it, and the invoice issued from that period, is not rewritten. The same holds for a credit the discount owed on earlier invoices (#4612): what no invoice has carried yet is withdrawn, and a credit already on an issued invoice stays there.  Audited in the admin audit log. Requires `admin.subscriptions.manage`, which only the Platform Administrator holds. 
         * @summary Revoke a discount on an organization\'s subscription
         * @param {string} organizationId Organization ID
         * @param {string} discountId A discount on this organization\&#39;s subscription
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async revokeSubscriptionDiscount(organizationId: string, discountId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SubscriptionAdminDiscountResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.revokeSubscriptionDiscount(organizationId, discountId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminSubscriptionDiscountsApi.revokeSubscriptionDiscount']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AdminSubscriptionDiscountsApi - factory interface
 */
export const AdminSubscriptionDiscountsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AdminSubscriptionDiscountsApiFp(configuration)
    return {
        /**
         * A dated percentage off what the practice pays Scrivio (#4476) — an introduction discount, goodwill, \"x days free\". One mechanism, the same one the self-signup trial uses.  - **Dated to the day.** `startsOn` and `endsOn` are both inclusive, and without `endsOn`   the discount is open-ended. Either may lie in the past or in the future. - **Also on a running subscription** — `ACTIVE`, `PAST_DUE` or `UNPAID`. Every period   computed after the grant is discounted for each day it overlaps, each grant taken off   what the grants before it left. - **An issued invoice is never changed.** A grant whose dates overlap a period that has   already been invoiced is accepted, and that invoice stands as issued: `affectedInvoices`   lists them. What the discount would have taken off them is credited on the **next**   invoice instead (#4612) — per day of overlap, at the amount those invoices charged — and   `creditEuro` states it. A credit worth more than the next invoice carries forward to the   one after; an invoice written off (`ONINBAAR`) is not credited.  Audited in the admin audit log. Requires `admin.subscriptions.manage`, which only the Platform Administrator holds. 
         * @summary Grant a discount on an organization\'s subscription
         * @param {string} organizationId Organization ID
         * @param {SubscriptionDiscountGrantRequest} subscriptionDiscountGrantRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        grantSubscriptionDiscount(organizationId: string, subscriptionDiscountGrantRequest: SubscriptionDiscountGrantRequest, options?: RawAxiosRequestConfig): AxiosPromise<SubscriptionDiscountGrantResponse> {
            return localVarFp.grantSubscriptionDiscount(organizationId, subscriptionDiscountGrantRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Stops a discount applying (#4476). The discount is **kept**, with the moment it was revoked and the admin who revoked it: an invoice already issued under it must go on resolving it.  Every period computed after the revocation is no longer discounted by it; a period computed before it, and the invoice issued from that period, is not rewritten. The same holds for a credit the discount owed on earlier invoices (#4612): what no invoice has carried yet is withdrawn, and a credit already on an issued invoice stays there.  Audited in the admin audit log. Requires `admin.subscriptions.manage`, which only the Platform Administrator holds. 
         * @summary Revoke a discount on an organization\'s subscription
         * @param {string} organizationId Organization ID
         * @param {string} discountId A discount on this organization\&#39;s subscription
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        revokeSubscriptionDiscount(organizationId: string, discountId: string, options?: RawAxiosRequestConfig): AxiosPromise<SubscriptionAdminDiscountResponse> {
            return localVarFp.revokeSubscriptionDiscount(organizationId, discountId, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AdminSubscriptionDiscountsApi - object-oriented interface
 */
export class AdminSubscriptionDiscountsApi extends BaseAPI {
    /**
     * A dated percentage off what the practice pays Scrivio (#4476) — an introduction discount, goodwill, \"x days free\". One mechanism, the same one the self-signup trial uses.  - **Dated to the day.** `startsOn` and `endsOn` are both inclusive, and without `endsOn`   the discount is open-ended. Either may lie in the past or in the future. - **Also on a running subscription** — `ACTIVE`, `PAST_DUE` or `UNPAID`. Every period   computed after the grant is discounted for each day it overlaps, each grant taken off   what the grants before it left. - **An issued invoice is never changed.** A grant whose dates overlap a period that has   already been invoiced is accepted, and that invoice stands as issued: `affectedInvoices`   lists them. What the discount would have taken off them is credited on the **next**   invoice instead (#4612) — per day of overlap, at the amount those invoices charged — and   `creditEuro` states it. A credit worth more than the next invoice carries forward to the   one after; an invoice written off (`ONINBAAR`) is not credited.  Audited in the admin audit log. Requires `admin.subscriptions.manage`, which only the Platform Administrator holds. 
     * @summary Grant a discount on an organization\'s subscription
     * @param {string} organizationId Organization ID
     * @param {SubscriptionDiscountGrantRequest} subscriptionDiscountGrantRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public grantSubscriptionDiscount(organizationId: string, subscriptionDiscountGrantRequest: SubscriptionDiscountGrantRequest, options?: RawAxiosRequestConfig) {
        return AdminSubscriptionDiscountsApiFp(this.configuration).grantSubscriptionDiscount(organizationId, subscriptionDiscountGrantRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Stops a discount applying (#4476). The discount is **kept**, with the moment it was revoked and the admin who revoked it: an invoice already issued under it must go on resolving it.  Every period computed after the revocation is no longer discounted by it; a period computed before it, and the invoice issued from that period, is not rewritten. The same holds for a credit the discount owed on earlier invoices (#4612): what no invoice has carried yet is withdrawn, and a credit already on an issued invoice stays there.  Audited in the admin audit log. Requires `admin.subscriptions.manage`, which only the Platform Administrator holds. 
     * @summary Revoke a discount on an organization\'s subscription
     * @param {string} organizationId Organization ID
     * @param {string} discountId A discount on this organization\&#39;s subscription
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public revokeSubscriptionDiscount(organizationId: string, discountId: string, options?: RawAxiosRequestConfig) {
        return AdminSubscriptionDiscountsApiFp(this.configuration).revokeSubscriptionDiscount(organizationId, discountId, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * AdminSubscriptionsApi - axios parameter creator
 */
export const AdminSubscriptionsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * The cutover for a practice Scrivio has invoiced by hand (#4475): from the activation date on, Scrivio computes and invoices what it owes. The same call puts a `CANCELED` subscription back on a plan (#4831).  - **The price is a snapshot.** Each seat type is charged the price stated in `seatPrices`,   or the plan\'s price today where the request names none. It is copied onto the   subscription, so a later change to the plan\'s price never reprices this practice. - **Never backdated.** `activationDate` may not be before today (Amsterdam): no practice   can be handed an invoice for a month it was never told it was paying for. - The subscription becomes `ACTIVE`, and its first period runs from `activationDate`   through the last day of that month, priced by day.  - **Reactivating a `CANCELED` subscription** replaces its previous seat prices, starts the   first period no earlier than `firstUnbilledDay` — a practice is never billed twice for one   day — and lifts the organization\'s `CANCELED` read-only gate. A suspension, or an invoice   from before the cancellation that is still overdue, keeps it read-only. Audited as   `REACTIVATED`.  Only an `UNMANAGED` or `CANCELED` subscription can be activated (409 otherwise); repricing one that is already on a plan is not something this endpoint does. The plan must sell the licence the organization holds. Audited in the admin audit log.  Requires `admin.subscriptions.manage`, which only the Platform Administrator holds. 
         * @summary Put an UNMANAGED or CANCELED organization on a plan and activate its subscription
         * @param {string} organizationId Organization ID
         * @param {SubscriptionActivationRequest} subscriptionActivationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activateOrganizationSubscription: async (organizationId: string, subscriptionActivationRequest: SubscriptionActivationRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('activateOrganizationSubscription', 'organizationId', organizationId)
            // verify required parameter 'subscriptionActivationRequest' is not null or undefined
            assertParamExists('activateOrganizationSubscription', 'subscriptionActivationRequest', subscriptionActivationRequest)
            const localVarPath = `/api/admin/organizations/{organizationId}/subscription/activation`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(subscriptionActivationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The practice is leaving (#4708, #4902). The admin chooses with `immediate`:  - **`immediate: true` — ended today.** The subscription becomes `CANCELED`, every mandate   that could still be charged is withdrawn at Mollie (each audited as its own revocation;   a payment Mollie has already submitted is not cancelled), and the organization becomes   read-only with reason `CANCELED` — it keeps reading and exporting its records and   nothing is deleted. What is owed stays owed: the running period and every issued   invoice are untouched, and no later period is billed. - **`immediate: false` — the terms the practice itself gets** (`POST   /api/v1/subscription/cancellation`): a practice in its free trial is ended today as   above; any other is given notice to the end of the next calendar month (agreement   art. 9.1), billed through it, and `endsOn` says when.  Undoing an immediate cancellation is not something this API does. Audited in the admin audit log. Requires `admin.subscriptions.manage`, which only the Platform Administrator holds. 
         * @summary Cancel an organization\'s subscription — now, or on the practice\'s own terms
         * @param {string} organizationId Organization ID
         * @param {AdminSubscriptionCancellationRequest} adminSubscriptionCancellationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        cancelOrganizationSubscription: async (organizationId: string, adminSubscriptionCancellationRequest: AdminSubscriptionCancellationRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('cancelOrganizationSubscription', 'organizationId', organizationId)
            // verify required parameter 'adminSubscriptionCancellationRequest' is not null or undefined
            assertParamExists('cancelOrganizationSubscription', 'adminSubscriptionCancellationRequest', adminSubscriptionCancellationRequest)
            const localVarPath = `/api/admin/organizations/{organizationId}/subscription/cancellation`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(adminSubscriptionCancellationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Everything Scrivio has recorded about what this practice pays it (#4474, ADR-042): where the subscription stands, the plan and the seat price agreed at activation, the seats it occupies today, every discount it was ever granted, the period ledger with each period\'s lines, the invoices issued, the payments taken and the mandates held.  This is the opposite direction of money from the practice\'s own invoices to cliënten and insurers, which is why every schema here is prefixed `Subscription…`.  **There is no 404 for an organization without a subscription.** Every organization that predates subscription billing is `UNMANAGED` — invoiced by hand — and one whose row has not been recorded yet is reported the same way, exactly as the practice\'s own `GET /api/v1/subscription` does. A 404 means the organization itself does not exist.  Requires `admin.subscriptions.view`, which only the Platform Administrator holds: it exposes a customer\'s price, discounts and payment state. 
         * @summary One organization\'s subscription to Scrivio, with its whole ledger
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getOrganizationSubscription: async (organizationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('getOrganizationSubscription', 'organizationId', organizationId)
            const localVarPath = `/api/admin/organizations/{organizationId}/subscription`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * What Scrivio\'s own customers add up to today (#4474): the monthly recurring revenue, the seats sold, how many subscriptions stand in each status, the free trials that end in the next seven days and the invoices that are overdue.  Platform-wide by design. Requires `admin.subscriptions.view`, which only the Platform Administrator holds. 
         * @summary The portfolio of every practice\'s subscription
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getSubscriptionPortfolio: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/admin/subscriptions/portfolio`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * One row per practice that pays Scrivio today (#4774): its subscription is `ACTIVE` or `PAST_DUE`, and its organisation is active, not deleted and not a test organisation. A practice on a free trial is listed at zero, after the paying ones; the rest are ordered by name. Each row states what its monthly amount is made of — the behandelaren occupying its seats, the agreed seat price and the discounts applying today — and the totals are the sum of the rows. The portfolio\'s `monthlyRecurringRevenueEuro` is the same total.  Platform-wide by design. Requires `admin.subscriptions.view`, which only the Platform Administrator holds. 
         * @summary The recurring revenue Scrivio earns today, per practice
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getSubscriptionRevenue: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/admin/subscriptions/revenue`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Every plan in the price book that is still offered (#4475), with its seat prices as they stand today. What the admin portal pre-fills when putting a practice on a plan — the price an admin then agrees may differ, and it is that agreed price, not this one, that the subscription pays from then on.  A retired plan is never listed. Requires `admin.subscriptions.view`. 
         * @summary The plans an organization can be put on, at what they cost today
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listSubscriptionPlans: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/admin/subscriptions/plans`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * A practice in `INVOICE` mode pays by bank transfer, so someone at Scrivio records it (#4477). Moves the invoice from `VERSTUURD` to `BETAALD` with the day the money arrived.  Only a `VERSTUURD` invoice can be marked paid: a `CONCEPT` one has not reached the practice, and a `BETAALD` or `ONINBAAR` one is already settled. Anything else is a 409. Nothing is sent to Mollie — this records a payment Mollie never saw.  Audited in the admin audit log. Requires `admin.subscriptions.manage`. 
         * @summary Record that a sent subscription invoice was paid by bank transfer
         * @param {string} invoiceId Subscription invoice ID
         * @param {SubscriptionInvoiceMarkPaidRequest} subscriptionInvoiceMarkPaidRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        markSubscriptionInvoicePaid: async (invoiceId: string, subscriptionInvoiceMarkPaidRequest: SubscriptionInvoiceMarkPaidRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'invoiceId' is not null or undefined
            assertParamExists('markSubscriptionInvoicePaid', 'invoiceId', invoiceId)
            // verify required parameter 'subscriptionInvoiceMarkPaidRequest' is not null or undefined
            assertParamExists('markSubscriptionInvoicePaid', 'subscriptionInvoiceMarkPaidRequest', subscriptionInvoiceMarkPaidRequest)
            const localVarPath = `/api/admin/subscriptions/invoices/{invoiceId}/mark-paid`
                .replace(`{${"invoiceId"}}`, encodeURIComponent(String(invoiceId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(subscriptionInvoiceMarkPaidRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Mails the practice that Scrivio needs a new mandate (#4611). The mail links to the practice\'s own subscription screen, behind its own login, where the checkout is started — **it carries no checkout or payment link**, so nothing can be signed from the mail itself.  Accepted exactly where that screen offers the checkout: the subscription is `ACTIVE`, the practice chose `AUTOMATED`, and no mandate can be charged. Addressed to the billing email, then the organisation\'s email, then the member who created it.  Audited in the admin audit log; a mail that cannot be sent leaves no entry (503). Requires `admin.subscriptions.manage`, which only the Platform Administrator holds. 
         * @summary Ask a practice to give a new direct-debit mandate
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        requestSubscriptionMandate: async (organizationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('requestSubscriptionMandate', 'organizationId', organizationId)
            const localVarPath = `/api/admin/organizations/{organizationId}/subscription/mandate-requests`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Withdraws a mandate at Mollie and records it as revoked (#4611), so nothing further is collected under it. The mandate is **kept**, `INVALID` with the moment it was revoked: a payment already taken under it must go on resolving it.  How the next amount is collected is not changed here. A practice that chose `AUTOMATED` and has no other chargeable mandate is collected as `INVOICE` from now on, with `collectionFallbackReason` `MANDATE_REVOKED` — the same fallback a revocation at the bank produces.  Mollie is asked first; if it cannot be reached nothing is recorded (503). Audited in the admin audit log. Requires `admin.subscriptions.manage`, which only the Platform Administrator holds. 
         * @summary Revoke a practice\'s direct-debit mandate
         * @param {string} organizationId Organization ID
         * @param {string} mandateId A mandate on this organization\&#39;s subscription — Scrivio\&#39;s id, not Mollie\&#39;s
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        revokeSubscriptionMandate: async (organizationId: string, mandateId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('revokeSubscriptionMandate', 'organizationId', organizationId)
            // verify required parameter 'mandateId' is not null or undefined
            assertParamExists('revokeSubscriptionMandate', 'mandateId', mandateId)
            const localVarPath = `/api/admin/organizations/{organizationId}/subscription/mandates/{mandateId}/revocation`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)))
                .replace(`{${"mandateId"}}`, encodeURIComponent(String(mandateId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Records that Scrivio has given up collecting a sent invoice (#4477): moves it from `VERSTUURD` to `ONINBAAR`. The subscription\'s own status is not touched.  Only a `VERSTUURD` invoice can be written off; anything else is a 409. Nothing is sent to Mollie.  Audited in the admin audit log with the reason given. Requires `admin.subscriptions.manage`. 
         * @summary Write a sent subscription invoice off as uncollectable
         * @param {string} invoiceId Subscription invoice ID
         * @param {SubscriptionInvoiceWriteOffRequest} subscriptionInvoiceWriteOffRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        writeOffSubscriptionInvoice: async (invoiceId: string, subscriptionInvoiceWriteOffRequest: SubscriptionInvoiceWriteOffRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'invoiceId' is not null or undefined
            assertParamExists('writeOffSubscriptionInvoice', 'invoiceId', invoiceId)
            // verify required parameter 'subscriptionInvoiceWriteOffRequest' is not null or undefined
            assertParamExists('writeOffSubscriptionInvoice', 'subscriptionInvoiceWriteOffRequest', subscriptionInvoiceWriteOffRequest)
            const localVarPath = `/api/admin/subscriptions/invoices/{invoiceId}/write-off`
                .replace(`{${"invoiceId"}}`, encodeURIComponent(String(invoiceId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(subscriptionInvoiceWriteOffRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AdminSubscriptionsApi - functional programming interface
 */
export const AdminSubscriptionsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AdminSubscriptionsApiAxiosParamCreator(configuration)
    return {
        /**
         * The cutover for a practice Scrivio has invoiced by hand (#4475): from the activation date on, Scrivio computes and invoices what it owes. The same call puts a `CANCELED` subscription back on a plan (#4831).  - **The price is a snapshot.** Each seat type is charged the price stated in `seatPrices`,   or the plan\'s price today where the request names none. It is copied onto the   subscription, so a later change to the plan\'s price never reprices this practice. - **Never backdated.** `activationDate` may not be before today (Amsterdam): no practice   can be handed an invoice for a month it was never told it was paying for. - The subscription becomes `ACTIVE`, and its first period runs from `activationDate`   through the last day of that month, priced by day.  - **Reactivating a `CANCELED` subscription** replaces its previous seat prices, starts the   first period no earlier than `firstUnbilledDay` — a practice is never billed twice for one   day — and lifts the organization\'s `CANCELED` read-only gate. A suspension, or an invoice   from before the cancellation that is still overdue, keeps it read-only. Audited as   `REACTIVATED`.  Only an `UNMANAGED` or `CANCELED` subscription can be activated (409 otherwise); repricing one that is already on a plan is not something this endpoint does. The plan must sell the licence the organization holds. Audited in the admin audit log.  Requires `admin.subscriptions.manage`, which only the Platform Administrator holds. 
         * @summary Put an UNMANAGED or CANCELED organization on a plan and activate its subscription
         * @param {string} organizationId Organization ID
         * @param {SubscriptionActivationRequest} subscriptionActivationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async activateOrganizationSubscription(organizationId: string, subscriptionActivationRequest: SubscriptionActivationRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SubscriptionAdminDetailResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.activateOrganizationSubscription(organizationId, subscriptionActivationRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminSubscriptionsApi.activateOrganizationSubscription']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The practice is leaving (#4708, #4902). The admin chooses with `immediate`:  - **`immediate: true` — ended today.** The subscription becomes `CANCELED`, every mandate   that could still be charged is withdrawn at Mollie (each audited as its own revocation;   a payment Mollie has already submitted is not cancelled), and the organization becomes   read-only with reason `CANCELED` — it keeps reading and exporting its records and   nothing is deleted. What is owed stays owed: the running period and every issued   invoice are untouched, and no later period is billed. - **`immediate: false` — the terms the practice itself gets** (`POST   /api/v1/subscription/cancellation`): a practice in its free trial is ended today as   above; any other is given notice to the end of the next calendar month (agreement   art. 9.1), billed through it, and `endsOn` says when.  Undoing an immediate cancellation is not something this API does. Audited in the admin audit log. Requires `admin.subscriptions.manage`, which only the Platform Administrator holds. 
         * @summary Cancel an organization\'s subscription — now, or on the practice\'s own terms
         * @param {string} organizationId Organization ID
         * @param {AdminSubscriptionCancellationRequest} adminSubscriptionCancellationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async cancelOrganizationSubscription(organizationId: string, adminSubscriptionCancellationRequest: AdminSubscriptionCancellationRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SubscriptionAdminDetailResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.cancelOrganizationSubscription(organizationId, adminSubscriptionCancellationRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminSubscriptionsApi.cancelOrganizationSubscription']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Everything Scrivio has recorded about what this practice pays it (#4474, ADR-042): where the subscription stands, the plan and the seat price agreed at activation, the seats it occupies today, every discount it was ever granted, the period ledger with each period\'s lines, the invoices issued, the payments taken and the mandates held.  This is the opposite direction of money from the practice\'s own invoices to cliënten and insurers, which is why every schema here is prefixed `Subscription…`.  **There is no 404 for an organization without a subscription.** Every organization that predates subscription billing is `UNMANAGED` — invoiced by hand — and one whose row has not been recorded yet is reported the same way, exactly as the practice\'s own `GET /api/v1/subscription` does. A 404 means the organization itself does not exist.  Requires `admin.subscriptions.view`, which only the Platform Administrator holds: it exposes a customer\'s price, discounts and payment state. 
         * @summary One organization\'s subscription to Scrivio, with its whole ledger
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getOrganizationSubscription(organizationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SubscriptionAdminDetailResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getOrganizationSubscription(organizationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminSubscriptionsApi.getOrganizationSubscription']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * What Scrivio\'s own customers add up to today (#4474): the monthly recurring revenue, the seats sold, how many subscriptions stand in each status, the free trials that end in the next seven days and the invoices that are overdue.  Platform-wide by design. Requires `admin.subscriptions.view`, which only the Platform Administrator holds. 
         * @summary The portfolio of every practice\'s subscription
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getSubscriptionPortfolio(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SubscriptionPortfolioResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getSubscriptionPortfolio(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminSubscriptionsApi.getSubscriptionPortfolio']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * One row per practice that pays Scrivio today (#4774): its subscription is `ACTIVE` or `PAST_DUE`, and its organisation is active, not deleted and not a test organisation. A practice on a free trial is listed at zero, after the paying ones; the rest are ordered by name. Each row states what its monthly amount is made of — the behandelaren occupying its seats, the agreed seat price and the discounts applying today — and the totals are the sum of the rows. The portfolio\'s `monthlyRecurringRevenueEuro` is the same total.  Platform-wide by design. Requires `admin.subscriptions.view`, which only the Platform Administrator holds. 
         * @summary The recurring revenue Scrivio earns today, per practice
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getSubscriptionRevenue(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SubscriptionRevenueResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getSubscriptionRevenue(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminSubscriptionsApi.getSubscriptionRevenue']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Every plan in the price book that is still offered (#4475), with its seat prices as they stand today. What the admin portal pre-fills when putting a practice on a plan — the price an admin then agrees may differ, and it is that agreed price, not this one, that the subscription pays from then on.  A retired plan is never listed. Requires `admin.subscriptions.view`. 
         * @summary The plans an organization can be put on, at what they cost today
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listSubscriptionPlans(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<SubscriptionPlanResponse>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listSubscriptionPlans(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminSubscriptionsApi.listSubscriptionPlans']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * A practice in `INVOICE` mode pays by bank transfer, so someone at Scrivio records it (#4477). Moves the invoice from `VERSTUURD` to `BETAALD` with the day the money arrived.  Only a `VERSTUURD` invoice can be marked paid: a `CONCEPT` one has not reached the practice, and a `BETAALD` or `ONINBAAR` one is already settled. Anything else is a 409. Nothing is sent to Mollie — this records a payment Mollie never saw.  Audited in the admin audit log. Requires `admin.subscriptions.manage`. 
         * @summary Record that a sent subscription invoice was paid by bank transfer
         * @param {string} invoiceId Subscription invoice ID
         * @param {SubscriptionInvoiceMarkPaidRequest} subscriptionInvoiceMarkPaidRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async markSubscriptionInvoicePaid(invoiceId: string, subscriptionInvoiceMarkPaidRequest: SubscriptionInvoiceMarkPaidRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SubscriptionInvoiceSummaryResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.markSubscriptionInvoicePaid(invoiceId, subscriptionInvoiceMarkPaidRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminSubscriptionsApi.markSubscriptionInvoicePaid']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Mails the practice that Scrivio needs a new mandate (#4611). The mail links to the practice\'s own subscription screen, behind its own login, where the checkout is started — **it carries no checkout or payment link**, so nothing can be signed from the mail itself.  Accepted exactly where that screen offers the checkout: the subscription is `ACTIVE`, the practice chose `AUTOMATED`, and no mandate can be charged. Addressed to the billing email, then the organisation\'s email, then the member who created it.  Audited in the admin audit log; a mail that cannot be sent leaves no entry (503). Requires `admin.subscriptions.manage`, which only the Platform Administrator holds. 
         * @summary Ask a practice to give a new direct-debit mandate
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async requestSubscriptionMandate(organizationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.requestSubscriptionMandate(organizationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminSubscriptionsApi.requestSubscriptionMandate']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Withdraws a mandate at Mollie and records it as revoked (#4611), so nothing further is collected under it. The mandate is **kept**, `INVALID` with the moment it was revoked: a payment already taken under it must go on resolving it.  How the next amount is collected is not changed here. A practice that chose `AUTOMATED` and has no other chargeable mandate is collected as `INVOICE` from now on, with `collectionFallbackReason` `MANDATE_REVOKED` — the same fallback a revocation at the bank produces.  Mollie is asked first; if it cannot be reached nothing is recorded (503). Audited in the admin audit log. Requires `admin.subscriptions.manage`, which only the Platform Administrator holds. 
         * @summary Revoke a practice\'s direct-debit mandate
         * @param {string} organizationId Organization ID
         * @param {string} mandateId A mandate on this organization\&#39;s subscription — Scrivio\&#39;s id, not Mollie\&#39;s
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async revokeSubscriptionMandate(organizationId: string, mandateId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SubscriptionMandateResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.revokeSubscriptionMandate(organizationId, mandateId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminSubscriptionsApi.revokeSubscriptionMandate']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Records that Scrivio has given up collecting a sent invoice (#4477): moves it from `VERSTUURD` to `ONINBAAR`. The subscription\'s own status is not touched.  Only a `VERSTUURD` invoice can be written off; anything else is a 409. Nothing is sent to Mollie.  Audited in the admin audit log with the reason given. Requires `admin.subscriptions.manage`. 
         * @summary Write a sent subscription invoice off as uncollectable
         * @param {string} invoiceId Subscription invoice ID
         * @param {SubscriptionInvoiceWriteOffRequest} subscriptionInvoiceWriteOffRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async writeOffSubscriptionInvoice(invoiceId: string, subscriptionInvoiceWriteOffRequest: SubscriptionInvoiceWriteOffRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SubscriptionInvoiceSummaryResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.writeOffSubscriptionInvoice(invoiceId, subscriptionInvoiceWriteOffRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminSubscriptionsApi.writeOffSubscriptionInvoice']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AdminSubscriptionsApi - factory interface
 */
export const AdminSubscriptionsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AdminSubscriptionsApiFp(configuration)
    return {
        /**
         * The cutover for a practice Scrivio has invoiced by hand (#4475): from the activation date on, Scrivio computes and invoices what it owes. The same call puts a `CANCELED` subscription back on a plan (#4831).  - **The price is a snapshot.** Each seat type is charged the price stated in `seatPrices`,   or the plan\'s price today where the request names none. It is copied onto the   subscription, so a later change to the plan\'s price never reprices this practice. - **Never backdated.** `activationDate` may not be before today (Amsterdam): no practice   can be handed an invoice for a month it was never told it was paying for. - The subscription becomes `ACTIVE`, and its first period runs from `activationDate`   through the last day of that month, priced by day.  - **Reactivating a `CANCELED` subscription** replaces its previous seat prices, starts the   first period no earlier than `firstUnbilledDay` — a practice is never billed twice for one   day — and lifts the organization\'s `CANCELED` read-only gate. A suspension, or an invoice   from before the cancellation that is still overdue, keeps it read-only. Audited as   `REACTIVATED`.  Only an `UNMANAGED` or `CANCELED` subscription can be activated (409 otherwise); repricing one that is already on a plan is not something this endpoint does. The plan must sell the licence the organization holds. Audited in the admin audit log.  Requires `admin.subscriptions.manage`, which only the Platform Administrator holds. 
         * @summary Put an UNMANAGED or CANCELED organization on a plan and activate its subscription
         * @param {string} organizationId Organization ID
         * @param {SubscriptionActivationRequest} subscriptionActivationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activateOrganizationSubscription(organizationId: string, subscriptionActivationRequest: SubscriptionActivationRequest, options?: RawAxiosRequestConfig): AxiosPromise<SubscriptionAdminDetailResponse> {
            return localVarFp.activateOrganizationSubscription(organizationId, subscriptionActivationRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * The practice is leaving (#4708, #4902). The admin chooses with `immediate`:  - **`immediate: true` — ended today.** The subscription becomes `CANCELED`, every mandate   that could still be charged is withdrawn at Mollie (each audited as its own revocation;   a payment Mollie has already submitted is not cancelled), and the organization becomes   read-only with reason `CANCELED` — it keeps reading and exporting its records and   nothing is deleted. What is owed stays owed: the running period and every issued   invoice are untouched, and no later period is billed. - **`immediate: false` — the terms the practice itself gets** (`POST   /api/v1/subscription/cancellation`): a practice in its free trial is ended today as   above; any other is given notice to the end of the next calendar month (agreement   art. 9.1), billed through it, and `endsOn` says when.  Undoing an immediate cancellation is not something this API does. Audited in the admin audit log. Requires `admin.subscriptions.manage`, which only the Platform Administrator holds. 
         * @summary Cancel an organization\'s subscription — now, or on the practice\'s own terms
         * @param {string} organizationId Organization ID
         * @param {AdminSubscriptionCancellationRequest} adminSubscriptionCancellationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        cancelOrganizationSubscription(organizationId: string, adminSubscriptionCancellationRequest: AdminSubscriptionCancellationRequest, options?: RawAxiosRequestConfig): AxiosPromise<SubscriptionAdminDetailResponse> {
            return localVarFp.cancelOrganizationSubscription(organizationId, adminSubscriptionCancellationRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Everything Scrivio has recorded about what this practice pays it (#4474, ADR-042): where the subscription stands, the plan and the seat price agreed at activation, the seats it occupies today, every discount it was ever granted, the period ledger with each period\'s lines, the invoices issued, the payments taken and the mandates held.  This is the opposite direction of money from the practice\'s own invoices to cliënten and insurers, which is why every schema here is prefixed `Subscription…`.  **There is no 404 for an organization without a subscription.** Every organization that predates subscription billing is `UNMANAGED` — invoiced by hand — and one whose row has not been recorded yet is reported the same way, exactly as the practice\'s own `GET /api/v1/subscription` does. A 404 means the organization itself does not exist.  Requires `admin.subscriptions.view`, which only the Platform Administrator holds: it exposes a customer\'s price, discounts and payment state. 
         * @summary One organization\'s subscription to Scrivio, with its whole ledger
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getOrganizationSubscription(organizationId: string, options?: RawAxiosRequestConfig): AxiosPromise<SubscriptionAdminDetailResponse> {
            return localVarFp.getOrganizationSubscription(organizationId, options).then((request) => request(axios, basePath));
        },
        /**
         * What Scrivio\'s own customers add up to today (#4474): the monthly recurring revenue, the seats sold, how many subscriptions stand in each status, the free trials that end in the next seven days and the invoices that are overdue.  Platform-wide by design. Requires `admin.subscriptions.view`, which only the Platform Administrator holds. 
         * @summary The portfolio of every practice\'s subscription
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getSubscriptionPortfolio(options?: RawAxiosRequestConfig): AxiosPromise<SubscriptionPortfolioResponse> {
            return localVarFp.getSubscriptionPortfolio(options).then((request) => request(axios, basePath));
        },
        /**
         * One row per practice that pays Scrivio today (#4774): its subscription is `ACTIVE` or `PAST_DUE`, and its organisation is active, not deleted and not a test organisation. A practice on a free trial is listed at zero, after the paying ones; the rest are ordered by name. Each row states what its monthly amount is made of — the behandelaren occupying its seats, the agreed seat price and the discounts applying today — and the totals are the sum of the rows. The portfolio\'s `monthlyRecurringRevenueEuro` is the same total.  Platform-wide by design. Requires `admin.subscriptions.view`, which only the Platform Administrator holds. 
         * @summary The recurring revenue Scrivio earns today, per practice
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getSubscriptionRevenue(options?: RawAxiosRequestConfig): AxiosPromise<SubscriptionRevenueResponse> {
            return localVarFp.getSubscriptionRevenue(options).then((request) => request(axios, basePath));
        },
        /**
         * Every plan in the price book that is still offered (#4475), with its seat prices as they stand today. What the admin portal pre-fills when putting a practice on a plan — the price an admin then agrees may differ, and it is that agreed price, not this one, that the subscription pays from then on.  A retired plan is never listed. Requires `admin.subscriptions.view`. 
         * @summary The plans an organization can be put on, at what they cost today
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listSubscriptionPlans(options?: RawAxiosRequestConfig): AxiosPromise<Array<SubscriptionPlanResponse>> {
            return localVarFp.listSubscriptionPlans(options).then((request) => request(axios, basePath));
        },
        /**
         * A practice in `INVOICE` mode pays by bank transfer, so someone at Scrivio records it (#4477). Moves the invoice from `VERSTUURD` to `BETAALD` with the day the money arrived.  Only a `VERSTUURD` invoice can be marked paid: a `CONCEPT` one has not reached the practice, and a `BETAALD` or `ONINBAAR` one is already settled. Anything else is a 409. Nothing is sent to Mollie — this records a payment Mollie never saw.  Audited in the admin audit log. Requires `admin.subscriptions.manage`. 
         * @summary Record that a sent subscription invoice was paid by bank transfer
         * @param {string} invoiceId Subscription invoice ID
         * @param {SubscriptionInvoiceMarkPaidRequest} subscriptionInvoiceMarkPaidRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        markSubscriptionInvoicePaid(invoiceId: string, subscriptionInvoiceMarkPaidRequest: SubscriptionInvoiceMarkPaidRequest, options?: RawAxiosRequestConfig): AxiosPromise<SubscriptionInvoiceSummaryResponse> {
            return localVarFp.markSubscriptionInvoicePaid(invoiceId, subscriptionInvoiceMarkPaidRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Mails the practice that Scrivio needs a new mandate (#4611). The mail links to the practice\'s own subscription screen, behind its own login, where the checkout is started — **it carries no checkout or payment link**, so nothing can be signed from the mail itself.  Accepted exactly where that screen offers the checkout: the subscription is `ACTIVE`, the practice chose `AUTOMATED`, and no mandate can be charged. Addressed to the billing email, then the organisation\'s email, then the member who created it.  Audited in the admin audit log; a mail that cannot be sent leaves no entry (503). Requires `admin.subscriptions.manage`, which only the Platform Administrator holds. 
         * @summary Ask a practice to give a new direct-debit mandate
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        requestSubscriptionMandate(organizationId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.requestSubscriptionMandate(organizationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Withdraws a mandate at Mollie and records it as revoked (#4611), so nothing further is collected under it. The mandate is **kept**, `INVALID` with the moment it was revoked: a payment already taken under it must go on resolving it.  How the next amount is collected is not changed here. A practice that chose `AUTOMATED` and has no other chargeable mandate is collected as `INVOICE` from now on, with `collectionFallbackReason` `MANDATE_REVOKED` — the same fallback a revocation at the bank produces.  Mollie is asked first; if it cannot be reached nothing is recorded (503). Audited in the admin audit log. Requires `admin.subscriptions.manage`, which only the Platform Administrator holds. 
         * @summary Revoke a practice\'s direct-debit mandate
         * @param {string} organizationId Organization ID
         * @param {string} mandateId A mandate on this organization\&#39;s subscription — Scrivio\&#39;s id, not Mollie\&#39;s
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        revokeSubscriptionMandate(organizationId: string, mandateId: string, options?: RawAxiosRequestConfig): AxiosPromise<SubscriptionMandateResponse> {
            return localVarFp.revokeSubscriptionMandate(organizationId, mandateId, options).then((request) => request(axios, basePath));
        },
        /**
         * Records that Scrivio has given up collecting a sent invoice (#4477): moves it from `VERSTUURD` to `ONINBAAR`. The subscription\'s own status is not touched.  Only a `VERSTUURD` invoice can be written off; anything else is a 409. Nothing is sent to Mollie.  Audited in the admin audit log with the reason given. Requires `admin.subscriptions.manage`. 
         * @summary Write a sent subscription invoice off as uncollectable
         * @param {string} invoiceId Subscription invoice ID
         * @param {SubscriptionInvoiceWriteOffRequest} subscriptionInvoiceWriteOffRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        writeOffSubscriptionInvoice(invoiceId: string, subscriptionInvoiceWriteOffRequest: SubscriptionInvoiceWriteOffRequest, options?: RawAxiosRequestConfig): AxiosPromise<SubscriptionInvoiceSummaryResponse> {
            return localVarFp.writeOffSubscriptionInvoice(invoiceId, subscriptionInvoiceWriteOffRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AdminSubscriptionsApi - object-oriented interface
 */
export class AdminSubscriptionsApi extends BaseAPI {
    /**
     * The cutover for a practice Scrivio has invoiced by hand (#4475): from the activation date on, Scrivio computes and invoices what it owes. The same call puts a `CANCELED` subscription back on a plan (#4831).  - **The price is a snapshot.** Each seat type is charged the price stated in `seatPrices`,   or the plan\'s price today where the request names none. It is copied onto the   subscription, so a later change to the plan\'s price never reprices this practice. - **Never backdated.** `activationDate` may not be before today (Amsterdam): no practice   can be handed an invoice for a month it was never told it was paying for. - The subscription becomes `ACTIVE`, and its first period runs from `activationDate`   through the last day of that month, priced by day.  - **Reactivating a `CANCELED` subscription** replaces its previous seat prices, starts the   first period no earlier than `firstUnbilledDay` — a practice is never billed twice for one   day — and lifts the organization\'s `CANCELED` read-only gate. A suspension, or an invoice   from before the cancellation that is still overdue, keeps it read-only. Audited as   `REACTIVATED`.  Only an `UNMANAGED` or `CANCELED` subscription can be activated (409 otherwise); repricing one that is already on a plan is not something this endpoint does. The plan must sell the licence the organization holds. Audited in the admin audit log.  Requires `admin.subscriptions.manage`, which only the Platform Administrator holds. 
     * @summary Put an UNMANAGED or CANCELED organization on a plan and activate its subscription
     * @param {string} organizationId Organization ID
     * @param {SubscriptionActivationRequest} subscriptionActivationRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public activateOrganizationSubscription(organizationId: string, subscriptionActivationRequest: SubscriptionActivationRequest, options?: RawAxiosRequestConfig) {
        return AdminSubscriptionsApiFp(this.configuration).activateOrganizationSubscription(organizationId, subscriptionActivationRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The practice is leaving (#4708, #4902). The admin chooses with `immediate`:  - **`immediate: true` — ended today.** The subscription becomes `CANCELED`, every mandate   that could still be charged is withdrawn at Mollie (each audited as its own revocation;   a payment Mollie has already submitted is not cancelled), and the organization becomes   read-only with reason `CANCELED` — it keeps reading and exporting its records and   nothing is deleted. What is owed stays owed: the running period and every issued   invoice are untouched, and no later period is billed. - **`immediate: false` — the terms the practice itself gets** (`POST   /api/v1/subscription/cancellation`): a practice in its free trial is ended today as   above; any other is given notice to the end of the next calendar month (agreement   art. 9.1), billed through it, and `endsOn` says when.  Undoing an immediate cancellation is not something this API does. Audited in the admin audit log. Requires `admin.subscriptions.manage`, which only the Platform Administrator holds. 
     * @summary Cancel an organization\'s subscription — now, or on the practice\'s own terms
     * @param {string} organizationId Organization ID
     * @param {AdminSubscriptionCancellationRequest} adminSubscriptionCancellationRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public cancelOrganizationSubscription(organizationId: string, adminSubscriptionCancellationRequest: AdminSubscriptionCancellationRequest, options?: RawAxiosRequestConfig) {
        return AdminSubscriptionsApiFp(this.configuration).cancelOrganizationSubscription(organizationId, adminSubscriptionCancellationRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Everything Scrivio has recorded about what this practice pays it (#4474, ADR-042): where the subscription stands, the plan and the seat price agreed at activation, the seats it occupies today, every discount it was ever granted, the period ledger with each period\'s lines, the invoices issued, the payments taken and the mandates held.  This is the opposite direction of money from the practice\'s own invoices to cliënten and insurers, which is why every schema here is prefixed `Subscription…`.  **There is no 404 for an organization without a subscription.** Every organization that predates subscription billing is `UNMANAGED` — invoiced by hand — and one whose row has not been recorded yet is reported the same way, exactly as the practice\'s own `GET /api/v1/subscription` does. A 404 means the organization itself does not exist.  Requires `admin.subscriptions.view`, which only the Platform Administrator holds: it exposes a customer\'s price, discounts and payment state. 
     * @summary One organization\'s subscription to Scrivio, with its whole ledger
     * @param {string} organizationId Organization ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getOrganizationSubscription(organizationId: string, options?: RawAxiosRequestConfig) {
        return AdminSubscriptionsApiFp(this.configuration).getOrganizationSubscription(organizationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * What Scrivio\'s own customers add up to today (#4474): the monthly recurring revenue, the seats sold, how many subscriptions stand in each status, the free trials that end in the next seven days and the invoices that are overdue.  Platform-wide by design. Requires `admin.subscriptions.view`, which only the Platform Administrator holds. 
     * @summary The portfolio of every practice\'s subscription
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getSubscriptionPortfolio(options?: RawAxiosRequestConfig) {
        return AdminSubscriptionsApiFp(this.configuration).getSubscriptionPortfolio(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * One row per practice that pays Scrivio today (#4774): its subscription is `ACTIVE` or `PAST_DUE`, and its organisation is active, not deleted and not a test organisation. A practice on a free trial is listed at zero, after the paying ones; the rest are ordered by name. Each row states what its monthly amount is made of — the behandelaren occupying its seats, the agreed seat price and the discounts applying today — and the totals are the sum of the rows. The portfolio\'s `monthlyRecurringRevenueEuro` is the same total.  Platform-wide by design. Requires `admin.subscriptions.view`, which only the Platform Administrator holds. 
     * @summary The recurring revenue Scrivio earns today, per practice
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getSubscriptionRevenue(options?: RawAxiosRequestConfig) {
        return AdminSubscriptionsApiFp(this.configuration).getSubscriptionRevenue(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Every plan in the price book that is still offered (#4475), with its seat prices as they stand today. What the admin portal pre-fills when putting a practice on a plan — the price an admin then agrees may differ, and it is that agreed price, not this one, that the subscription pays from then on.  A retired plan is never listed. Requires `admin.subscriptions.view`. 
     * @summary The plans an organization can be put on, at what they cost today
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listSubscriptionPlans(options?: RawAxiosRequestConfig) {
        return AdminSubscriptionsApiFp(this.configuration).listSubscriptionPlans(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * A practice in `INVOICE` mode pays by bank transfer, so someone at Scrivio records it (#4477). Moves the invoice from `VERSTUURD` to `BETAALD` with the day the money arrived.  Only a `VERSTUURD` invoice can be marked paid: a `CONCEPT` one has not reached the practice, and a `BETAALD` or `ONINBAAR` one is already settled. Anything else is a 409. Nothing is sent to Mollie — this records a payment Mollie never saw.  Audited in the admin audit log. Requires `admin.subscriptions.manage`. 
     * @summary Record that a sent subscription invoice was paid by bank transfer
     * @param {string} invoiceId Subscription invoice ID
     * @param {SubscriptionInvoiceMarkPaidRequest} subscriptionInvoiceMarkPaidRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public markSubscriptionInvoicePaid(invoiceId: string, subscriptionInvoiceMarkPaidRequest: SubscriptionInvoiceMarkPaidRequest, options?: RawAxiosRequestConfig) {
        return AdminSubscriptionsApiFp(this.configuration).markSubscriptionInvoicePaid(invoiceId, subscriptionInvoiceMarkPaidRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Mails the practice that Scrivio needs a new mandate (#4611). The mail links to the practice\'s own subscription screen, behind its own login, where the checkout is started — **it carries no checkout or payment link**, so nothing can be signed from the mail itself.  Accepted exactly where that screen offers the checkout: the subscription is `ACTIVE`, the practice chose `AUTOMATED`, and no mandate can be charged. Addressed to the billing email, then the organisation\'s email, then the member who created it.  Audited in the admin audit log; a mail that cannot be sent leaves no entry (503). Requires `admin.subscriptions.manage`, which only the Platform Administrator holds. 
     * @summary Ask a practice to give a new direct-debit mandate
     * @param {string} organizationId Organization ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public requestSubscriptionMandate(organizationId: string, options?: RawAxiosRequestConfig) {
        return AdminSubscriptionsApiFp(this.configuration).requestSubscriptionMandate(organizationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Withdraws a mandate at Mollie and records it as revoked (#4611), so nothing further is collected under it. The mandate is **kept**, `INVALID` with the moment it was revoked: a payment already taken under it must go on resolving it.  How the next amount is collected is not changed here. A practice that chose `AUTOMATED` and has no other chargeable mandate is collected as `INVOICE` from now on, with `collectionFallbackReason` `MANDATE_REVOKED` — the same fallback a revocation at the bank produces.  Mollie is asked first; if it cannot be reached nothing is recorded (503). Audited in the admin audit log. Requires `admin.subscriptions.manage`, which only the Platform Administrator holds. 
     * @summary Revoke a practice\'s direct-debit mandate
     * @param {string} organizationId Organization ID
     * @param {string} mandateId A mandate on this organization\&#39;s subscription — Scrivio\&#39;s id, not Mollie\&#39;s
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public revokeSubscriptionMandate(organizationId: string, mandateId: string, options?: RawAxiosRequestConfig) {
        return AdminSubscriptionsApiFp(this.configuration).revokeSubscriptionMandate(organizationId, mandateId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Records that Scrivio has given up collecting a sent invoice (#4477): moves it from `VERSTUURD` to `ONINBAAR`. The subscription\'s own status is not touched.  Only a `VERSTUURD` invoice can be written off; anything else is a 409. Nothing is sent to Mollie.  Audited in the admin audit log with the reason given. Requires `admin.subscriptions.manage`. 
     * @summary Write a sent subscription invoice off as uncollectable
     * @param {string} invoiceId Subscription invoice ID
     * @param {SubscriptionInvoiceWriteOffRequest} subscriptionInvoiceWriteOffRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public writeOffSubscriptionInvoice(invoiceId: string, subscriptionInvoiceWriteOffRequest: SubscriptionInvoiceWriteOffRequest, options?: RawAxiosRequestConfig) {
        return AdminSubscriptionsApiFp(this.configuration).writeOffSubscriptionInvoice(invoiceId, subscriptionInvoiceWriteOffRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * AdminSupportPartnersApi - axios parameter creator
 */
export const AdminSupportPartnersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Creates a new support partner with the provided details.  Requires `admin.support-partners.manage` permission. 
         * @summary Create a new support partner
         * @param {CreateSupportPartnerRequest} createSupportPartnerRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createSupportPartner: async (createSupportPartnerRequest: CreateSupportPartnerRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'createSupportPartnerRequest' is not null or undefined
            assertParamExists('createSupportPartner', 'createSupportPartnerRequest', createSupportPartnerRequest)
            const localVarPath = `/api/admin/support-partners`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createSupportPartnerRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Deactivates a support partner (soft delete). Sets the status to INACTIVE but preserves the data.  Revokes the access of every agent of the partner: their admin sessions are revoked, and while the partner is INACTIVE they are refused on every admin request and its pending invitations cannot be accepted. The agents and the organization links are kept, so reactivating the partner restores access without re-inviting.  Requires `admin.support-partners.manage` permission. 
         * @summary Deactivate a support partner
         * @param {string} id Support partner ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteSupportPartner: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('deleteSupportPartner', 'id', id)
            const localVarPath = `/api/admin/support-partners/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns detailed information about a specific support partner, including the list of organization IDs assigned to this partner.  Requires `admin.support-partners.view` permission. 
         * @summary Get a support partner by ID
         * @param {string} id Support partner ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getSupportPartner: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('getSupportPartner', 'id', id)
            const localVarPath = `/api/admin/support-partners/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns a list of all support partners with optional filtering by status.  Requires `admin.support-partners.view` permission. 
         * @summary List all support partners
         * @param {SupportPartnerStatus} [status] Filter by support partner status
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listSupportPartners: async (status?: SupportPartnerStatus, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/admin/support-partners`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (status !== undefined) {
                localVarQueryParameter['status'] = status;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Updates the details of an existing support partner.  Requires `admin.support-partners.manage` permission. 
         * @summary Update a support partner
         * @param {string} id Support partner ID
         * @param {UpdateSupportPartnerRequest} updateSupportPartnerRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateSupportPartner: async (id: string, updateSupportPartnerRequest: UpdateSupportPartnerRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('updateSupportPartner', 'id', id)
            // verify required parameter 'updateSupportPartnerRequest' is not null or undefined
            assertParamExists('updateSupportPartner', 'updateSupportPartnerRequest', updateSupportPartnerRequest)
            const localVarPath = `/api/admin/support-partners/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateSupportPartnerRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Updates the list of organizations assigned to this support partner. This replaces the current organization assignments with the provided list.  An INACTIVE partner can only have organizations removed: a request naming an organization it is not already linked to is refused with 409.  Requires `admin.support-partners.manage` permission. 
         * @summary Update organizations assigned to a support partner
         * @param {string} id Support partner ID
         * @param {UpdateSupportPartnerOrganizationsRequest} updateSupportPartnerOrganizationsRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateSupportPartnerOrganizations: async (id: string, updateSupportPartnerOrganizationsRequest: UpdateSupportPartnerOrganizationsRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('updateSupportPartnerOrganizations', 'id', id)
            // verify required parameter 'updateSupportPartnerOrganizationsRequest' is not null or undefined
            assertParamExists('updateSupportPartnerOrganizations', 'updateSupportPartnerOrganizationsRequest', updateSupportPartnerOrganizationsRequest)
            const localVarPath = `/api/admin/support-partners/{id}/organizations`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateSupportPartnerOrganizationsRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AdminSupportPartnersApi - functional programming interface
 */
export const AdminSupportPartnersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AdminSupportPartnersApiAxiosParamCreator(configuration)
    return {
        /**
         * Creates a new support partner with the provided details.  Requires `admin.support-partners.manage` permission. 
         * @summary Create a new support partner
         * @param {CreateSupportPartnerRequest} createSupportPartnerRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createSupportPartner(createSupportPartnerRequest: CreateSupportPartnerRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SupportPartnerResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createSupportPartner(createSupportPartnerRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminSupportPartnersApi.createSupportPartner']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Deactivates a support partner (soft delete). Sets the status to INACTIVE but preserves the data.  Revokes the access of every agent of the partner: their admin sessions are revoked, and while the partner is INACTIVE they are refused on every admin request and its pending invitations cannot be accepted. The agents and the organization links are kept, so reactivating the partner restores access without re-inviting.  Requires `admin.support-partners.manage` permission. 
         * @summary Deactivate a support partner
         * @param {string} id Support partner ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteSupportPartner(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteSupportPartner(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminSupportPartnersApi.deleteSupportPartner']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns detailed information about a specific support partner, including the list of organization IDs assigned to this partner.  Requires `admin.support-partners.view` permission. 
         * @summary Get a support partner by ID
         * @param {string} id Support partner ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getSupportPartner(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SupportPartnerResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getSupportPartner(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminSupportPartnersApi.getSupportPartner']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns a list of all support partners with optional filtering by status.  Requires `admin.support-partners.view` permission. 
         * @summary List all support partners
         * @param {SupportPartnerStatus} [status] Filter by support partner status
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listSupportPartners(status?: SupportPartnerStatus, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<SupportPartnerResponse>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listSupportPartners(status, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminSupportPartnersApi.listSupportPartners']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Updates the details of an existing support partner.  Requires `admin.support-partners.manage` permission. 
         * @summary Update a support partner
         * @param {string} id Support partner ID
         * @param {UpdateSupportPartnerRequest} updateSupportPartnerRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateSupportPartner(id: string, updateSupportPartnerRequest: UpdateSupportPartnerRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SupportPartnerResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateSupportPartner(id, updateSupportPartnerRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminSupportPartnersApi.updateSupportPartner']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Updates the list of organizations assigned to this support partner. This replaces the current organization assignments with the provided list.  An INACTIVE partner can only have organizations removed: a request naming an organization it is not already linked to is refused with 409.  Requires `admin.support-partners.manage` permission. 
         * @summary Update organizations assigned to a support partner
         * @param {string} id Support partner ID
         * @param {UpdateSupportPartnerOrganizationsRequest} updateSupportPartnerOrganizationsRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateSupportPartnerOrganizations(id: string, updateSupportPartnerOrganizationsRequest: UpdateSupportPartnerOrganizationsRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SupportPartnerResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateSupportPartnerOrganizations(id, updateSupportPartnerOrganizationsRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminSupportPartnersApi.updateSupportPartnerOrganizations']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AdminSupportPartnersApi - factory interface
 */
export const AdminSupportPartnersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AdminSupportPartnersApiFp(configuration)
    return {
        /**
         * Creates a new support partner with the provided details.  Requires `admin.support-partners.manage` permission. 
         * @summary Create a new support partner
         * @param {CreateSupportPartnerRequest} createSupportPartnerRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createSupportPartner(createSupportPartnerRequest: CreateSupportPartnerRequest, options?: RawAxiosRequestConfig): AxiosPromise<SupportPartnerResponse> {
            return localVarFp.createSupportPartner(createSupportPartnerRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Deactivates a support partner (soft delete). Sets the status to INACTIVE but preserves the data.  Revokes the access of every agent of the partner: their admin sessions are revoked, and while the partner is INACTIVE they are refused on every admin request and its pending invitations cannot be accepted. The agents and the organization links are kept, so reactivating the partner restores access without re-inviting.  Requires `admin.support-partners.manage` permission. 
         * @summary Deactivate a support partner
         * @param {string} id Support partner ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteSupportPartner(id: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteSupportPartner(id, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns detailed information about a specific support partner, including the list of organization IDs assigned to this partner.  Requires `admin.support-partners.view` permission. 
         * @summary Get a support partner by ID
         * @param {string} id Support partner ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getSupportPartner(id: string, options?: RawAxiosRequestConfig): AxiosPromise<SupportPartnerResponse> {
            return localVarFp.getSupportPartner(id, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns a list of all support partners with optional filtering by status.  Requires `admin.support-partners.view` permission. 
         * @summary List all support partners
         * @param {SupportPartnerStatus} [status] Filter by support partner status
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listSupportPartners(status?: SupportPartnerStatus, options?: RawAxiosRequestConfig): AxiosPromise<Array<SupportPartnerResponse>> {
            return localVarFp.listSupportPartners(status, options).then((request) => request(axios, basePath));
        },
        /**
         * Updates the details of an existing support partner.  Requires `admin.support-partners.manage` permission. 
         * @summary Update a support partner
         * @param {string} id Support partner ID
         * @param {UpdateSupportPartnerRequest} updateSupportPartnerRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateSupportPartner(id: string, updateSupportPartnerRequest: UpdateSupportPartnerRequest, options?: RawAxiosRequestConfig): AxiosPromise<SupportPartnerResponse> {
            return localVarFp.updateSupportPartner(id, updateSupportPartnerRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Updates the list of organizations assigned to this support partner. This replaces the current organization assignments with the provided list.  An INACTIVE partner can only have organizations removed: a request naming an organization it is not already linked to is refused with 409.  Requires `admin.support-partners.manage` permission. 
         * @summary Update organizations assigned to a support partner
         * @param {string} id Support partner ID
         * @param {UpdateSupportPartnerOrganizationsRequest} updateSupportPartnerOrganizationsRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateSupportPartnerOrganizations(id: string, updateSupportPartnerOrganizationsRequest: UpdateSupportPartnerOrganizationsRequest, options?: RawAxiosRequestConfig): AxiosPromise<SupportPartnerResponse> {
            return localVarFp.updateSupportPartnerOrganizations(id, updateSupportPartnerOrganizationsRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AdminSupportPartnersApi - object-oriented interface
 */
export class AdminSupportPartnersApi extends BaseAPI {
    /**
     * Creates a new support partner with the provided details.  Requires `admin.support-partners.manage` permission. 
     * @summary Create a new support partner
     * @param {CreateSupportPartnerRequest} createSupportPartnerRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createSupportPartner(createSupportPartnerRequest: CreateSupportPartnerRequest, options?: RawAxiosRequestConfig) {
        return AdminSupportPartnersApiFp(this.configuration).createSupportPartner(createSupportPartnerRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Deactivates a support partner (soft delete). Sets the status to INACTIVE but preserves the data.  Revokes the access of every agent of the partner: their admin sessions are revoked, and while the partner is INACTIVE they are refused on every admin request and its pending invitations cannot be accepted. The agents and the organization links are kept, so reactivating the partner restores access without re-inviting.  Requires `admin.support-partners.manage` permission. 
     * @summary Deactivate a support partner
     * @param {string} id Support partner ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteSupportPartner(id: string, options?: RawAxiosRequestConfig) {
        return AdminSupportPartnersApiFp(this.configuration).deleteSupportPartner(id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns detailed information about a specific support partner, including the list of organization IDs assigned to this partner.  Requires `admin.support-partners.view` permission. 
     * @summary Get a support partner by ID
     * @param {string} id Support partner ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getSupportPartner(id: string, options?: RawAxiosRequestConfig) {
        return AdminSupportPartnersApiFp(this.configuration).getSupportPartner(id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns a list of all support partners with optional filtering by status.  Requires `admin.support-partners.view` permission. 
     * @summary List all support partners
     * @param {SupportPartnerStatus} [status] Filter by support partner status
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listSupportPartners(status?: SupportPartnerStatus, options?: RawAxiosRequestConfig) {
        return AdminSupportPartnersApiFp(this.configuration).listSupportPartners(status, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Updates the details of an existing support partner.  Requires `admin.support-partners.manage` permission. 
     * @summary Update a support partner
     * @param {string} id Support partner ID
     * @param {UpdateSupportPartnerRequest} updateSupportPartnerRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateSupportPartner(id: string, updateSupportPartnerRequest: UpdateSupportPartnerRequest, options?: RawAxiosRequestConfig) {
        return AdminSupportPartnersApiFp(this.configuration).updateSupportPartner(id, updateSupportPartnerRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Updates the list of organizations assigned to this support partner. This replaces the current organization assignments with the provided list.  An INACTIVE partner can only have organizations removed: a request naming an organization it is not already linked to is refused with 409.  Requires `admin.support-partners.manage` permission. 
     * @summary Update organizations assigned to a support partner
     * @param {string} id Support partner ID
     * @param {UpdateSupportPartnerOrganizationsRequest} updateSupportPartnerOrganizationsRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateSupportPartnerOrganizations(id: string, updateSupportPartnerOrganizationsRequest: UpdateSupportPartnerOrganizationsRequest, options?: RawAxiosRequestConfig) {
        return AdminSupportPartnersApiFp(this.configuration).updateSupportPartnerOrganizations(id, updateSupportPartnerOrganizationsRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * AdminUsersApi - axios parameter creator
 */
export const AdminUsersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Disables a user account, preventing them from logging in.  Disabled users: - Cannot authenticate via Firebase - Existing sessions are invalidated - Data is preserved (not deleted)  **Scoping:** - Support agents can only disable users in their scoped organizations - Platform admins can disable any user  Requires `admin.users.manage` permission. 
         * @summary Disable a user
         * @param {string} id User ID
         * @param {AdminDisableUserRequest} adminDisableUserRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        disableUser: async (id: string, adminDisableUserRequest: AdminDisableUserRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('disableUser', 'id', id)
            // verify required parameter 'adminDisableUserRequest' is not null or undefined
            assertParamExists('disableUser', 'adminDisableUserRequest', adminDisableUserRequest)
            const localVarPath = `/api/admin/users/{id}/disable`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(adminDisableUserRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Re-enables a previously disabled user account, restoring their ability to log in.  **Scoping:** - Support agents can only enable users in their scoped organizations - Platform admins can enable any user  Requires `admin.users.manage` permission. 
         * @summary Enable a user
         * @param {string} id User ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        enableUser: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('enableUser', 'id', id)
            const localVarPath = `/api/admin/users/{id}/enable`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns detailed information about a specific user.  **Scoping:** - Support agents can only see users if they share at least one scoped organization - Organizations list only includes organizations the admin has access to - Other organizations are shown as a count only  Requires `admin.users.view` permission. 
         * @summary Get user details
         * @param {string} id User ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getUserById: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('getUserById', 'id', id)
            const localVarPath = `/api/admin/users/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Initiates an impersonation session for a user, allowing an admin to view the application as that user for debugging purposes.  **Security:** - Cannot impersonate other admin users - Session expires after 1 hour - The admin states why, and that reason is recorded on the `IMPERSONATION_STARTED`   admin audit entry — so the trail shows not only who looked at a practice\'s data   and when, but why (NEN 7510 8.34) - All actions during impersonation are logged to the admin audit log  **Scoping:** - Platform admins can impersonate users in any organization - Support agents can only impersonate users in organizations assigned to their support partner  Requires `admin.users.impersonate` permission. 
         * @summary Start user impersonation
         * @param {string} id User ID to impersonate
         * @param {ImpersonateUserRequest} impersonateUserRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        impersonateUser: async (id: string, impersonateUserRequest: ImpersonateUserRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('impersonateUser', 'id', id)
            // verify required parameter 'impersonateUserRequest' is not null or undefined
            assertParamExists('impersonateUser', 'impersonateUserRequest', impersonateUserRequest)
            const localVarPath = `/api/admin/users/{id}/impersonate`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(impersonateUserRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Triggers a password reset email for the specified user.  Uses Firebase Admin SDK to generate a password reset link and sends it to the user\'s email address. The action is logged to the admin audit log.  **Scoping:** - Support agents can only reset passwords for users in their scoped organizations - Platform admins can reset passwords for any user  Requires `admin.users.manage` permission. 
         * @summary Trigger password reset
         * @param {string} id User ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resetUserPassword: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('resetUserPassword', 'id', id)
            const localVarPath = `/api/admin/users/{id}/reset-password`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AdminUsersApi - functional programming interface
 */
export const AdminUsersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AdminUsersApiAxiosParamCreator(configuration)
    return {
        /**
         * Disables a user account, preventing them from logging in.  Disabled users: - Cannot authenticate via Firebase - Existing sessions are invalidated - Data is preserved (not deleted)  **Scoping:** - Support agents can only disable users in their scoped organizations - Platform admins can disable any user  Requires `admin.users.manage` permission. 
         * @summary Disable a user
         * @param {string} id User ID
         * @param {AdminDisableUserRequest} adminDisableUserRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async disableUser(id: string, adminDisableUserRequest: AdminDisableUserRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminUserDetailDto>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.disableUser(id, adminDisableUserRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminUsersApi.disableUser']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Re-enables a previously disabled user account, restoring their ability to log in.  **Scoping:** - Support agents can only enable users in their scoped organizations - Platform admins can enable any user  Requires `admin.users.manage` permission. 
         * @summary Enable a user
         * @param {string} id User ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async enableUser(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminUserDetailDto>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.enableUser(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminUsersApi.enableUser']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns detailed information about a specific user.  **Scoping:** - Support agents can only see users if they share at least one scoped organization - Organizations list only includes organizations the admin has access to - Other organizations are shown as a count only  Requires `admin.users.view` permission. 
         * @summary Get user details
         * @param {string} id User ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getUserById(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminUserDetailDto>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getUserById(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminUsersApi.getUserById']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Initiates an impersonation session for a user, allowing an admin to view the application as that user for debugging purposes.  **Security:** - Cannot impersonate other admin users - Session expires after 1 hour - The admin states why, and that reason is recorded on the `IMPERSONATION_STARTED`   admin audit entry — so the trail shows not only who looked at a practice\'s data   and when, but why (NEN 7510 8.34) - All actions during impersonation are logged to the admin audit log  **Scoping:** - Platform admins can impersonate users in any organization - Support agents can only impersonate users in organizations assigned to their support partner  Requires `admin.users.impersonate` permission. 
         * @summary Start user impersonation
         * @param {string} id User ID to impersonate
         * @param {ImpersonateUserRequest} impersonateUserRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async impersonateUser(id: string, impersonateUserRequest: ImpersonateUserRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ImpersonationTokenResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.impersonateUser(id, impersonateUserRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminUsersApi.impersonateUser']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Triggers a password reset email for the specified user.  Uses Firebase Admin SDK to generate a password reset link and sends it to the user\'s email address. The action is logged to the admin audit log.  **Scoping:** - Support agents can only reset passwords for users in their scoped organizations - Platform admins can reset passwords for any user  Requires `admin.users.manage` permission. 
         * @summary Trigger password reset
         * @param {string} id User ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async resetUserPassword(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AdminPasswordResetResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.resetUserPassword(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AdminUsersApi.resetUserPassword']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AdminUsersApi - factory interface
 */
export const AdminUsersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AdminUsersApiFp(configuration)
    return {
        /**
         * Disables a user account, preventing them from logging in.  Disabled users: - Cannot authenticate via Firebase - Existing sessions are invalidated - Data is preserved (not deleted)  **Scoping:** - Support agents can only disable users in their scoped organizations - Platform admins can disable any user  Requires `admin.users.manage` permission. 
         * @summary Disable a user
         * @param {string} id User ID
         * @param {AdminDisableUserRequest} adminDisableUserRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        disableUser(id: string, adminDisableUserRequest: AdminDisableUserRequest, options?: RawAxiosRequestConfig): AxiosPromise<AdminUserDetailDto> {
            return localVarFp.disableUser(id, adminDisableUserRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Re-enables a previously disabled user account, restoring their ability to log in.  **Scoping:** - Support agents can only enable users in their scoped organizations - Platform admins can enable any user  Requires `admin.users.manage` permission. 
         * @summary Enable a user
         * @param {string} id User ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        enableUser(id: string, options?: RawAxiosRequestConfig): AxiosPromise<AdminUserDetailDto> {
            return localVarFp.enableUser(id, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns detailed information about a specific user.  **Scoping:** - Support agents can only see users if they share at least one scoped organization - Organizations list only includes organizations the admin has access to - Other organizations are shown as a count only  Requires `admin.users.view` permission. 
         * @summary Get user details
         * @param {string} id User ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getUserById(id: string, options?: RawAxiosRequestConfig): AxiosPromise<AdminUserDetailDto> {
            return localVarFp.getUserById(id, options).then((request) => request(axios, basePath));
        },
        /**
         * Initiates an impersonation session for a user, allowing an admin to view the application as that user for debugging purposes.  **Security:** - Cannot impersonate other admin users - Session expires after 1 hour - The admin states why, and that reason is recorded on the `IMPERSONATION_STARTED`   admin audit entry — so the trail shows not only who looked at a practice\'s data   and when, but why (NEN 7510 8.34) - All actions during impersonation are logged to the admin audit log  **Scoping:** - Platform admins can impersonate users in any organization - Support agents can only impersonate users in organizations assigned to their support partner  Requires `admin.users.impersonate` permission. 
         * @summary Start user impersonation
         * @param {string} id User ID to impersonate
         * @param {ImpersonateUserRequest} impersonateUserRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        impersonateUser(id: string, impersonateUserRequest: ImpersonateUserRequest, options?: RawAxiosRequestConfig): AxiosPromise<ImpersonationTokenResponse> {
            return localVarFp.impersonateUser(id, impersonateUserRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Triggers a password reset email for the specified user.  Uses Firebase Admin SDK to generate a password reset link and sends it to the user\'s email address. The action is logged to the admin audit log.  **Scoping:** - Support agents can only reset passwords for users in their scoped organizations - Platform admins can reset passwords for any user  Requires `admin.users.manage` permission. 
         * @summary Trigger password reset
         * @param {string} id User ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resetUserPassword(id: string, options?: RawAxiosRequestConfig): AxiosPromise<AdminPasswordResetResponse> {
            return localVarFp.resetUserPassword(id, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AdminUsersApi - object-oriented interface
 */
export class AdminUsersApi extends BaseAPI {
    /**
     * Disables a user account, preventing them from logging in.  Disabled users: - Cannot authenticate via Firebase - Existing sessions are invalidated - Data is preserved (not deleted)  **Scoping:** - Support agents can only disable users in their scoped organizations - Platform admins can disable any user  Requires `admin.users.manage` permission. 
     * @summary Disable a user
     * @param {string} id User ID
     * @param {AdminDisableUserRequest} adminDisableUserRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public disableUser(id: string, adminDisableUserRequest: AdminDisableUserRequest, options?: RawAxiosRequestConfig) {
        return AdminUsersApiFp(this.configuration).disableUser(id, adminDisableUserRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Re-enables a previously disabled user account, restoring their ability to log in.  **Scoping:** - Support agents can only enable users in their scoped organizations - Platform admins can enable any user  Requires `admin.users.manage` permission. 
     * @summary Enable a user
     * @param {string} id User ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public enableUser(id: string, options?: RawAxiosRequestConfig) {
        return AdminUsersApiFp(this.configuration).enableUser(id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns detailed information about a specific user.  **Scoping:** - Support agents can only see users if they share at least one scoped organization - Organizations list only includes organizations the admin has access to - Other organizations are shown as a count only  Requires `admin.users.view` permission. 
     * @summary Get user details
     * @param {string} id User ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getUserById(id: string, options?: RawAxiosRequestConfig) {
        return AdminUsersApiFp(this.configuration).getUserById(id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Initiates an impersonation session for a user, allowing an admin to view the application as that user for debugging purposes.  **Security:** - Cannot impersonate other admin users - Session expires after 1 hour - The admin states why, and that reason is recorded on the `IMPERSONATION_STARTED`   admin audit entry — so the trail shows not only who looked at a practice\'s data   and when, but why (NEN 7510 8.34) - All actions during impersonation are logged to the admin audit log  **Scoping:** - Platform admins can impersonate users in any organization - Support agents can only impersonate users in organizations assigned to their support partner  Requires `admin.users.impersonate` permission. 
     * @summary Start user impersonation
     * @param {string} id User ID to impersonate
     * @param {ImpersonateUserRequest} impersonateUserRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public impersonateUser(id: string, impersonateUserRequest: ImpersonateUserRequest, options?: RawAxiosRequestConfig) {
        return AdminUsersApiFp(this.configuration).impersonateUser(id, impersonateUserRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Triggers a password reset email for the specified user.  Uses Firebase Admin SDK to generate a password reset link and sends it to the user\'s email address. The action is logged to the admin audit log.  **Scoping:** - Support agents can only reset passwords for users in their scoped organizations - Platform admins can reset passwords for any user  Requires `admin.users.manage` permission. 
     * @summary Trigger password reset
     * @param {string} id User ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public resetUserPassword(id: string, options?: RawAxiosRequestConfig) {
        return AdminUsersApiFp(this.configuration).resetUserPassword(id, options).then((request) => request(this.axios, this.basePath));
    }
}



